import { makeIcon, isIconName } from '@caos/ui';
import { readJsonPath, rowMatches } from '../predicate.js';
import { setActiveNavigate, navigateFromFrame } from '../navigate-seam.js';
import { landingTabKey, resolveOrgHomeApp } from '../shell-wiring.js';
import type { SurfaceRenderer } from '../host.js';
import { applyDesignSystem } from '../design-system.js';
import { recentsFor } from './recent-items.js';
import type {
  ShellFramePlan,
  ShellFrameTemplatePlan,
  ShellFrameRegion,
  ShellInjectionRef,
  DesignSystemRef,
  ShellIntakeField,
  ShellIntakeDescriptor,
  ShellNavTab,
  ShellGuidanceAnchor,
  ShellAssistantDescriptor,
  ShellGuidanceDescriptor,
  ShellGuidanceSection,
  ShellGuidanceRecipe,
  ShellGuidanceStep,
} from '../kernel-contract.js';
import {
  topicsForSurface,
  topicsMatchingSearch,
  type GuidanceReader,
  type GuidanceSurface,
} from '../guidance-articles.js';
import { splitGuidanceProse, guidanceProseWords } from '../guidance-prose.js';
import { currentThemePref, nextThemePref, applyThemePref, THEME_CHANGE_EVENT, type ThemePref } from '../theme.js';
import { orderSeamContributions } from '../seam-order.js';
import {
  loadNavArrangement,
  saveNavArrangement,
  clearNavArrangement,
  applyNavArrangement,
  arrangementFrom,
} from '../nav-preferences.js';
import { PREFERENCES_CHANGE_EVENT, preference, setPreference } from '../person-preferences.js';
import { RAIL_SHOWN_PREFIX, edgeTabShown } from '../edge-tab-preferences.js';
import { COMPACT_RAIL_KEY, compactRailOn } from '../rail-preferences.js';
import { loadListView, type ListView } from '../data-source.js';
import { OPEN_RECORD_EVENT } from '../content-source.js';
import { lastOpenRecordAnnouncement, type OpenRecordAnnouncement } from '../open-record-event.js';
import { SESSION_SIGN_OUT_ENDPOINT, signOutSession } from '../session-sync.js';
import { createGlobalSearch, searchDestinations } from './global-search.js';
import { buildMetadataSearchOverlay, bindMetadataSearchKeys, type MetadataSearchOverlay } from './metadata-search.js';
import { projectedRef, submissionRef } from './submission-ref.js';
import {
  askWhyThenStart,
  setImpersonationFetch,
  setImpersonationLeaveTo,
  setImpersonationRunning,
  type DoorFetch,
} from './impersonation-door.js';
import './shell-frame.css';

/**
 * CAOS-243 — the shell FRAME renderer.
 *
 * When the boot resolution order resolves a shell master, the kernel emits a `hand-to-shell`
 * {@link import('../kernel-contract.js').SurfaceDecision} carrying a {@link ShellFramePlan}: the
 * structured references — mastered shell key, the 5 required shell surfaces, the registry
 * injections grouped by seam, and the resolved theme — that this renderer assembles the platform
 * CHROME from, around the kernel's content region. The kernel stays pixel-free; this is the pixel
 * layer that turns the plan into the frame, using the base `@caos/ui` web components.
 *
 * Surface → component map (design note CAOS-243):
 *   impersonation-banner  → <caos-impersonation-banner>  (shown only while impersonating)
 *   global-header         → <caos-topbar>                (brand from theme, header-actions in center)
 *   app-navigation-strip  → <caos-workspace-tabs> + <caos-nav-gadget>  (fed by navigation-entries)
 *   app-switcher          → <caos-app-launcher>          (in the workspace-tabs launcher slot)
 *   record-tabs-strip     → <caos-tab-strip>             (record surfaces only)
 * Injection seams:
 *   navigation-entries → the app nav tabs + launcher apps + nav-gadget items
 *   header-actions     → an action group slotted into the topbar `center`
 *   avatar-menu        → wired to the topbar account overflow (see note below)
 *   edge-anchored      → tabs stacked in the right-hand gutter (CAOS-450) (AI assistant / report-a-bug /
 *                        guidance), route-independent, beside content rather than over it
 *
 * Graceful degradation is first-class: empty injections, no apps, no impersonation, and a
 * non-record content plan each render a VALID frame — the optional surfaces are omitted, never
 * rendered empty or broken.
 *
 * NOTE (caos-ui follow-up, flagged): `<caos-topbar>` exposes only a `center` slot and a fixed
 * account menu, so header-actions are surfaced in `center` and avatar-menu contributions are wired
 * as data on the topbar (`data-avatar-menu-injections`) pending a topbar prop to fold them into the
 * native overflow. Everything is driven by the registry injections — nothing is faked.
 */

/** Runtime context the frame needs beyond the kernel plan (session/route bits not on the plan). */
export interface ShellRuntime {
  /** The signed-in user, for the topbar account block. Absent ⇒ the account block hides. */
  readonly user?: {
    readonly name: string;
    readonly sub?: string;
    /**
     * CAOS-342 — the roles the signed-in person holds. They fill the account menu's "Viewing as"
     * preview, and the first is the role the sub line names. Empty/absent ⇒ no preview offered.
     */
    readonly roles?: readonly { readonly key: string; readonly label: string }[];
  };
  /**
   * The server-resolved environment identity shown persistently in the header and document title
   * (for example, "PRODUCTION · Contoso" or "SANDBOX · Contoso").
   */
  readonly env?: string;
  /** The active app key (highlights the launcher row / nav tab). Defaults to the first nav entry. */
  readonly activeAppKey?: string;
  /** The active app title shown in the nav strip. */
  readonly activeAppLabel?: string;
  /**
   * The active object tab within the active app (CAOS-267) — highlights the nav tab and drives the
   * derived content region. Absent ⇒ the active app's landing (its tab set).
   */
  readonly activeTabKey?: string;
  /** Present ⇒ render the impersonation banner ("Viewing as …"). */
  readonly impersonation?: {
    readonly targetLabel: string;
    readonly realLabel?: string;
    /** CAOS-607 — when the borrowed session runs out (ISO). Drives the strip's countdown. */
    readonly expiresAt?: string;
  };
  /**
   * CAOS-607 — who this person may act as. Non-empty ⇒ the account menu offers "Log in as another
   * user"; absent or empty ⇒ the entry does not appear at all, which is the ordinary case for
   * almost everybody signed in.
   *
   * Handed in rather than fetched here. The kernel decides eligibility and this list is only ever
   * its answer — a surface that derived its own from some broader roster would eventually offer a
   * name the policy then refuses, which is worse than offering nobody.
   */
  readonly impersonationOptions?: readonly { readonly id: string; readonly label: string }[];
  /**
   * CAOS-606 — the roles the signed-in person may PREVIEW the workspace as. Absent/empty ⇒ the
   * account menu's "Viewing as" switcher is not offered, which is the right answer for somebody
   * holding one role: there is nothing to switch between.
   */
  readonly previewRoles?: readonly { readonly key: string; readonly label: string }[];
  /** CAOS-606 — the role in force, or absent/null when the workspace is resolving normally. */
  readonly activePreview?: { readonly key: string; readonly label: string } | null;
  /**
   * CAOS-606 — start previewing as a role, or stop when the key is null. The frame calls this and
   * does nothing else with the result; the caller owns what happens next (production reloads, so
   * every app, tab, layout and record re-resolves under the new role rather than leaving the
   * workspace half in each). Absent ⇒ the switcher is rendered inert-free, because a menu whose
   * entries the host is not listening for is the control CAOS-581 removed.
   */
  readonly setPreviewRole?: (roleKey: string | null) => void;
  /**
   * CAOS-694/CAOS-342 — where the account menu's "Settings" entry should go for a given route, or
   * `undefined` when it must not be offered on that route. Absent callback ⇒ the entry never
   * appears at all, which is the honest state for a frame with nowhere to send the click.
   *
   * The caller (`shell-wiring.ts`'s `resolveSettingsHref`) is the single place that decides
   * presence — the signed-in person administers the app the route selects AND that app declares a
   * `settingsPage` — so this renderer never re-derives the gate, it only reflects it.
   *
   * CAOS-1659 — A FUNCTION OF THE ROUTE, not a value, and that is the whole point. Both halves of
   * the gate can change under a navigation: the app the route selects obviously does, and so
   * therefore does whether this person administers it. This frame keeps its top chrome MOUNTED
   * across an in-place navigation (see the navigator registered below), so a resolved string
   * handed over once was correct only for the route the frame was assembled on — the entry stayed
   * offered after a move into an app with no settings page, and still opened the app you left.
   * Same shape, and the same reason, as {@link editPageHrefFor} and {@link resolveContent}.
   */
  readonly settingsHrefFor?: (route: string) => string | undefined;
  /**
   * CAOS-608 — where the Setup gear's "Edit this page" entry should go for a given route, or
   * `undefined` when that route has nothing editable behind it. Absent callback ⇒ the entry never
   * appears, which is how it has shipped since CAOS-581 and is the honest state for a frame with
   * nowhere to send the click.
   *
   * A FUNCTION OF THE ROUTE, not a value, and that is the whole point. The answer changes on every
   * navigation — a record whose object has an authored layout, a record whose object has none, and
   * a list all differ — and this frame keeps its top chrome MOUNTED across an in-place navigation
   * (see the navigator registered below). A resolved string handed over once would therefore be
   * correct only for the route the frame was assembled on: walk from a record whose object has an
   * authored layout to one whose object has none, and the entry would still be offered, still
   * pointing at the first object's layout. That is the control that appears to work and does
   * something quietly different, which is the reason CAOS-581 unplugged this entry in the first
   * place. Same shape as {@link resolveContent} for the same reason.
   *
   * The gate itself lives in `resolveEditPageHref` (content-source.ts) — record page, tab names an
   * object, object has an AUTHORED layout rather than the synthesised default. This renderer never
   * re-derives any of it; it asks, and reflects the answer.
   */
  readonly editPageHrefFor?: (route: string) => string | undefined;
  /**
   * CAOS-287 — open the in-shell "get more apps" surface, from the App Launcher's foot affordance.
   *
   * Present — the affordance does something. Absent — choosing it does nothing, which is the honest
   * behaviour for a frame assembled with no install seam (a test, a fixture, a static render) and
   * is why the renderer never reaches for the picker itself. The frame draws; the host installs.
   */
  readonly getMoreApps?: () => void;
  /** The content region: a resolved content descriptor, a pre-built node, or omitted (a ready state). */
  readonly content?: ShellContent | Node;
  /**
   * Navigation callback (CAOS-267) — invoked with an href when a launcher app, a nav tab, or the
   * nav-gadget is chosen, so the app nav is LIVE. Absent ⇒ the chrome renders but does not route
   * (the pure default, used by the deterministic tests). Production passes a `location.assign` wrap.
   */
  readonly navigate?: (href: string) => void;
  /**
   * CAOS-306 × CAOS-300 — the route content resolver. On a persistent in-place navigation (no full
   * document load) the frame calls this for the new path and paints the resolved list/record surface
   * in the content region; `undefined` ⇒ the app landing (the frame renders its object-tab cards).
   * Absent ⇒ in-place nav falls back to the derived landing surface (the pure/test/fixture default).
   *
   * CAOS-345 — it is handed the whole route, query string included, because which SAVED LIST VIEW a
   * list opens on rides in the address (`?view=…`). Taking only the path would silently drop it.
   */
  readonly resolveContent?: (route: string) => Promise<Node | undefined>;
}

/** A resolved RECORD content descriptor — the host projection of a kernel record content plan. */
export interface ShellRecordContent {
  readonly kind: 'record';
  readonly app: string;
  readonly tab: string;
  readonly object: string;
  readonly recordId?: string;
  readonly title: string;
  /** The record's facet tabs (the record-tabs-strip). Defaults to a single "Details" tab. */
  readonly tabs?: readonly string[];
  /** The assembled sections (label + field labels) — a shallow projection, not full CAOS-127 fidelity. */
  readonly sections?: readonly { readonly label: string; readonly fields: readonly string[] }[];
}

/** A resolved LIST content descriptor — the host projection of a kernel list content plan. */
export interface ShellListContent {
  readonly kind: 'list';
  readonly app: string;
  readonly tab: string;
  readonly object: string;
  readonly title: string;
  readonly columns?: readonly string[];
  readonly rows?: readonly Record<string, unknown>[];
}

/** A pre-page fallback content descriptor (unknown route / missing record / unauthorized). */
export interface ShellFallbackContent {
  readonly kind: 'fallback';
  readonly fallbackKind: 'unknown-route' | 'missing-record' | 'unauthorized';
  readonly message: string;
}

/**
 * CAOS-247 — a generic row action the roster renders per row: a labelled button that POSTs a
 * control-plane WRITE route (`/api/<noun>/<verb>/<rowId>`), reading the row id from `paramFrom`.
 * `appliesWhen` is an optional PURE predicate over the row (`status == "active"`) gating whether the
 * button shows for that row; `intent` optionally tints it. Host projection of the kernel RowAction.
 */
export interface ShellRowAction {
  readonly id: string;
  readonly label: string;
  readonly verb: { readonly noun: string; readonly verb: string };
  readonly paramFrom: string;
  readonly intent?: string;
  readonly appliesWhen?: string;
}

/**
 * CAOS-247 — a resolved CONTROL-PLANE content descriptor: the host projection of the kernel's
 * ControlPlaneContentPlan. It carries the READ route (turned into `/api/<noun>/<verb>`), the stat
 * tiles (each reading a JSON path from the payload), the roster columns, and the per-row actions —
 * but NO DATA. The renderer fetches the payload live on mount, renders `<caos-stat-tile>` tiles +
 * a `<caos-data-table>` roster fed the rows AS DATA (the component does no live fetch of its own),
 * and re-reads after any action, mirroring the empty-workspace reboot precedent.
 */
export interface ShellControlPlaneContent {
  readonly kind: 'controlPlane';
  readonly app: string;
  readonly tab: string;
  readonly title: string;
  /** The control-plane READ route: the host fetches `/api/<noun>/<verb>`. */
  readonly read: { readonly noun: string; readonly verb: string };
  /** Optional JSON path into the payload naming the row array (e.g. `"orgs"`); absent ⇒ the payload IS the array. */
  readonly rowsAt?: string;
  /**
   * The roster columns (key → header label). A column may `derive` its cell from the whole row when
   * what a reader needs is not a field the route returns — a reference code built from an id, a
   * status worded for the person who reported it rather than for the person triaging it.
   */
  readonly columns?: readonly {
    readonly key: string;
    readonly label: string;
    readonly derive?: (row: Record<string, unknown>) => unknown;
  }[];
  /** Stat tiles, each a label + a JSON path read from the payload (e.g. `"plan.name"`, `"counts.production"`). */
  readonly stats?: readonly { readonly label: string; readonly valueAt: string }[];
  /** The per-row actions rendered against each roster row. */
  readonly rowActions?: readonly ShellRowAction[];
}

/**
 * CAOS-320 — a resolved INTAKE content descriptor: the host projection of the kernel's
 * IntakeContentPlan and the {@link ShellIntakeDescriptor} carried on an edge-anchored injection. It
 * carries the WRITE route (turned into `/api/<noun>/<verb>`), the target object, the form title +
 * fields — but NO data. {@link renderIntakeContent} paints the working form; on submit it POSTs the
 * write route with `{ object, fields }` and confirms with a reference code.
 */
export interface ShellIntakeContent extends ShellIntakeDescriptor {
  readonly kind: 'intake';
}

/** The content the frame's content region paints — the host projection of the kernel content plan. */
export type ShellContent =
  | ShellRecordContent
  | ShellListContent
  | ShellControlPlaneContent
  | ShellIntakeContent
  | ShellFallbackContent;

/** The kernel's shell required surfaces, in contract order (mirrors slots.ts). */
const SHELL_SURFACES = [
  'impersonation-banner',
  'global-header',
  'app-navigation-strip',
  'record-tabs-strip',
  'app-switcher',
] as const;

/** A minimal registry-shaped reader the host can build a frame plan from (tests / dev harness). */
export interface ShellRegistryReader {
  masterOf(capability: string): Promise<string | null>;
  injectionsFor(capability: string): Promise<readonly ShellInjectionRef[]>;
}

/**
 * Build a {@link ShellFramePlan} from a registry-shaped reader + the resolved theme. In PRODUCTION
 * the kernel projects this into the `hand-to-shell` decision; this host-side builder is the seam
 * the dev fixture and the deterministic tests drive with a MOCK registry (masterOf('shell') +
 * injectionsFor('shell')), exercising the whole path without a kernel or a database. Returns `null`
 * when no shell master resolves — there is no frame to build.
 */
export async function buildShellFramePlan(
  reader: ShellRegistryReader,
  design: DesignSystemRef,
): Promise<ShellFramePlan | null> {
  const master = await reader.masterOf('shell');
  if (!master) return null;
  const rows = await reader.injectionsFor('shell');
  const injections: Record<string, ShellInjectionRef[]> = {};
  for (const row of rows) {
    (injections[row.injectionPoint] ??= []).push({
      injectionPoint: row.injectionPoint,
      sourceKey: row.sourceKey,
    });
  }
  return { master, surfaces: [...SHELL_SURFACES], injections, design };
}

/** Humanize an injection source key into a label ("sales_app" → "Sales App", "reports" → "Reports"). */
function humanize(key: string): string {
  return key
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * CAOS-751 — the edge-anchored sources a person can show or hide, as My Profile > Preferences needs
 * them: the same list the gutter is built from, labelled by the same rule.
 *
 * Exported from here rather than resolved by the caller because {@link labelOf} is the ONE place a
 * label is decided (see its own note on why), and a second caller humanizing source keys for itself
 * would drift the moment either rule changed. The profile surface takes `{ sourceKey, label }` and
 * knows nothing about injections, so nothing about the seam leaks into a module that draws cards.
 */
export function edgeTabChoices(plan: ShellFramePlan): { sourceKey: string; label: string }[] {
  return seam(plan, 'edge-anchored').map((ref) => ({ sourceKey: ref.sourceKey, label: labelOf(ref) }));
}

/** An injection's real label from metadata, or the humanized source key as a last resort (CAOS-267). */
function labelOf(ref: ShellInjectionRef): string {
  // CAOS-1519 — `typeof` rather than `??`. `label` is TYPED `string | undefined`, but the boot
  // envelope reaches the host through an unchecked cast (`kernel-decision-source.ts`) and nothing
  // validates it at the wire, so a non-string can arrive here — and every consumer downstream
  // treats the result as a real string (`railLabel` calls `.trim()` on it, which throws and takes
  // the whole frame down, not just the label). For every value the declared type actually permits
  // this is identical to the `??` it replaces; it only adds an answer for the values the type
  // system is not checking. This is the single place the label is resolved, so guarding it here
  // covers the rail, the panels, `anchorKind`, and Home's hero at once.
  const raw: unknown = ref.label;
  return typeof raw === 'string' ? raw : humanize(ref.sourceKey);
}

/**
 * CAOS-734 — which panel an edge anchor opens, decided by its DECLARED `anchor.kind` and never by
 * sniffing the source key or label text. An injection from BEFORE that field existed falls back to
 * the regex heuristic below: a COMPATIBILITY SHIM, kept rather than defaulting an undeclared tab
 * straight to the assistant, which would silently re-route any shipped report/guidance package that
 * has not picked up the field. The three catalog packages with an edge tab all declare `kind`, and
 * CAOS-151 carries it from each package's own claim onto the plan (`enrichShellPlan`), because the
 * kernel projection keeps a claim's placement but not its kind. The shim still decides for any
 * installed package that declares none.
 *
 * CAOS-769 — this lives at module scope because a SECOND caller now needs the same answer. The Home
 * surface only points at an assistant when one is really anchored, and it asked the question with a
 * bare `kind ?? 'assistant'`, which called the first edge anchor the assistant whatever it was: an
 * org with only Report a Bug installed was told to "ask Report from the spark in the corner". That
 * is the same false claim about uninstalled software this ticket exists to remove, so both callers
 * resolve the kind through here rather than each keeping their own idea of it.
 */
function anchorKind(ref: ShellInjectionRef, label = labelOf(ref)): 'assistant' | 'report' | 'help' {
  return (
    ref.anchor?.kind ??
    (/report|bug/i.test(ref.sourceKey) || /report|bug/i.test(label)
      ? 'report'
      : /help|guid/i.test(ref.sourceKey) || /help|guid/i.test(label)
        ? 'help'
        : 'assistant')
  );
}

// CAOS-1649 — `landingTabKey` was defined here and asked only by the chrome. It now lives in
// `shell-wiring.ts` and is imported, so the chrome, `appDeclaresItsLanding` and the CONTENT route
// all get their answer from one place. They used to work it out separately, and the content route
// did not ask at all — which is how an app opened at its own address could highlight the landing tab
// and draw a different surface underneath it.

/**
 * The injections for a seam, or an empty list (a seam with no contribution is simply absent).
 *
 * CAOS-730 — every `point` this function is called with here is a REAL registry-backed render
 * consumer as far as `kernel/src/capabilities/seam-coverage.ts`'s `RENDERED_INJECTION_POINTS.shell`
 * is concerned; `host/test/seam-render-coverage.test.ts` traces this exact function at test time
 * (via a `Proxy` over `plan.injections`) against that table and fails if the two ever disagree.
 * Adding, removing, or renaming a `seam(plan, '...')` call site below is a table-affecting change —
 * the test will say so, but it is worth knowing why the moment you touch one.
 *
 * CAOS-751 — that guarantee is about the points `assembleShellFrame` renders with. {@link
 * edgeTabChoices} also reads this accessor, for a NON-rendering purpose (listing the anchored
 * sources for My Profile's preferences), and is not part of the render-coverage trace: it draws
 * nothing and is never reached through `assembleShellFrame`. It reads `edge-anchored`, a point the
 * frame already renders, so it adds nothing to the traced set either way.
 */
function seam(plan: ShellFramePlan, point: string): readonly ShellInjectionRef[] {
  return plan.injections[point] ?? [];
}

/**
 * CAOS-1492 K7 — where a contributed seam ENTRY opens.
 *
 * `sourceKey` is the contributing PACKAGE's key, which is the right address only when the package
 * and the app it ships are the same word; `pkg_report_a_bug` shipping the `feedback` app was sent
 * to `/pkg_report_a_bug`, which resolves to no app at all. A claim that names a destination opens
 * that instead. Module scope because CAOS-768 gave it a second caller, for the same reason
 * {@link labelOf} sits here: two seams deriving an address for themselves would drift the moment
 * either rule changed.
 */
function entryHref(ref: ShellInjectionRef): string {
  const d = ref.destination;
  if (!d?.app) return `/${ref.sourceKey}`;
  return d.tab ? `/${d.app}/${d.tab}` : `/${d.app}`;
}

/**
 * CAOS-768 — the shell seam a package extends to pin a destination into the workspace tab row.
 *
 * Mirrored from `kernel/src/capabilities/slots.ts` rather than imported: `@caos/host` deploys as its
 * own dependency-free Worker and never imports kernel code (see `kernel-contract.ts`'s header). The
 * pair is held in agreement STRUCTURALLY — `host/test/caos-768-home-tabs.test.ts` reads the kernel
 * file off disk and asserts both values against it, so a change to either side that forgets the
 * other reds rather than quietly rendering a seam nobody declares or dropping a tab that was
 * accepted at deploy.
 */
const HOME_TABS_SEAM = 'home-tabs';

/**
 * CAOS-768 — how many contributed tabs the row draws. The kernel refuses a COMPONENT that declares
 * more than this (deploy rule 10), which is the limit it can enforce without one package's install
 * failing over another's choices; the limit ACROSS packages can only be applied where they meet,
 * which is here. Applied after ordering, never before, so which two survive is the seam's declared
 * order (and the org's pin, CAOS-732) rather than whichever rows the registry happened to return
 * first.
 */
const HOME_TAB_SLOTS = 2;

/**
 * CAOS-768 — the pinned tabs, in row order, capped at the slots the row has.
 *
 * The same defensive second guarantee {@link orderedAnchors} gives the edge gutter: the kernel's
 * registry already returns this seam's rows ordered on this exact key with the org's pin applied,
 * and re-deriving it here means a caller that assembles a plan directly — a test, the dev fixture,
 * a stub registry — cannot silently fall back to install-order. That matters more here than on an
 * unbounded seam: with the order wrong, the cap does not merely reshuffle the row, it decides which
 * package gets a tab at all.
 */
function homeTabEntries(
  plan: ShellFramePlan,
): { key: string; label: string; href: string; icon?: string; app: string; tab?: string }[] {
  const refs = seam(plan, HOME_TABS_SEAM);
  const pinned = plan.seamOrderOverrides?.[HOME_TABS_SEAM];
  const override = pinned && pinned.length > 0 ? { sourceKeys: pinned } : undefined;
  const ordered = orderSeamContributions(refs, (r) => ({ sourceKey: r.sourceKey, order: r.homeTab?.order }), override);
  return ordered.slice(0, HOME_TAB_SLOTS).map((ref) => {
    // A DECLARED label wins; absent one the tab is named the way every other contributed entry is.
    // Guarded by `typeof` for the reason `labelOf` documents: the boot envelope reaches the host
    // through an unchecked cast, so the declared type is not a guarantee about the value.
    const declared: unknown = ref.homeTab?.label;
    const label = typeof declared === 'string' && declared.trim().length > 0 ? declared.trim() : labelOf(ref);
    const icon: unknown = ref.homeTab?.icon;
    return {
      key: ref.sourceKey,
      label,
      href: entryHref(ref),
      ...(typeof icon === 'string' && icon.length > 0 ? { icon } : {}),
      app: ref.destination?.app ?? ref.sourceKey,
      ...(ref.destination?.tab ? { tab: ref.destination.tab } : {}),
    };
  });
}

/**
 * CAOS-713 — the standard `workspace` blueprint's shape (CAOS-658), as a host-side DATA fixture.
 * {@link assembleShellFrame} falls back to this ONLY when `plan.frame` is absent — production
 * always carries a real `plan.frame` (the kernel resolves it unconditionally in
 * `runtime/boot-orchestrator.ts`), but the pre-existing dev fixture and every pre-CAOS-713 test
 * build a plan without one. This is not a second resolution path — it is the same 5-region shape
 * the kernel seeds, so an un-migrated caller renders exactly the frame it did before this ticket.
 */
const DEFAULT_WORKSPACE_FRAME: ShellFrameTemplatePlan = {
  requested: 'workspace',
  resolved: true,
  regions: [
    { key: 'banner', label: 'Banner', pinned: true },
    { key: 'header', label: 'Header', pinned: true },
    { key: 'nav_strip', label: 'App Navigation', pinned: true },
    { key: 'record_tabs_strip', label: 'Record Tabs', pinned: true },
    { key: 'main', label: 'Main', columns: 2 },
  ],
  geometry: {
    areas: ['banner', 'header', 'nav_strip', 'record_tabs_strip', 'main'],
    cols: 'minmax(0, 1fr)',
    stack: ['banner', 'header', 'nav_strip', 'record_tabs_strip', 'main'],
  },
};

/**
 * The blueprint's PRIMARY region — `main` when declared, else the first. Mirrors the kernel's
 * `presentation/place.ts` `primaryRegionKey`, applied here to the FRAME's own region set: the
 * region that gets the remaining viewport height and scrolls, while the rest size to their content.
 */
function primaryFrameRegionKey(regions: readonly ShellFrameRegion[]): string {
  return regions.find((r) => r.key === 'main')?.key ?? regions[0]?.key ?? 'main';
}

/**
 * One row track per declared `areas` row: `minmax(0, 1fr)` for the row holding the PRIMARY region
 * (it fills whatever height the chrome rows do not take, and scrolls within that height), `auto`
 * for every other row (sized to its own chrome, exactly as the frame's flex-column layout sized
 * its chrome bars before this ticket). The blueprint's geometry does not carry row-sizing — a
 * template declares REGIONS and their relationship, never pixels or which one grows — so this is
 * the host's own rendering convention for turning "the regions and their relationship" into an
 * actual viewport-filling grid, not a kernel capability.
 */
function frameGridTemplateRows(areas: readonly string[], primaryKey: string): string {
  return areas.map((row) => (row.trim().split(/\s+/).includes(primaryKey) ? 'minmax(0, 1fr)' : 'auto')).join(' ');
}

/**
 * CAOS-713 — build the frame's region grid by INTERPRETING the resolved workspace blueprint: one
 * wrapper per declared region (keyed `data-region`, `data-pinned` when the blueprint pins it),
 * laid out as a CSS grid whose `grid-template-areas`/`-columns` are the blueprint's OWN geometry —
 * never a hardcoded region set. The PRIMARY region is flagged `data-role="primary"` (see
 * `shell-frame.css`) so it — and only it — gets the remaining height and scrolls.
 */
function buildFrameGrid(frame: HTMLElement, template: ShellFrameTemplatePlan): Map<string, HTMLElement> {
  const grid = document.createElement('div');
  grid.className = 'caos-frame__grid';
  grid.dataset.template = template.requested;
  grid.style.gridTemplateAreas = template.geometry.areas.map((row) => `"${row}"`).join(' ');
  grid.style.gridTemplateColumns = template.geometry.cols;
  const primary = primaryFrameRegionKey(template.regions);
  grid.style.gridTemplateRows = frameGridTemplateRows(template.geometry.areas, primary);

  const regionEls = new Map<string, HTMLElement>();
  for (const region of template.regions) {
    const el = document.createElement('div');
    el.className = 'caos-frame__region';
    el.dataset.region = region.key;
    el.style.gridArea = region.key;
    if (region.pinned) el.dataset.pinned = 'true';
    if (region.key === primary) el.dataset.role = 'primary';
    regionEls.set(region.key, el);
    grid.append(el);
  }
  frame.append(grid);
  return regionEls;
}

/**
 * Assemble the shell chrome around a content region, from the kernel plan + runtime context.
 * Pure (no I/O, no globals) so tests drive it directly. Returns the frame root element.
 */
/**
 * CAOS-152 — the org metadata generation the workspace was built from, for anything a person
 * submits about what they are looking at.
 *
 * Held as module state rather than threaded through every panel because the panels that need it
 * are opened from the frame's own chrome, several calls below where the plan is in scope, and
 * threading a number through four builders that do not otherwise care about it would be worse in
 * every way than one value the frame sets when it assembles.
 *
 * Absent until a plan carrying one has been assembled. That is a real state, not a placeholder: a
 * plan built before this field existed, or a fixture not modelling generations, genuinely does not
 * know, and {@link generationField} then sends nothing rather than sending a number nobody observed.
 */
let activeMetadataGeneration: number | undefined;

/** The generation as a request field, or nothing at all when none was ever stated. */
function generationField(): { metadataGeneration?: number } {
  return activeMetadataGeneration === undefined ? {} : { metadataGeneration: activeMetadataGeneration };
}

/**
 * CAOS-1688 — the reporter's browser as a request field, read from the browser itself.
 *
 * This is the whole reason the ticket is a host change and not only a kernel one: the one place the
 * user agent exists is the browser doing the submitting, and the alternative — an authored form field
 * — would ask a reporter to type a string they cannot be expected to know and would get wrong. It
 * belongs with `pageUrl` and `appContext`: context captured at submit, never asked for.
 *
 * Returns NOTHING rather than a placeholder when there is no navigator or it reports an empty string.
 * Sending `''` would be stored as "the browser said nothing", which is a different claim from "we did
 * not capture it", and only the second is true of a non-browser runtime such as the test environment
 * or a server-side render. See the column comment on `caos.feedback.user_agent`.
 */
function userAgentField(): { userAgent?: string } {
  if (typeof navigator === 'undefined') return {};
  const ua = navigator.userAgent;
  return typeof ua === 'string' && ua.length > 0 ? { userAgent: ua } : {};
}

export function assembleShellFrame(plan: ShellFramePlan, runtime: ShellRuntime = {}): HTMLElement {
  // CAOS-152 — what the workspace was built from, recorded as it is assembled so a report opened
  // from this frame's own chrome can say which generation the person was looking at.
  activeMetadataGeneration = plan.metadataGeneration;
  const frame = document.createElement('div');
  frame.className = 'caos-frame';
  frame.dataset.shellMaster = plan.master;

  // CAOS-713 — the frame's region skeleton is INTERPRETED from the resolved workspace blueprint,
  // never hardcoded here. `plan.frame` is absent only for a not-yet-migrated fixture/test (see
  // `DEFAULT_WORKSPACE_FRAME`'s doc); production always carries the kernel-resolved one.
  const frameTemplate = plan.frame ?? DEFAULT_WORKSPACE_FRAME;
  const regionEls = buildFrameGrid(frame, frameTemplate);
  const framePrimaryKey = primaryFrameRegionKey(frameTemplate.regions);
  /**
   * Fail-safe placement (CAOS-713): a surface bound to a region the resolved blueprint does not
   * declare (a customized blueprint dropped it) lands in the PRIMARY region instead — degrade,
   * never drop the surface or crash, the same discipline `presentation/place.ts` documents for a
   * page's sections landing in a template that does not declare their requested region.
   */
  const regionFor = (key: string): HTMLElement => regionEls.get(key) ?? regionEls.get(framePrimaryKey)!;

  // navigation-entries are the installed APPS (CAOS-267): they populate the App Launcher, and the
  // ACTIVE app's own tabs populate the app-scoped nav strip + the nav-gadget overflow.
  //
  // CAOS-302/291: an ABSENT `activeAppKey` means the Atlantis AI HOME (no app pre-selected) — the
  // frame does NOT auto-open the first app. An active app opens on its landing tab's real content,
  // never a generated menu of tab names.
  const navEntries = seam(plan, 'navigation-entries');
  const activeTabKey = runtime.activeTabKey;
  const nav = (href: string): void => runtime.navigate?.(href);
  setActiveNavigate(nav);

  // 1) impersonation-banner — only while impersonating, or (CAOS-606) while previewing a role.
  //
  // One banner for both, because it makes the same promise to the person reading it: what is on
  // this screen is not your own workspace. Building a second strip would have meant two things
  // that must never both be missing, maintained apart.
  //
  // They are NOT the same act, and the labels say so. An impersonation is somebody else's session;
  // a preview is your own session with part of your access set aside, so the banner names the ROLE
  // rather than a person, and says "Previewing as" rather than "Viewing as" — because the rows on
  // screen are still yours, and a banner that implied otherwise would misdescribe the one thing
  // this feature is careful about.
  // CAOS-1904 AC2 — tell the door whether a borrowed session is in force, so a second door drawn
  // somewhere else can be UNAVAILABLE and say so rather than pressable and refused on submit.
  //
  // Set here, unconditionally and on every render, rather than once at boot: the frame keeps its
  // top chrome across an in-place navigation, so a value fixed at boot would outlive the session
  // it was true for — the same reasoning `editPageHrefFor` is asked per route for. Set BEFORE the
  // banner, so the one place that reads `runtime.impersonation` to draw and the one that reads it
  // to gate cannot disagree about the render they are in.
  setImpersonationRunning(Boolean(runtime.impersonation));
  const previewing = runtime.activePreview ?? null;
  if (runtime.impersonation || previewing) {
    const banner = document.createElement('caos-impersonation-banner');
    if (runtime.impersonation) {
      // Both at once. CAOS-607 landed the other one while this sat in review, so the two are now
      // genuinely combinable: an administrator acting as somebody else can preview that session
      // as a role. The banner's whole job is to say what is not your own workspace, and an `else`
      // here would have it name one of two such things and stay silent about the other. Whichever
      // arrives second is the one a person has not noticed, so dropping either drops the one that
      // mattered.
      banner.setAttribute(
        'target-label',
        previewing
          ? `${runtime.impersonation.targetLabel} — previewing as ${previewing.label}`
          : runtime.impersonation.targetLabel,
      );
      if (runtime.impersonation.realLabel) banner.setAttribute('real-label', runtime.impersonation.realLabel);
      // CAOS-607 — the cap, on screen. Absent when the kernel could not name one; the strip then
      // draws without a countdown rather than with an invented one.
      if (runtime.impersonation.expiresAt) banner.setAttribute('expires-at', runtime.impersonation.expiresAt);
    } else if (previewing) {
      banner.setAttribute('target-label', `Previewing as ${previewing.label}`);
      // The real label is the person themselves: the workspace is still theirs, and the rows in it
      // are the rows they could always read.
      if (runtime.user?.name) banner.setAttribute('real-label', runtime.user.name);
    }
    // The banner's end control stops the PREVIEW whenever one is running. An impersonation is ended
    // by whatever started it, which is not this frame.
    if (previewing) banner.addEventListener('impersonation-end', () => runtime.setPreviewRole?.(null));
    banner.setAttribute('product', humanize(plan.master));
    regionFor('banner').append(banner);
  }

  // 2) global-header — the topbar. Brand resolves from the theme tokens (--brand-wordmark);
  //    the account block from the session; header-actions into the `center` slot.
  const topbar = document.createElement('caos-topbar');
  topbar.setAttribute('show-setup', '');
  setActiveUserName(runtime.user?.name);
  if (runtime.user) {
    topbar.setAttribute('user-name', runtime.user.name);
    // CAOS-342 — the second line of the account block names the role this person holds and the
    // workspace they are in ("Bootstrap Admin · CAOS Dev"). The menu head repeats it, so the role
    // stays on screen.
    if (runtime.user.sub) topbar.setAttribute('user-sub', runtime.user.sub);
    // CAOS-606 — the "Viewing as" switcher, live. CAOS-581 unplugged it here because nothing on
    // the platform could re-resolve the workspace under a chosen subset of somebody's permission
    // sets; `preview state`/`start`/`end` now can, so the list goes back.
    //
    // Still gated on the host having somewhere to send the choice: the component's own contract is
    // that it must never offer an entry no host is listening for, and handing it roles without
    // `setPreviewRole` would relight exactly the dead control CAOS-581 removed. Both or neither.
    //
    // The list comes from `previewRoles` (the kernel's preview resolver) rather than from
    // `user.roles` (whoami's directly-assigned sets), so the menu cannot offer a role the resolver
    // would not honour, or omit one it would — a set held through a permission set group is a role
    // this person holds, and previewing as it works.
    if (runtime.setPreviewRole && runtime.previewRoles && runtime.previewRoles.length > 0) {
      topbar.personaOptions = runtime.previewRoles.map((r) => ({ key: r.key, label: r.label }));
      if (previewing) {
        topbar.setAttribute('active-persona-key', previewing.key);
        topbar.setAttribute('persona-label', previewing.label);
      }
      topbar.addEventListener('topbar-persona', (ev) => {
        // A null key is the menu's "Reset to default" entry — stop previewing.
        const key = (ev as CustomEvent<{ key: string | null }>).detail?.key ?? null;
        runtime.setPreviewRole!(key);
      });
    }
  }
  if (runtime.env) topbar.setAttribute('env', runtime.env);

  // CAOS-294 — the theme switch, now LIVE. Seed the button with the saved preference, then cycle
  // system → light → dark on each click: apply it to <html> (theme.ts flips the tokens.css cascade)
  // and persist it. The topbar's sun/moon icon reflects the state via the shared THEME_CHANGE_EVENT,
  // so a change from the Profile preference keeps the button in sync too. The switch is
  // route-independent chrome, so it stays wired across in-place navigation.
  topbar.setAttribute('theme', currentThemePref());
  topbar.addEventListener('topbar-theme', () => {
    applyThemePref(nextThemePref(currentThemePref()));
  });
  // Only the newest frame listens, as with the open-record subscription: a re-render builds a fresh
  // topbar, and a previous one left subscribed would stay alive for the rest of the session.
  activeThemeListener = replaceDocumentListener(THEME_CHANGE_EVENT, activeThemeListener, (ev) => {
    const pref = (ev as CustomEvent<ThemePref>).detail;
    if (pref) topbar.setAttribute('theme', pref);
  });

  // CAOS-137 — "View my profile" opens the Profile surface (the topbar account menu emits this).
  // CAOS-372 — the avatar menu opens the My Profile APP, so the place you land is a place you can
  // find again from the switcher rather than a route only this menu knows about.
  topbar.addEventListener('topbar-profile', () => nav('/my_profile'));

  // CAOS-326 — "My feedback" from the account menu: what you reported and what happened to it,
  // reachable from anywhere in the workspace rather than only from the report form.
  // CAOS-50 — "Get help" opens the composer in place rather than navigating, so a person asking
  // for help does not lose the screen they were stuck on — which is also the context the
  // submission captures.
  topbar.addEventListener('topbar-get-help', () => openHelp());
  topbar.addEventListener('topbar-my-feedback', () => nav('/my_profile/feedback'));

  // CAOS-140 — the Setup gear opens the Setup admin app (its console + Object Manager).
  topbar.addEventListener('topbar-setup', () => nav('/setup'));

  // CAOS-694/CAOS-342 — the account menu's per-app "Settings" entry. `settingsHrefFor` answers
  // with the fully-resolved gate (administers the app this route selects AND it declares a
  // settingsPage) — reflect the answer; there is nothing left to re-check here.
  //
  // CAOS-1659 — RE-ASKED ON EVERY ROUTE, because this frame's top chrome survives an in-place
  // navigation and the answer does not. Called once here for the route the frame is assembled on,
  // and again by the navigator below for each route after it. REMOVING the attribute matters as
  // much as setting it: the previous route's entry has to go when the new route has no answer.
  let settingsHref: string | undefined;
  const syncSettingsEntry = (route: string): void => {
    settingsHref = runtime.settingsHrefFor?.(route);
    if (settingsHref) topbar.setAttribute('show-settings', '');
    else topbar.removeAttribute('show-settings');
  };
  // `currentPath` is declared further down with the open-records strip it belongs to, so this reads
  // the same source it initialises from rather than referencing it before it exists.
  syncSettingsEntry(typeof location !== 'undefined' ? location.pathname : '');
  // One listener for the life of the frame, reading the href the last sync resolved. Re-adding a
  // listener per navigation is how CAOS-564's duplicate-listener class of bug starts.
  topbar.addEventListener('topbar-settings', () => {
    if (settingsHref) nav(settingsHref);
  });

  // CAOS-608 — "Edit this page", wired for the first time. The entry and its event have existed
  // since CAOS-581, which deliberately left them unclaimed rather than point them at Setup's then
  // read-only layout browse: an entry that opens a viewer when it says "edit" is worse than one
  // that is honestly absent. CAOS-142 built the editor, so there is somewhere real to send it.
  //
  // RE-ASKED ON EVERY ROUTE, because this frame's top chrome survives an in-place navigation and
  // the answer does not. `syncEditPageEntry` is called once here for the route the frame is
  // assembled on, and again by the navigator below for each route after it.
  let editPageHref: string | undefined;
  const syncEditPageEntry = (route: string): void => {
    editPageHref = runtime.editPageHrefFor?.(route);
    if (editPageHref) topbar.setAttribute('show-edit-page', '');
    else topbar.removeAttribute('show-edit-page');
  };
  // currentPath is declared further down with the open-records strip it belongs to, so this
  // reads the same source it initialises from rather than referencing it before it exists.
  syncEditPageEntry(typeof location !== 'undefined' ? location.pathname : '');
  // One listener for the life of the frame, reading the href the last sync resolved. Re-adding a
  // listener per navigation is how CAOS-564's duplicate-listener class of bug starts.
  topbar.addEventListener('topbar-edit-page', () => {
    if (editPageHref) nav(editPageHref);
  });

  // CAOS-336 — Sign out, now actually wired. The account menu has always EMITTED this event, but
  // nothing in the assembled shell listened for it, so choosing "Sign out" did nothing at all: no
  // request left the browser and the session stayed live across a reload. On a shared machine a
  // sign-out that silently leaves you signed in is a security problem, not a cosmetic one.
  //
  // Drive the SAME logout path the Profile surface uses — POST the logout endpoint, then leave for
  // the root, where boot resolves the signed-out state and renders sign-in. Navigate even if the
  // request fails, so a network error can never strand someone in a workspace they asked to leave.
  topbar.addEventListener('topbar-signout', () => {
    void (async () => {
      try {
        await signOutSession(() =>
          controlPlaneFetch(SESSION_SIGN_OUT_ENDPOINT, { method: 'POST' }),
        );
      } catch {
        // Swallow deliberately: a failed logout call must not become an unhandled rejection, and
        // must not stop us leaving. The navigation below is what the person asked for.
      } finally {
        leaveTo('/');
      }
    })();
  });


  // CAOS-607 — "Log in as another user", now with something behind it.
  //
  // The menu draws this picker only when it is HANDED people, and nothing ever handed it any, so
  // the entry has never appeared on screen. (The CAOS-581 audit reported it as a control that did
  // nothing when pressed; it could not be pressed.) This hands it the kernel's answer.
  //
  // Read from runtime state, NOT fetched here. This function runs on every in-app navigation — the
  // shell repaints its regions in place — and who you may act as changes about never, so asking on
  // each assembly would be a request per click. It is resolved once at boot, beside the banner,
  // and for the same reason: both are properties of the session rather than of the workspace.
  //
  // Nobody eligible, or a caller without the permission, arrives here as an absent list and the
  // entry simply does not appear. For almost everybody signed in that is the correct outcome.
  if (!runtime.impersonation && runtime.impersonationOptions && runtime.impersonationOptions.length > 0) {
    (topbar as unknown as { impersonationOptions: { id: string; label: string }[] }).impersonationOptions =
      runtime.impersonationOptions.map((p) => ({ id: p.id, label: p.label }));
  }

  topbar.addEventListener('topbar-impersonate', (ev) => {
    const detail = (ev as CustomEvent<{ id?: string; label?: string }>).detail;
    if (!detail?.id) return;
    // CAOS-1904 — no `offerWrite`, which keeps this entry exactly as it was: the account menu has
    // only ever started READ-ONLY sessions, because it never sent the `write` flag the route reads.
    // That is the safe collapse of two permissions and it is deliberately not changed here — AC4 of
    // CAOS-1904 is that the existing door is untouched. The Setup door offers the choice.
    askWhyThenStart({ body: { principal: detail.id }, label: detail.label ?? 'that person' });
  });

  // CAOS-607 — the way out. Delete the impersonation and reload as yourself.
  //
  // `leaveTo` rather than an in-place re-render: who the workspace resolves as is decided at boot,
  // and every surface already on screen was built for somebody else. Re-rendering pieces of it
  // would leave a frame half-belonging to each person, which is precisely the confusion the banner
  // exists to prevent.
  frame.addEventListener('impersonation-end', () => {
    void (async () => {
      try {
        await controlPlaneFetch(IMPERSONATION_END_ENDPOINT, { method: 'POST' });
      } catch {
        // Swallowed on purpose, and the navigation still happens. The host clears the cookie on
        // this path whatever the kernel answered, so leaving is what actually returns somebody to
        // themselves — a failed call must never be the thing that keeps them inside another
        // person's account.
      } finally {
        leaveTo('/');
      }
    })();
  });

  const headerActions = orderedHeaderActions(seam(plan, 'header-actions'), plan.seamOrderOverrides?.['header-actions']);
  if (headerActions.length > 0) {
    const group = document.createElement('div');
    group.className = 'caos-frame__header-actions';
    group.setAttribute('slot', 'center');
    for (const action of headerActions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'caos-frame__header-action';
      btn.dataset.sourceKey = action.sourceKey;
      btn.textContent = labelOf(action);
      group.append(btn);
    }
    topbar.append(group);
  }

  // CAOS-135 — global search, part of the global header itself rather than a contribution: every
  // workspace gets it, in the centre slot ahead of any header-actions. A hit opens its record in the
  // first installed app that shows its object, so the destinations come from the same
  // navigation-entries the App Launcher lists.
  topbar.prepend(createGlobalSearch({ destinations: searchDestinations(navEntries), navigate: nav }));

  // CAOS-581 — avatar-menu contributions now appear IN the menu. They used to be recorded on the
  // element as a data attribute and rendered nowhere, so a package could claim the seam and its entry
  // would never show up where a person would look for it. Each contributed entry opens the app that
  // contributed it, which is what a source key addresses everywhere else in this navigation (it is
  // the same `/<sourceKey>` route the app launcher opens).
  const avatarMenu = seam(plan, 'avatar-menu');
  // CAOS-753 — the fallback path for a tab this person hid (CAOS-751). The right-edge gutter is the
  // only way to reach an anchored source's surface, so hiding its tab would otherwise remove ACCESS
  // and not merely the tab — a setting that quietly uninstalls something is not a preference.
  //
  // They ride the seam that already exists rather than a new one: the account menu is where the
  // workspace already puts "things about you and yours that are not on the screen", and a hidden
  // source is exactly that. Nothing is claimed on `avatar-menu` itself — a package's contributions
  // are untouched and come first; these are appended by the host, which is also why they can appear
  // in a workspace where no package contributes to that seam at all.
  //
  // A package that contributes to BOTH seams under one key keeps its own entry and gets no fallback:
  // it already named a destination here, and listing the key twice would make the menu ambiguous
  // about which one was chosen — the event carries only the key.
  const edgeSources = seam(plan, 'edge-anchored');
  const contributedKeys = new Set(avatarMenu.map((i) => i.sourceKey));
  const fallbackSources = (): ShellInjectionRef[] =>
    edgeSources.filter((a) => !contributedKeys.has(a.sourceKey) && !edgeTabShown(a.sourceKey));
  // Asked at the moment of the click rather than remembered from the last render: the list and the
  // handler then cannot disagree about what a key means, however the menu came to be drawn.
  const isFallback = (key: string): boolean =>
    !contributedKeys.has(key) && edgeSources.some((a) => a.sourceKey === key) && !edgeTabShown(key);
  const avatarItems = (): { key: string; label: string }[] => [
    ...avatarMenu.map((i) => ({ key: i.sourceKey, label: labelOf(i) })),
    ...fallbackSources().map((a) => ({ key: a.sourceKey, label: labelOf(a) })),
  ];

  if (avatarMenu.length > 0 || edgeSources.length > 0) {
    topbar.dataset.avatarMenuInjections = avatarMenu.map((i) => i.sourceKey).join(',');
    (topbar as unknown as { avatarMenuItems: unknown }).avatarMenuItems = avatarItems();
    // CAOS-1492 K7 — a claim that names a DESTINATION opens that, rather than its own source key.
    // `sourceKey` is the contributing PACKAGE's key, which is the right address only when the
    // package and the app it ships are the same word; `pkg_report_a_bug` shipping the `feedback`
    // app was previously sent to `/pkg_report_a_bug`, which resolves to no app at all.
    const hrefByKey = new Map(avatarMenu.map((i) => [i.sourceKey, entryHref(i)]));
    topbar.addEventListener('topbar-avatar-item', (ev) => {
      const key = (ev as CustomEvent<{ key?: string }>).detail?.key;
      if (!key) return;
      // CAOS-753 — a hidden source OPENS, it does not navigate. Its surface is a panel in the gutter,
      // not a route: sending this to `/<sourceKey>` would land on an app that in general does not
      // exist, which is a worse answer than the tab being hidden.
      if (isFallback(key) && openEdgeSource(key)) return;
      nav(hrefByKey.get(key) ?? `/${key}`);
    });
  }
  // The menu follows the preference: hide a tab in My Profile and it is reachable here at once, show
  // it again and it stops being listed twice over. Replaced for EVERY frame, including one with
  // nothing on either seam — conditioning it on the block above would leave a workspace that anchors
  // nothing holding the previous frame's listener, writing menu items onto a detached topbar.
  activeAvatarVisibilityListener = replaceDocumentListener(
    PREFERENCES_CHANGE_EVENT,
    activeAvatarVisibilityListener,
    (ev) => {
      const keys = (ev as CustomEvent<{ keys?: string[] }>).detail?.keys ?? [];
      if (!keys.some((k) => k.startsWith(RAIL_SHOWN_PREFIX))) return;
      (topbar as unknown as { avatarMenuItems: unknown }).avatarMenuItems = avatarItems();
    },
  );
  regionFor('header').append(topbar);

  // 3) app-navigation-strip — workspace-tabs, with the launcher (app-switcher) + nav-gadget slotted.
  //    These chrome nodes are built ONCE and PERSIST across in-app navigation (CAOS-300); a client
  //    navigation re-feeds their data in place (paintAppRegion) rather than remounting the frame.
  const strip = document.createElement('caos-workspace-tabs');
  /**
   * CAOS-1651 — the org's home, resolved once per frame. It is a property of what this org has
   * installed and designated, not of where the person is standing, so an app switch must not be
   * able to change it (the same reason `pinnedHomeTabs` is resolved once below).
   */
  const homeApp = resolveOrgHomeApp(plan);
  if (homeApp) {
    // The anchor names what it opens. Before this the anchor read "Home" while `/` already went to
    // the designated app (CAOS-291 shipped that half), so the control named one destination and
    // went to another — the shape CAOS-769 removed, arrived at from the other direction.
    strip.setAttribute('home-label', labelOf(homeApp));
  }
  // A chosen tab / the AI home anchor route through the runtime's navigate seam.
  strip.addEventListener('workspace-navigate', (ev) => {
    const href = (ev as CustomEvent<{ href?: string }>).detail?.href;
    if (href) nav(href);
  });
  // CAOS-507 — a destination that opts into a separate browser tab emits a different event, so
  // opening it never touches this document's history, route, or mounted workspace state.
  strip.addEventListener('workspace-open-new-tab', (ev) => {
    const href = (ev as CustomEvent<{ href?: string }>).detail?.href;
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  });
  strip.addEventListener('workspace-home', () => nav('/'));

  // app-switcher — the launcher, slotted leading. Its apps ARE the installed apps (navigation-entries),
  // minus any that opt out of the switcher (CAOS-292 — the workspace is the outer page template every
  // app renders inside, not a destination of its own).
  const launcher = document.createElement('caos-app-launcher');
  launcher.setAttribute('slot', 'launcher');
  // CAOS-376 — the switcher lists APPS. It used to expand the current app in place into its tabs,
  // which turned a short list of places to go into a long one and put the same destinations in two
  // navigations at once — the tab bar is right there, and it is where tabs belong.
  //
  // A workspace that wants its tabs in the launcher is free to build one; this is the CAOS default
  // workspace, and this is the shape it takes.
  const launcherApps = (): unknown =>
    navEntries.filter(offeredInSwitcher).map((e) => ({
      key: e.sourceKey,
      label: labelOf(e),
      icon: e.icon,
      destinationCount: e.tabs?.length ?? 0,
    }));
  (launcher as unknown as { apps: unknown }).apps = launcherApps();
  // Choosing an app navigates to it through the same navigate seam (client-routed in production).
  launcher.addEventListener('launcher-navigate', (ev) => {
    const detail = (ev as CustomEvent<{ key?: string; href?: string }>).detail;
    const href = detail?.href ?? (detail?.key ? `/${detail.key}` : undefined);
    if (href) nav(href);
  });
  // CAOS-287 — the launcher's foot affordance. The component only reports that it was chosen; what
  // opens is the host's, supplied on the runtime. With no handler this is deliberately inert rather
  // than a stub surface, so a frame with no install seam cannot offer an install that cannot happen.
  launcher.addEventListener('launcher-get-more-apps', () => {
    runtime.getMoreApps?.();
  });
  strip.append(launcher);

  // The Customize Navigation control, slotted trailing (CAOS-293) — the sliders button that opens the
  // Customize Navigation popup directly.
  //
  // CAOS-580 — the arrangement is REMEMBERED. It used to be held only in this closure, so it was gone
  // on the next load while the popup said it had been saved. It now goes through `nav-preferences`,
  // kept against the person with the theme choice and the rail pinning (CAOS-470), and is re-read every
  // time the strip is populated — including the very first paint, which is what makes it survive a
  // reload rather than merely a navigation.
  const gadget = document.createElement('caos-nav-gadget');
  gadget.setAttribute('slot', 'gadget');
  let currentAppKey: string | undefined;
  let currentTabKey: string | undefined;
  /**
   * CAOS-1820 — the LAST REAL APP ENTERED ("stickyApp" in the reference application's
   * `AppLayout.tsx:450-461`, UI-307; CAOS-1820 cites the path in full, which this cannot
   * — a reference app's folder is named for ITS business domain and the vocabulary census
   * refuses a customer's business word anywhere under `host/src`). The global Home is a
   * DESTINATION, not an app switch: visiting it focuses the Home surface but must not throw away the
   * app you were in. The app's object nav and the open-records strip are the only listing of the
   * work you have in progress, and the Home anchor is the most reachable control in the strip, so
   * discarding them there empties that listing on one click with no warning and no undo.
   *
   * Undefined until the first real app is entered, which is what keeps a FRESH session showing the
   * launcher and the Home anchor alone — the parity the audit measured as already correct.
   *
   * Set only for an app that is genuinely in `navEntries`. An UNKNOWN app segment is not an app
   * entered, so it neither becomes the sticky app nor displaces the one already held: it keeps the
   * "no app's chrome" reading it has always had (see the CAOS-1660 note in `populateStrip`).
   *
   * Only the APP LAUNCHER swaps it, and that falls out rather than being enforced here: the
   * launcher is the one control that navigates to another app's own route, and entering an app is
   * what sets this. The Home anchor navigates to `/`, which names no app and so changes nothing.
   */
  let stickyApp: ShellInjectionRef | undefined;
  /**
   * The app whose chrome is on screen: the route's own app, or — on the global Home, where the route
   * names none — the sticky app. This is the ONE answer the launcher highlight, the strip's app name,
   * its tab set, its arrangement and the open-records strip all read, so they cannot disagree about
   * which app's context is showing.
   *
   * Deliberately NOT a replacement for `currentAppKey`, which stays the RAW route app. Two things
   * already depend on the raw reading and would regress if it were folded into this one: the
   * guidance scope below needs "no app open" to stay tellable from "an app's landing", and the
   * browser title needs `/` to name the home wherever you reached it from (CAOS-291/CAOS-1660).
   */
  let currentContextAppKey: string | undefined;
  gadget.addEventListener('gadget-nav-change', (ev) => {
    const detail = (ev as CustomEvent<{ items?: { id: string }[] }>).detail;
    if (!currentContextAppKey || !detail?.items) return;
    const app = navEntries.find((e) => e.sourceKey === currentContextAppKey);
    const published = (app?.tabs ?? []).map((t) => ({ key: t.key }));
    saveNavArrangement(currentContextAppKey, arrangementFrom(published, detail.items.map((i) => i.id)));
    populateStrip(currentAppKey, currentTabKey);
  });
  // Reset to Default — forget this app's arrangement and repaint from what the app publishes. The
  // repaint is what restores the app ON SCREEN: the control no longer keeps its own copy of a
  // default list, which is why resetting used to restore whichever app was opened first.
  gadget.addEventListener('gadget-nav-reset', () => {
    if (!currentContextAppKey) return;
    clearNavArrangement(currentContextAppKey);
    populateStrip(currentAppKey, currentTabKey);
  });
  // CAOS-470 — the arrangement is kept against the person, so it can arrive after the first paint:
  // preferences load at sign-in, and a write settles to what the kernel stored. Repaint the strip
  // whenever the arrangement of the app on screen changes. Only the newest frame listens.
  activeNavPreferenceListener = replaceDocumentListener(PREFERENCES_CHANGE_EVENT, activeNavPreferenceListener, (ev) => {
    const keys = (ev as CustomEvent<{ keys?: string[] }>).detail?.keys ?? [];
    if (currentContextAppKey && keys.includes(`nav.arrangement.${currentContextAppKey}`))
      populateStrip(currentAppKey, currentTabKey);
  });

  // CAOS-345 — the per-tab list-views menu. ONE menu is owned here and reused for every tab (the
  // component is a controlled popover, portaled to the body so the tab row cannot clip it). The tab
  // strip emits `tab-caret` with the tab and the caret's live screen rect; we fill the menu with the
  // saved views the tab carries and open it there. Choosing one navigates to the same tab with the
  // view named in the address, so the list opens on it and the address is shareable.
  const caret = document.createElement('caos-object-caret');
  // CAOS-1701 — which caret opening the pending recents answer belongs to. Bumped on every open,
  // so a late answer for a tab the person has already left is discarded rather than drawn.
  let caretOpenToken = 0;
  const caretViews = (appKey: string | undefined, tab: ShellNavTab): unknown =>
    (tab.listViews ?? []).map((v) => ({
      id: v.key,
      label: v.label,
      href: `/${appKey}/${tab.key}?view=${encodeURIComponent(v.key)}`,
    }));
  strip.addEventListener('tab-caret', (ev) => {
    const detail = (ev as CustomEvent<{ id?: string; anchor?: { left: number; bottom: number; top?: number; right?: number } }>).detail;
    // CAOS-1820, FROM REVIEW — the CONTEXT app, not the raw route app. The tabs this caret belongs
    // to are the ones the strip drew, and on the global Home those are the sticky app's. Read from
    // `currentAppKey` the lookup found no app there, so `tab` was undefined and the handler returned:
    // a tab visibly offering a list-views caret that did nothing when clicked. The recents href below
    // would have been built from the same absent key and addressed `/undefined/<tab>/<id>`.
    //
    // This is breakage the sticky context INTRODUCED rather than inherited: before it, no tab was
    // drawn on this route at all, so there was no caret here to be dead.
    const app = currentContextAppKey ? navEntries.find((e) => e.sourceKey === currentContextAppKey) : undefined;
    const tab = (app?.tabs ?? []).find((t) => t.key === detail?.id);
    if (!tab || !detail?.anchor) return;
    (caret as unknown as { views: unknown }).views = caretViews(currentContextAppKey, tab);
    // CAOS-1701 — the menu opens NOW, on the saved views alone, and the recently-viewed section
    // arrives when the kernel answers. Waiting for it would make a menu of saved views as slow as
    // the slowest query behind it, to show a convenience; so recents start empty, which the caret
    // renders as no section at all, and fill in a moment later.
    (caret as unknown as { recents: unknown }).recents = [];
    const caretToken = ++caretOpenToken;
    const recentsObject = tab.object;
    if (recentsObject) {
      void recentsFor(recentsObject, (recordId) => `/${currentContextAppKey}/${tab.key}/${encodeURIComponent(recordId)}`)
        .then((items) => {
          // Only the menu that asked may be filled. Without this, opening one tab's caret and then
          // another's would drop the first tab's records into the second tab's menu whenever the
          // first request finished last — a menu quietly listing another object's records.
          if (caretToken !== caretOpenToken || items.length === 0) return;
          (caret as unknown as { recents: unknown }).recents = items;
        });
    }
    (caret as unknown as { openAt: (a: { left: number; bottom: number; top?: number; right?: number }) => void }).openAt(detail.anchor);
  });
  caret.addEventListener('caret-navigate', (ev) => {
    const href = (ev as CustomEvent<{ href?: string }>).detail?.href;
    if (href) nav(href);
  });
  regionFor('nav_strip').append(caret);
  regionFor('nav_strip').append(strip);

  // 3b) open-records strip (CAOS-346) — the records you have open, under the app navigation. Opening
  //     a record no longer throws away the one you were looking at: each earns an entry with its own
  //     close control, and the strip SURVIVES moving between list views, so several records can be
  //     held open at once. Empty ⇒ the strip renders nothing at all (no empty rail).
  //
  //     CAOS-695 — the Setup pages you have open live here too (Dragon OS parity, UI-565): one entry
  //     per section, keyed by the `id` it announces, that follows you deeper into the section. And the
  //     strip holds the APP you are in — each app's entries step aside while you are elsewhere and are
  //     there as you left them when you come back, which is what the reference's per-app strip does.
  const openRecords: { id: string; label: string; href: string; closeTo?: string }[] = [];
  const recordsStrip = document.createElement('caos-open-records');
  /** The path currently being shown — which open record, if any, reads as the one you are on. */
  let currentPath = typeof location !== 'undefined' ? location.pathname : undefined;
  const appOfPath = (path: string | undefined): string => (path ?? '').split('/').filter(Boolean)[0] ?? '';
  /** An entry is the one you are on at its own address, or anywhere beneath the place it stands for. */
  const isCurrentEntry = (r: { id: string; href: string }): boolean =>
    currentPath !== undefined && (r.href === currentPath || r.id === currentPath || currentPath.startsWith(`${r.id}/`));
  /**
   * CAOS-1820 — WHOSE app context a path stands in. One function, so the nav strip and the
   * open-records strip cannot disagree about it, and three cases in a deliberate order:
   *
   *  1. The path NAMES an app segment ⇒ that segment, whether or not it is an installed app. An
   *     UNKNOWN segment resolves to no entry and no entries, which is the reading `/nosuchapp` has
   *     always had. This is first because the route is always the authority where it has an answer.
   *  2. No segment, and the org DESIGNATES a home app ⇒ that app. `/` is an app route in such an
   *     org, not the global Home; see `atGlobalHome` below for why that is not a choice.
   *  3. No segment and no designation ⇒ the GLOBAL HOME, and the sticky app answers for it. This is
   *     the case this ticket exists for.
   */
  const contextAppKeyForPath = (path: string | undefined): string | undefined =>
    appOfPath(path) || homeApp?.sourceKey || stickyApp?.sourceKey;
  /**
   * Is this the global Home — the one route the app context carries ACROSS rather than belonging to?
   *
   * Asked of the PATH, not of the resolved app key, because `resolveFromPath` deliberately collapses
   * an UNKNOWN app segment to the same "no app" answer the root gives. By the time the key is
   * resolved `/` and `/nosuchapp` are indistinguishable, and they must not behave alike: `/nosuchapp`
   * is not the Home anchor and keeps the "no app's chrome" reading it has always had.
   *
   * AND NOT IN AN ORG THAT DESIGNATES A HOME APP, which is the half a review had to point out. There
   * `/` resolves to that app (CAOS-291), so going there is ENTERING it — CAOS-1651 puts it in those
   * terms: "the designated home app being open IS being at home". Letting the app you came from win
   * at `/` instead would make an in-place navigation to `/` show one app where a FRESH LOAD of the
   * same address shows another, which is precisely the drift CAOS-291 exists to prevent, on the one
   * route every person hits first. The app you left is not discarded either way: CAOS-695 keeps its
   * open entries, they step aside, and they are there as you left them when you go back.
   */
  const atGlobalHome = (): boolean => appOfPath(currentPath) === '' && homeApp === undefined;
  /**
   * WHOSE entries the strip is showing. Before this the comparison on the global Home was against
   * the empty string and nothing matched, so one click on Home emptied the strip.
   *
   * Derived from the PATH ALONE rather than from the app key the last populate resolved, which makes
   * it independent of the order the two strips are painted in. That matters: the navigator paints
   * this one BEFORE it populates the nav strip, so anything read from the populate's own state would
   * be the PREVIOUS route's — and a review found exactly that, as Support's entries appearing for a
   * frame on the way into an org's designated home app.
   */
  const stripAppKey = (): string => contextAppKeyForPath(currentPath) ?? '';
  const visibleOpenRecords = (): typeof openRecords => openRecords.filter((r) => appOfPath(r.id) === stripAppKey());
  const paintOpenRecords = (): void => {
    (recordsStrip as unknown as { records: unknown }).records = visibleOpenRecords().map((r) => ({
      id: r.id,
      label: r.label,
      href: r.href,
      active: isCurrentEntry(r),
    }));
  };
  // A record announces itself once it is genuinely open (read, allowed, found). Re-opening one that
  // is already in the strip refreshes its name (and, for a section, where in it you are) rather than
  // adding it twice.
  const takeOpened = (detail: Partial<OpenRecordAnnouncement> | undefined): boolean => {
    if (!detail?.href || !detail.label) return false;
    const id = detail.id ?? detail.href;
    const existing = openRecords.find((r) => r.id === id);
    if (existing) {
      existing.label = detail.label;
      existing.href = detail.href;
    } else {
      openRecords.push({ id, label: detail.label, href: detail.href, ...(detail.closeTo ? { closeTo: detail.closeTo } : {}) });
    }
    currentPath = detail.href;
    return true;
  };
  const onRecordOpened = (ev: Event): void => {
    if (!takeOpened((ev as CustomEvent<Partial<OpenRecordAnnouncement>>).detail)) return;
    paintOpenRecords();
    // CAOS-695 — whether the app's landing tab reads as current depends on whether an open entry does.
    //
    // CAOS-1820 — A REVIEW ASKED WHETHER THIS GUARD SHOULD GO, now that the strip can be showing an
    // app's tabs on a route that names none. It should not, and dropping it is a regression rather
    // than a tidy-up: `populateStrip` is driven by the ROUTE, and an announcement is not a route
    // change. `takeOpened` above has just moved `currentPath` onto the announced record, so a
    // populate driven from here would re-derive the context from a path the navigator has not been
    // told about and take the app's tabs back off the Home strip.
    //
    // Nothing is left unrefreshed by keeping it. What this call exists to recompute is which tab
    // reads as current when an open entry stands for the route instead, and on the global Home no
    // tab is in force for it to take that from. The records strip, which is what the announcement
    // actually changes, is repainted on the line above unconditionally.
    if (currentAppKey) populateStrip(currentAppKey, currentTabKey);
  };
  // CAOS-695 — the page a full load opens on is resolved before this frame exists, so its
  // announcement found nobody listening. Take it up now, but only while it is still where you are.
  const pendingOpen = lastOpenRecordAnnouncement();
  if (pendingOpen && typeof location !== 'undefined' && pendingOpen.href === location.pathname) takeOpened(pendingOpen);
  if (typeof document !== 'undefined') {
    // Only the newest frame listens. A re-render builds a fresh frame; leaving the previous one
    // subscribed would keep an unmounted strip alive and double-handle every opened record.
    if (activeRecordOpenListener) document.removeEventListener(OPEN_RECORD_EVENT, activeRecordOpenListener);
    activeRecordOpenListener = onRecordOpened;
    document.addEventListener(OPEN_RECORD_EVENT, onRecordOpened);
  }
  recordsStrip.addEventListener('record-open', (ev) => {
    const href = (ev as CustomEvent<{ href?: string }>).detail?.href;
    if (href) nav(href);
  });
  // Closing the record you are LOOKING AT has to send you somewhere — back to the list it came from,
  // which is where the reference lands you, or wherever the entry said it closes to (a Setup page
  // closes to Setup Home, CAOS-695). Closing any other one just drops it from the strip.
  recordsStrip.addEventListener('record-close', (ev) => {
    const detail = (ev as CustomEvent<{ id?: string; href?: string }>).detail;
    const id = detail?.id ?? detail?.href;
    if (!id) return;
    const i = openRecords.findIndex((r) => r.id === id);
    const closed = i >= 0 ? openRecords.splice(i, 1)[0] : undefined;
    const wasCurrent = closed ? isCurrentEntry(closed) : detail?.href === currentPath;
    paintOpenRecords();
    if (wasCurrent) {
      if (closed?.closeTo) {
        nav(closed.closeTo);
        return;
      }
      const segments = (closed?.href ?? id).split('/').filter(Boolean);
      nav(segments.length >= 2 ? `/${segments[0]}/${segments[1]}` : '/');
    }
  });
  paintOpenRecords();
  regionFor('record_tabs_strip').append(recordsStrip);

  // 4) record-tabs-strip + 5) content region. The main content node PERSISTS; navigation swaps only
  //    its children (and the record-tabs strip), leaving the topbar + strip + edge orbs mounted.
  const main = document.createElement('main');
  main.className = 'caos-frame__content';

  /** The record-tabs strip is present only for a record content surface; tracked so nav can drop it. */
  let recordTabsEl: HTMLElement | null = null;
  const dropRecordTabs = (): void => {
    recordTabsEl?.remove();
    recordTabsEl = null;
  };
  const mountRecordTabs = (record: ShellRecordContent): void => {
    const tabStrip = document.createElement('caos-tab-strip');
    tabStrip.setAttribute('aria-label', 'Record');
    const labels = record.tabs && record.tabs.length > 0 ? record.tabs : ['Details'];
    (tabStrip as unknown as { items: unknown }).items = labels.map((l) => ({
      value: l.toLowerCase().replace(/\s+/g, '-'),
      label: l,
    }));
    tabStrip.setAttribute('value', labels[0]!.toLowerCase().replace(/\s+/g, '-'));
    recordTabsEl = tabStrip as unknown as HTMLElement;
    // CAOS-713 — placed into the `record_tabs_strip` region the blueprint declares (fail-safe to
    // the primary region if a customized blueprint drops it), not hardcoded above the content.
    regionFor('record_tabs_strip').append(recordTabsEl);
  };

  /**
   * Resolve the active app + tab from a URL path (client-router seam). An unknown app segment
   * resolves to the Atlantis AI Home (no app); the ROOT path resolves the org's `defaultApp`
   * designation and falls back to that same Home — mirroring `resolveActiveRoute`, which is the
   * point: this is the in-place navigation twin of the initial paint, and the two must not drift.
   *
   * It answers for the ROUTE only. CAOS-1820 — what CARRIES ACROSS a visit to the root (the app
   * context: its object nav and open records) is not a property of the route and is deliberately not
   * resolved here, because resolving it here would make a fresh load of `/` inherit an app it had
   * never entered and so disagree with `resolveActiveRoute`, which is the drift above.
   */
  const resolveFromPath = (pathname: string): { appKey?: string; tabKey?: string } => {
    const segments = pathname.split('/').filter(Boolean);
    // CAOS-291 — root asks the org where it lands, exactly as the initial paint does. Navigating
    // back to `/` in place must reach the same screen a fresh load of `/` reaches; resolving the
    // designation in one resolver and not the other would put the two one click apart.
    const app = segments[0]
      ? navEntries.find((e) => e.sourceKey === segments[0])
      : resolveOrgHomeApp(plan);
    if (!app) return {}; // root with no designation, or an unknown app ⇒ Home
    const tab = (app.tabs ?? []).find((t) => t.key === segments[1]);
    return { appKey: app.sourceKey, tabKey: tab?.key };
  };

  /**
   * Populate the app-scoped chrome (nav strip data, launcher highlight, gadget items) for a given
   * app + tab. An ABSENT app is the global Home: the strip's Home anchor is focused and no tab is
   * highlighted. For an active app with no explicit tab, its LANDING TAB is highlighted so the app
   * opens on real content, not a menu. Returns the resolved app + effective tab.
   *
   * CAOS-1820 — an absent app does NOT mean an absent app CONTEXT. The Home anchor is a destination,
   * not an app switch, so the object nav and the open-records strip of the last app entered stay on
   * screen there; `stickyApp` holds it and `contextApp` is what this paints the strip from. Only
   * with nothing yet entered — a fresh session — is there no context, and then the strip is the
   * launcher and the anchor alone. What the ABSENT app still decides, unchanged, is which tab reads
   * as current (none), which surface the content region paints (the Home landing, in
   * `paintAppRegion`), and what the browser tab is called (the home's name, not the app's).
   */
  // CAOS-768 — resolved once per frame: which packages hold a pinned slot is a property of what is
  // installed, not of where the person is standing, so an app switch must not be able to change it.
  const pinnedHomeTabs = homeTabEntries(plan);

  const populateStrip = (
    appKey?: string,
    tabKey?: string,
  ): { app?: ShellInjectionRef; effectiveTabKey?: string } => {
    const app = appKey ? navEntries.find((e) => e.sourceKey === appKey) : undefined;
    // CAOS-1820 — entering a real app is what makes it the sticky one. Done before the context is
    // resolved so an app route is its own context, and an unknown app segment leaves the held one
    // alone rather than clearing it.
    if (app) stickyApp = app;
    // The app whose CHROME this paints. Equal to `app` on an app route; on the global Home it is the
    // sticky app, which is what makes the app context survive a visit there. Absent on a fresh
    // session (nothing entered yet) and on an unknown app segment, so both keep today's reading —
    // see `atGlobalHome` for why that second case cannot be told apart from the first by `appKey`.
    const contextApp = appKey ? app : atGlobalHome() ? stickyApp : undefined;
    const contextAppKey = appKey ?? contextApp?.sourceKey;
    const tabs = contextApp?.tabs ?? [];
    // The ACTIVE tab is the route's, not the context's: on the global Home no tab is in force, so
    // the app's nav shows with nothing underlined and the Home anchor is the focused thing.
    const effectiveTabKey = app ? (tabKey ?? landingTabKey(app)) : undefined;
    currentAppKey = appKey;
    currentTabKey = tabKey;
    currentContextAppKey = contextAppKey;
    // CAOS-1660 — `runtime.activeAppLabel` is a resolved VALUE, true only of the route this frame
    // was assembled on, and the top chrome stays mounted across an in-place navigation. It is
    // reached ONLY when the route names an app that is not in `navEntries` — the case it was
    // written for. With no app on the route at all there is no app to label, and falling back to
    // the boot app's name made the browser tab keep naming the app you had left: assembled on
    // one app, navigated to `/` or to an unknown app, the title still read that app's name where
    // a FRESH LOAD of the same route gives the home title. Two answers one click apart, which is
    // what CAOS-291 set out to prevent for the screen and had not been checked for its name.
    //
    // Note the fallback was never load-bearing in this branch even at assembly: `buildShellRuntime`
    // sets `activeAppLabel` from the same lookup as `activeAppKey`, so no app key means no label
    // either. It could only ever produce a value AFTER a navigation, which is the defect itself.
    //
    // CAOS-1820 — so this label is the ROUTE's, and the strip's is the CONTEXT's. They are the same
    // on an app route and differ on exactly one route: the global Home, where the strip must keep
    // naming the app whose nav is still on screen while the title must still read the home's name.
    // Handing one value to both is what would reintroduce the drift the paragraph above describes.
    const appLabel = app
      ? labelOf(app)
      : appKey
        ? (runtime.activeAppLabel ?? humanize(appKey))
        : '';
    const contextAppLabel = contextApp ? labelOf(contextApp) : appLabel;
    // CAOS-507 — every address identifies itself in the browser chrome. The active destination
    // wins, then the app landing, then the product home; the server-resolved environment label is
    // always appended when available, so a production tool tab is unmistakable among many tabs.
    // `effectiveTabKey` is absent on the global Home, and no tab's key is `undefined`, so this finds
    // nothing there and the title falls to the route's own label — the home title, not the sticky
    // app's name. That is the CAOS-1660 invariant: `/` names the home however you reached it.
    const activeDestination = effectiveTabKey === undefined ? undefined : tabs.find((t) => t.key === effectiveTabKey);
    const destinationLabel = activeDestination?.label ?? (appLabel || 'CAOS');
    const title = [destinationLabel, runtime.env]
      .filter((part): part is string => Boolean(part))
      .join(' · ');
    if (typeof document !== 'undefined') document.title = title;
    // CAOS-1651 — the designated home app being open IS being at home, and for an org that
    // designates one this is the state `/` resolves to rather than an edge case. The anchor already
    // carries that app's name, so the separate app-name segment is suppressed: handing both prints
    // the same name twice, which is the defect CAOS-358 reported. The component will not suppress
    // it — a control in `caos-ui/test/caos-358-an-app-names-itself-once.test.ts` pins the duplicate
    // as reachable so its "exactly once" assertion can discriminate — so the decision is the
    // host's, which is also where it belongs: which app is the org's home is the host's knowledge.
    const atDesignatedHome = Boolean(appKey) && appKey === homeApp?.sourceKey;
    // CAOS-1820 — the same suppression, asked of the CONTEXT app, because the context app is the one
    // whose name this segment prints. On the global Home of an org that designates no home app the
    // anchor reads the platform's "Home", so the sticky app's name is NOT a duplicate of it — it is
    // the only thing on the strip saying whose object nav is still showing, and it stays.
    const nameWouldRepeatTheAnchor = contextApp !== undefined && contextApp.sourceKey === homeApp?.sourceKey;
    if (!appKey || atDesignatedHome) {
      // The org's home — the platform's Home page, or the app this org put there.
      strip.setAttribute('home-active', '');
      if (contextApp && !nameWouldRepeatTheAnchor) strip.setAttribute('active-app-label', contextAppLabel);
      else strip.removeAttribute('active-app-label');
    } else {
      strip.removeAttribute('home-active');
      strip.setAttribute('active-app-label', contextAppLabel);
    }
    // CAOS-1820 — the launcher highlights the app whose context is showing, which on the global Home
    // is the sticky one. It is also the control that changes it, so the lockup naming the app you
    // will come back to is the same control you use to leave it.
    if (contextAppKey) launcher.setAttribute('active-app-key', contextAppKey);
    else launcher.removeAttribute('active-app-key');
    // The launcher's list does not change with the route — it is the same set of apps wherever you
    // are. It is rebuilt here only because the surrounding chrome is.
    (launcher as unknown as { apps: unknown }).apps = launcherApps();
    // CAOS-1820 — the popup names the app whose navigation it edits, which is the context app: on
    // the global Home that is the sticky one, and handing it the route's (absent) label would open a
    // control titled after nothing over the tab set it is about to rearrange.
    gadget.setAttribute('app-label', contextAppLabel);
    // CAOS-580 — honour this person's remembered arrangement for THIS app, read fresh from the store
    // on every populate (so it is in force on the first paint after a reload, not only after they
    // touch the popup). What the app publishes stays the authority on which tabs exist.
    const arrangement = contextAppKey ? loadNavArrangement(contextAppKey) : undefined;
    const { visible: visibleTabs, available: hiddenTabs } = applyNavArrangement(tabs, arrangement);
    // Overflow split (CAOS-297/298): the first `overflowAfter` tabs stay pinned in the bar; the rest
    // are flagged `overflow` so the strip collapses them into its "More" menu. Absent/invalid ⇒ no
    // flag on any tab ⇒ the strip renders every tab in the bar exactly as before (additive default).
    const overflowAfter =
      contextApp && typeof contextApp.overflowAfter === 'number' && contextApp.overflowAfter >= 1
        ? contextApp.overflowAfter
        : undefined;
    // A tab whose key is empty IS the app's own landing (Setup's Home) — its address is the app
    // itself, not the app with a trailing slash.
    const tabHref = (key: string): string => (key ? `/${contextAppKey}/${key}` : `/${contextAppKey}`);
    // CAOS-695 — a route below the app that names none of its tabs (a Setup section) is shown by the
    // open entry standing for it, so the landing tab does not ALSO read as current: Home is the base
    // tab, lit on the app's own address. A route no open entry stands for lights the landing tab as
    // it always has.
    const openEntryIsCurrent = tabKey === undefined && visibleOpenRecords().some(isCurrentEntry);
    const items = visibleTabs.map((t, i) => ({
      id: t.key,
      label: t.label,
      // An external destination already has its own address. When it also opts into the general
      // CAOS-507 behavior, open that address directly; opening the CAOS route first would create an
      // intermediate tab whose resolver immediately opened a second one.
      href: t.openInNewTab && t.url ? t.url : tabHref(t.key),
      active: !openEntryIsCurrent && t.key === effectiveTabKey,
      overflow: overflowAfter !== undefined ? i >= overflowAfter : false,
      ...(t.openInNewTab ? { openInNewTab: true } : {}),
      // CAOS-344 — the object the tab holds and the glyph it leads with. The strip uses the object
      // to set the reference's uppercase treatment and the glyph to mark what the tab is for.
      ...(t.object ? { object: t.object } : {}),
      ...(t.icon ? { icon: t.icon } : {}),
      // CAOS-345 — the tab shows its list-views caret only when it has views to offer, so the menu
      // is never opened onto nothing.
      ...((t.listViews?.length ?? 0) > 0 ? { caret: true } : {}),
    }));
    // The gadget (custom-navigation editor) shows the FULL, unsplit list — overflow is a bar-render
    // concern, not a membership change — so hand it items without the overflow flag.
    const gadgetItems = items.map(({ overflow: _overflow, ...rest }) => rest);
    // CAOS-340 — remember where the user is, for the feedback report's Where column.
    const activeTab = items.find((i) => i.active);
    setActiveContextLabel(
      appKey ? (activeTab ? `${appLabel} · ${activeTab.label}` : appLabel) : undefined,
    );
    (strip as unknown as { tabs: unknown }).tabs = items;
    // CAOS-768 — the pinned tabs, recomputed on every populate for the same reason the app's tabs
    // are: WHICH of them reads as current changes with the route even though the SET does not. A tab
    // that named an app but no tab inside it is current whenever that app is open — it stands for
    // the app, not for one page of it.
    (strip as unknown as { homeTabs: unknown }).homeTabs = pinnedHomeTabs.map((t) => ({
      id: t.key,
      label: t.label,
      href: t.href,
      ...(t.icon ? { icon: t.icon } : {}),
      active: appKey === t.app && (t.tab === undefined || effectiveTabKey === t.tab),
    }));
    (gadget as unknown as { items: unknown }).items = gadgetItems;
    // A tab this person hid is offered back under Available Items — otherwise hiding one is a
    // one-way door, and the hidden set from the previous app would follow them into this one.
    (gadget as unknown as { available: unknown }).available = hiddenTabs.map((t) => ({
      id: t.key,
      label: t.label,
      href: tabHref(t.key),
    }));
    // What Reset to Default restores: THIS app's published navigation, refreshed on every populate.
    // The control keeping its own copy is what used to make a reset restore another app's tabs.
    (gadget as unknown as { defaults: unknown }).defaults = tabs.map((t) => ({
      id: t.key,
      label: t.label,
      href: tabHref(t.key),
    }));
    // CAOS-580 — the control is offered only where there is a navigation to customise. On the
    // default app (no app open) and on an app that publishes no tabs, the popup would open onto an
    // empty list, so the control is not shown at all rather than shown and useless.
    const customisable = Boolean(contextAppKey && tabs.length > 0);
    if (customisable) {
      if (gadget.parentNode !== strip) strip.append(gadget);
    } else if (gadget.parentNode) {
      gadget.remove();
    }
    return { app, effectiveTabKey };
  };

  /**
   * CAOS-300 — repaint ONLY the app-scoped regions for a new route: the nav strip data, the launcher
   * highlight, the gadget items, and the derived content surface. The topbar, impersonation banner,
   * and edge orbs are untouched, so they keep DOM identity across navigation. HOME renders the
   * Atlantis AI home; an app renders its landing tab's (or the chosen tab's) real content.
   */
  const paintAppRegion = (appKey?: string, tabKey?: string): void => {
    const { app, effectiveTabKey } = populateStrip(appKey, tabKey);
    dropRecordTabs();
    // CAOS-774 — the content region says WHICH KIND of surface it is holding, so the signature
    // gradient ground can be painted on the home landing and nowhere else. Marking the region
    // rather than the page block is what lets the ground run edge to edge behind the region's own
    // padding instead of reading as a tinted card floating inside it.
    if (!appKey) {
      main.dataset.surface = 'home';
      main.replaceChildren(renderHomeSurface(plan, navEntries, runtime));
    } else if (app) {
      delete main.dataset.surface;
      main.replaceChildren(renderAppSurface(app, effectiveTabKey));
    } else {
      delete main.dataset.surface;
      main.replaceChildren(readyContent(plan));
    }
  };

  // CAOS-713 — the content region lands in whichever region the blueprint names `main` (or, absent
  // one, its primary region) — not appended straight onto the frame root.
  regionFor('main').append(main);

  // Initial paint. An explicit content plan (record/list/control-plane/fallback) wins and still
  // populates the app-scoped chrome; otherwise the surface is derived from the route — the Atlantis
  // AI Home when no app is active, or the active app's landing-tab content (CAOS-267/302).
  const content = runtime.content;
  if (content instanceof Node) {
    populateStrip(runtime.activeAppKey, activeTabKey);
    main.append(content);
  } else if (content) {
    populateStrip(runtime.activeAppKey, activeTabKey);
    if (content.kind === 'record') mountRecordTabs(content);
    main.append(renderContentPlan(content));
  } else {
    paintAppRegion(runtime.activeAppKey, activeTabKey);
  }

  // Register this frame's client-router seam so a production navigate can repaint in place (CAOS-300).
  // Pure/test callers never invoke it; only the host's popstate + navigate wiring drives it.
  setActiveFrameNavigator((route) => {
    if (!frame.isConnected) return;
    // CAOS-345 — the route may carry a query (`?view=…`, which saved list view to open on). The app
    // and tab come from the PATH; the whole route, query intact, goes to the content resolver.
    const pathname = route.split('?')[0]!.split('#')[0]!;
    // CAOS-346 — the open-records strip persists across the move; only which entry reads as current
    // changes. Moving to a list leaves every open record in place, unmarked. CAOS-695 — moving to
    // another app shows that app's entries; the ones left behind are kept, not closed.
    currentPath = pathname;
    paintOpenRecords();
    // CAOS-608/CAOS-1659 — both account-menu entries follow the route. Without these the
    // attribute set when the frame was assembled outlives the page it was true for, and the
    // listener registered beside it keeps that page's href with it. Two entries, one reason:
    // every answer the top chrome holds has to be re-asked, because the chrome is not rebuilt.
    syncSettingsEntry(pathname);
    syncEditPageEntry(pathname);
    const { appKey, tabKey } = resolveFromPath(pathname);
    // Persistent-shell in-place repaint. When the host supplies a content resolver (CAOS-306), keep
    // the top chrome mounted, refresh only the app-scoped nav strip, then swap the content region for
    // the resolved list/record surface once it loads; a resolver miss (app landing) derives the tab
    // cards. Without a resolver (tests/fixtures) the derived landing surface paints synchronously.
    if (runtime.resolveContent) {
      populateStrip(appKey, tabKey);
      dropRecordTabs();
      const requested = pathname;
      void runtime.resolveContent(route).then((node) => {
        // Ignore a stale resolve (the frame unmounted or the route moved on since we asked).
        if (!frame.isConnected || requested !== window.location.pathname) return;
        // CAOS-774 — resolved content is never the home landing, so the home ground goes with the
        // content it belonged to. Without this the flag set on the way IN survives a resolver-driven
        // navigation, and an app page reached FROM home wears the home ground while the same page
        // reached directly does not. `paintAppRegion` owns the flag on every other path.
        if (node) {
          delete main.dataset.surface;
          main.replaceChildren(node);
        } else paintAppRegion(appKey, tabKey);
      });
    } else {
      paintAppRegion(appKey, tabKey);
    }
  });

  /**
   * CAOS-150 — which screen a guide is being opened BESIDE, and who is opening it.
   *
   * Read at the moment a panel opens rather than when the rail is built, because the reader
   * navigates and the rail does not get rebuilt for it.
   *
   * THE SURFACE. `app` and the tab's `object`/`page` come from the route the frame is already
   * tracking for the nav strip, so a topic's scope is matched against the same answer the strip
   * highlights — there is no second idea of "where am I" here to disagree with it. A tab points at
   * an object OR a page, never both (`ShellNavTab.page`), so at most one of those two is set.
   *
   * `placement` IS SUPPLIED ONLY WHERE THE FRAME ACTUALLY KNOWS IT, which is the app's landing (no
   * tab in force) and a tab that opens a page. A tab over an object is a LIST, and the placement
   * vocabulary has no member for a list — it names the record page, not the roster that leads to
   * it — so guessing `recordPage` there would make a topic scoped to a record page appear on a
   * screen with no record on it. Absent is the honest answer, and `topicInScope` is explicit that
   * an absent axis does not satisfy an entry that names one.
   *
   * THE SAME RULE DECIDES THE WORKSPACE HOME, and it is why the first test below is on `app` rather
   * than on the tab. `homePage` is defined in the vocabulary as *an app's* home page
   * (`ComponentPlacementSchema` in `kernel/src/meta/component.ts`), and with no app open there is no
   * app whose home this is — the reader is on the workspace itself. Testing the tab alone could not
   * tell those apart, because "no tab in force" is equally true of an app's landing and of no app at
   * all, so an article scoped to `homePage` and written about an app's cards would also have come up
   * beside a workspace that has none. The vocabulary has no member for the workspace home, so it
   * gets no placement; an article that genuinely belongs on every screen declares no scope instead,
   * which is how a workspace-wide topic reaches it. A tab key that resolves to no tab is
   * unclassified for the same reason and likewise gets none.
   *
   * `page` IS READ TWICE HERE AND THE TWO READINGS DIFFER ON PURPOSE. The `page` AXIS is the key of
   * whatever page is on screen, so it takes all three of `page`, `rosterPage` and `rosterDetailPage`
   * — a topic scoped to a roster's key should match when that roster is open. The PLACEMENT takes
   * only `tab.page`, because the other two are LISTS: `ShellNavTab.rosterPage` is "a list surface
   * that reads its own rows", the same category as a tab over an object, and the rule above already
   * says a list gets no placement. Deriving `appPage` from the union made a roster tab report a
   * standalone screen while an object tab on the same kind of screen reported nothing — the same
   * confusion as the workspace-home case, one axis along. Worth spelling out because collapsing the
   * two reads back into one variable looks like a simplification.
   *
   * The `app` AXIS is likewise the raw `currentAppKey` while the placement gates on the RESOLVED nav
   * entry, and that asymmetry is deliberate: a route naming an app is a fact even when nothing
   * installed answers to it, and the nav strip already labels such a key by humanising it. The
   * placement cannot be that lenient, because it is a claim about a SCREEN, and an app the frame
   * cannot resolve has no tabs to tell a landing from anything else.
   *
   * WHAT IS STILL AN APPROXIMATION HERE, stated so the next reader does not have to find it out from
   * a wrong article. The vocabulary derives a placement from a page's KIND —
   * `PLACEMENT_BY_PAGE_KIND` maps `home` to `homePage` and `setup`/`editor` to `appPage` — but
   * `ShellNavTab` carries only a page KEY, never its kind, so the frame cannot ask that question
   * from here. Two consequences, both narrower than the workspace-home case above and neither fixable
   * without putting the kind on the contract: an app opened with no tab on the route reads as
   * `homePage` even though `populateStrip` then shows its LANDING tab, which may be a list; and a tab
   * that opens a page reads as `appPage` whatever kind the page actually is. Both stay inside one
   * app, so an article scoped to either is at worst on the wrong screen of the right app — unlike
   * `homePage` leaking onto the workspace, which put it in front of readers who had opened no app.
   *
   * THE READER. `role` is the role IN FORCE: the previewed role when somebody is previewing, their
   * first role otherwise, matching how `runtime.user.roles` is already read for the account menu's
   * "Viewing as" line. Previewing therefore changes which articles come with the workspace, which
   * is the behaviour previewing exists for — a guide that ignored it would describe a workspace the
   * previewer is not looking at.
   */
  const guidanceContextNow = (): { surface: GuidanceSurface; reader: GuidanceReader } => {
    const app = currentAppKey ? navEntries.find((e) => e.sourceKey === currentAppKey) : undefined;
    const tab = currentTabKey ? (app?.tabs ?? []).find((t) => t.key === currentTabKey) : undefined;
    const page = tab?.page ?? tab?.rosterPage ?? tab?.rosterDetailPage;
    const placement =
      app === undefined
        ? undefined
        : currentTabKey === undefined
          ? 'homePage'
          : tab?.page !== undefined
            ? 'appPage'
            : undefined;
    const role = runtime.activePreview?.key ?? runtime.user?.roles?.[0]?.key;
    return {
      surface: {
        ...(currentAppKey ? { app: currentAppKey } : {}),
        ...(tab?.object ? { object: tab.object } : {}),
        ...(page ? { page } : {}),
        ...(placement ? { placement } : {}),
      },
      reader: { ...(role ? { role } : {}) },
    };
  };

  // edge-anchored — N distinct anchored app surfaces (the AI assistant, Report-a-Bug, Guidance
  // Center, …) stacked as TABS in the right-hand gutter (CAOS-450), reachable regardless of route,
  // beside page content rather than over it. CAOS-320: an anchor whose source package contributes an
  // engine-rendered INTAKE form opens a REAL working form panel (not a dead button); an anchor
  // without one stays a plain button. Each tab owns its own panel, so multiple coexist.
  const edge = seam(plan, 'edge-anchored');
  if (edge.length > 0) {
    // CAOS-450/AC2 — flags the frame so the content region reserves `--caos-edge-gutter` of extra
    // right padding (shell-frame.css), which is what keeps a tab beside content instead of over it.
    // The RESERVED WIDTH itself is set by `buildEdgeAnchors` (it shrinks once every tab is
    // un-tacked — see `syncGutterReservation`); this flag only turns the reservation on at all.
    frame.dataset.hasEdgeAnchors = 'true';
    frame.append(buildEdgeAnchors(edge, frame, plan.seamOrderOverrides?.['edge-anchored'], nav, guidanceContextNow));
  } else {
    // CAOS-753 — a workspace with nothing anchored owns no openers. Without this, the registry keeps
    // the PREVIOUS frame's map and `openEdgeSource` would reach into a torn-down rail.
    activeEdgeOpeners = new Map();
  }

  // CAOS-564 — the METADATA search overlay, on every page because it is part of the frame rather
  // than a contribution any workspace could omit. It is built hidden and asks the kernel nothing
  // until it is opened, so a workspace nobody opens it on pays for one element.
  //
  // The keystroke binding is detached when the frame is replaced: `assembleShellFrame` is called
  // again on every navigation, and a listener left behind would answer for an overlay that is no
  // longer on the page — two frames on, three listeners would race to open three dead overlays.
  // The PREVIOUS overlay is torn down, not merely unbound. Detaching alone would leave the old
  // frame's overlay in whatever DOM still holds it — visible, and permanently deaf to the shortcut,
  // because the keystroke now reaches only the new instance. `destroy` closes it and removes it, so
  // a frame kept on screen while another is built cannot show a dead search box.
  liveMetadataSearch?.detach();
  liveMetadataSearch?.overlay.destroy();
  const metadataSearch = buildMetadataSearchOverlay({ navigate: nav });
  liveMetadataSearch = { overlay: metadataSearch, detach: bindMetadataSearchKeys(metadataSearch) };
  frame.append(metadataSearch.element);

  return frame;
}

/**
 * The one live overlay and its keystroke binding. A module-level single is what the shortcut needs:
 * there is one workspace frame at a time, and two bindings would race to open two overlays.
 */
let liveMetadataSearch: { overlay: MetadataSearchOverlay; detach: () => void } | null = null;

/**
 * CAOS-753 — how to open each edge-anchored source's own surface, by source key, for the frame that
 * is currently mounted. Replaced wholesale when a frame is built, for the same reason the rail's
 * preference subscription is: a re-render builds fresh tabs, and an opener belonging to a frame that
 * is no longer on screen would open a panel into a detached tree.
 *
 * It exists because hiding a tab (CAOS-751) must not remove ACCESS to what the tab opens, and the
 * fallback path — the account menu — is built in a different function from the rail.
 */
let activeEdgeOpeners = new Map<string, (focusRole?: string) => void>();

/** The newest frame's subscription keeping the account menu's hidden-source list current. */
let activeAvatarVisibilityListener: ((ev: Event) => void) | undefined;

/**
 * Open an edge-anchored source's surface by key, whether or not this person shows its tab.
 *
 * Returns false when the live frame has no such source, so a caller can tell "not installed here"
 * from "opened" rather than silently doing nothing.
 */
export function openEdgeSource(sourceKey: string): boolean {
  const open = activeEdgeOpeners.get(sourceKey);
  if (!open) return false;
  open();
  return true;
}

/**
 * CAOS-752 — the mark each declared anchor KIND carries, from the shared icon vocabulary.
 *
 * Per kind, not per package: a tab's `kind` is what the platform knows about it (it is already what
 * decides which panel the tab opens), and a picture chosen per package would be the host deciding
 * how somebody else's surface looks. `help` keeps the open book CAOS-375 gave it. The other two are
 * the nearest marks the vocabulary holds for "an assistant" and "something you file"; a package
 * declaring its OWN glyph is the better answer and needs a metatype it does not have yet, so it is
 * deliberately a later ticket rather than three pictures guessed here and then frozen.
 */
const EDGE_KIND_GLYPH = { assistant: 'sparkles', report: 'clipboard', help: 'book-open' } as const;

/** The gutter width reserved while at least one edge-anchored tab sits pinned/proud of the edge. */
const EDGE_GUTTER_PINNED = '56px';
/**
 * CAOS-450 (review fix) — the gutter width reserved once EVERY edge-anchored tab has been
 * un-tacked to its sliver. Un-tacking exists so a person working a wide table or a narrow viewport
 * can reclaim that width — if the reservation stayed fixed at the pinned width regardless, the
 * "retract to a sliver" half of AC4 would change how the tab LOOKS without giving back anything a
 * person can actually use, which was the point of the escape hatch CAOS-348's dismiss-to-header
 * used to provide before it retired. 20px safely covers the un-tacked sliver (9px, `shell-frame.css`
 * `.caos-frame__edge-slot--rail[data-pinned='false']`) plus a small margin.
 */
const EDGE_GUTTER_RETRACTED = '20px';
/**
 * CAOS-751 — the width reserved when this person has hidden EVERY tab in the gutter. Not the
 * retracted width: a retracted tab is still there to be clicked and still needs its sliver, whereas
 * a gutter with nothing in it should cost the content region nothing at all.
 */
const EDGE_GUTTER_NONE = '0px';

/**
 * CAOS-450 — the workspace edge renders every anchored surface as a TAB stacked in the right-hand
 * gutter, BESIDE page content rather than over it. This retires the CAOS-299/348 floating
 * bottom-right "orb" stack: a corner circle sat on top of whatever page was open (worst on a
 * phone), which is exactly what CAOS-348's dismiss-to-header escape hatch existed to work around.
 * A gutter tab needs no such escape hatch — it is already out of the way — so that affordance
 * retires with the corner it served; CAOS-327's un-tack/retract/hover-forward interaction (a tab
 * slides to a thin sliver at the edge and comes forward on hover or focus) is what every tab gets
 * instead, uniformly, because every anchor is on the rail now. The width RESERVED for content
 * follows the same distinction (`syncGutterReservation` below): full while any tab is pinned,
 * shrunk to a sliver's worth once every tab is un-tacked, so retracting a tab actually buys back
 * screen width rather than only changing how the tab looks.
 *
 * Each tab opens a real floating panel:
 *   • the assistant tab (Atlantis AI) → an assistant panel (suggested prompts + a composer);
 *   • the report tab (report-a-bug)   → a report composer / the real intake form (CAOS-320);
 *   • the help tab → the recipes its source package roots (CAOS-148), read from installed
 *     `guidanceRecipe` / `guidanceAnchor` metadata the way the report tab reads its intake form;
 *   • a help tab whose package roots no recipe, and any other declared `kind` → a generic,
 *     content-blind placeholder panel (CAOS-756) — the source package supplies its own content
 *     through installed metadata, never through bespoke rendering baked in here. Guidance Center's hand-built, hardcoded-content
 *     help panel (browsable/searchable `platform.*` topics) retired under CAOS-756: that content
 *     was host-baked rather than shipped as installable metadata, exactly the packaging defect the
 *     ticket closed. See `caos-pkg-guidance` for the real, installable metadata replacement
 *     (`guidanceAnchor`, `guidanceRecipe`, `guidanceSection` and `guidanceTopic` — the last two
 *     arrived with CAOS-327 and CAOS-150, and this list named only the first two until then).
 * One panel is open at a time; Esc and an outside click close it.
 *
 * CAOS-734 — which of the three panel behaviours a tab gets is read from its DECLARED
 * `anchor.kind` (`'assistant' | 'report' | 'help'`), never inferred from the surface's sourceKey/
 * label text. A package that predates this field falls back to the old regex heuristic as a
 * one-release compatibility shim (see the comment on `resolvedKind` inside `buildAnchor`).
 *
 * Stacking order is DECLARED, never derived from array/install order: the kernel's
 * CapabilityRegistry already returns injections ordered by `(anchor.order, source_key)`, an org's
 * pinned override applied (CAOS-341/450/732 — see `capabilities/registry.ts`); this function
 * defensively re-sorts on the same key so a caller that hands anchors in another order (a test, a
 * hand-built dev fixture, a stub registry) still renders the deterministic order rather than
 * whatever order the array arrived in. CAOS-732 — `pinnedSourceKeys` carries the SAME override the
 * kernel already applied (`plan.seamOrderOverrides['edge-anchored']`), so this defensive re-sort
 * re-derives the PINNED order, not the unpinned default — a caller that never supplies it (the
 * pre-CAOS-732 dev fixture, a test) simply gets the unpinned deterministic order, unchanged.
 */
function buildEdgeAnchors(
  edge: readonly ShellInjectionRef[],
  frame: HTMLElement,
  pinnedSourceKeys?: readonly string[],
  // CAOS-688 — how the assistant panel's way-through opens the full surface. The frame owns
  // navigation, so the panel asks for it rather than reaching for the router itself; a rail built
  // without one (the deterministic tests) simply has no jump to make.
  nav?: (href: string) => void,
  /**
   * CAOS-150 — the screen being viewed and who is viewing it, for a guide that carries ARTICLES.
   *
   * A FUNCTION rather than a value, because the rail is built once and the reader then navigates:
   * a snapshot taken here would have a guide opened on the third screen still answering for the
   * first. It is called when a panel opens, which is also when the panel is built, so what it
   * returns is current by construction.
   *
   * Absent ⇒ no surface and no reader facts, which resolves to "every unscoped topic, and no topic
   * whose visibility names anything". That is the right answer for the deterministic fixtures and
   * tests that build a rail with no workspace around it.
   */
  guidanceContext?: () => { readonly surface: GuidanceSurface; readonly reader: GuidanceReader },
): DocumentFragment {
  const ordered = orderedAnchors(edge, pinnedSourceKeys);

  const frag = document.createDocumentFragment();
  const railSlot = document.createElement('div');
  railSlot.className = 'caos-frame__edge-rail';

  let openKey: string | null = null;
  let openPanel: HTMLElement | null = null;
  const tabs = new Map<string, HTMLButtonElement>();
  // CAOS-753 — how to open each source's surface, handed to the module registry once the rail is
  // built so the account menu can reach a hidden tab's panel.
  const openers = new Map<string, (focusRole?: string) => void>();
  // CAOS-470 — how to re-read each tab's pinning when the person's preferences change, by source key.
  const pinSyncs = new Map<string, () => void>();
  // CAOS-751 — and how to re-read whether this person wants the tab at all. Separate from pinSyncs
  // because the two answer different questions and a change to one must not repaint the other.
  const showSyncs = new Map<string, () => void>();
  // CAOS-148 — the help tabs that carry installed guidance, in rail order, so a request to open
  // guidance at an anchor role can find the first one whose recipes name it.
  const guidanceOpeners: {
    readonly sourceKey: string;
    readonly guidance: ShellGuidanceDescriptor;
    readonly open: (role?: string) => void;
  }[] = [];

  /**
   * CAOS-450 (review fix) — the reserved gutter is not a fixed cost of HAVING a tab; it is the
   * width the WIDEST currently-visible tab actually needs. Every rail tab shares the same
   * horizontal lane (they stack vertically, not side by side), so that width is `full` the moment
   * ANY tab is pinned (proud of the edge) and only shrinks to `retracted` once EVERY tab has been
   * un-tacked. Un-tacking a tab that sits alongside a still-pinned one buys back nothing — the lane
   * still has to fit the pinned one — which is why this is a whole-rail computation, not a
   * per-tab one. Set as an INLINE custom property on the frame (rather than a CSS-only rule) so it
   * reacts the instant a pin toggles, and so a test can read the actual reserved width directly
   * instead of inferring it from a class name.
   */
  const syncGutterReservation = (): void => {
    // CAOS-751 — a tab this person hid is not in the lane, so it cannot be what the lane has to be
    // wide enough for. Hiding the last visible tab gives the width back entirely rather than
    // leaving the content region indented around an empty rail, and clears the frame's own flag so
    // the reservation is off rather than merely zero.
    const visible = [...railSlot.querySelectorAll<HTMLElement>('.caos-frame__edge-slot--rail')].filter(
      (el) => !el.hidden,
    );
    if (visible.length === 0) {
      delete frame.dataset.hasEdgeAnchors;
      frame.style.setProperty('--caos-edge-gutter', EDGE_GUTTER_NONE);
      return;
    }
    frame.dataset.hasEdgeAnchors = 'true';
    const anyPinned = visible.some((el) => el.dataset.pinned !== 'false');
    frame.style.setProperty('--caos-edge-gutter', anyPinned ? EDGE_GUTTER_PINNED : EDGE_GUTTER_RETRACTED);
  };

  // CAOS-753 — the source whose tab is hidden but is showing anyway, because the person asked for it
  // from the account menu. It is revealed for exactly as long as its panel is open: the panel anchors
  // to the rail and the tab is its close control, so opening one into a gutter reserving no width
  // would leave a panel flush against the edge with nothing beside it to dismiss it. The stored
  // preference is NOT touched — asking to see something once is not changing your mind about it.
  let revealedKey: string | null = null;

  const close = (): void => {
    openPanel?.remove();
    openPanel = null;
    // CAOS-149 — every dismissal funnels through here (the close button, Escape, a click outside,
    // opening another tab), so this is the one place that can promise no highlight outlives the
    // panel that drew it.
    clearGuidanceHighlight();
    if (openKey) tabs.get(openKey)?.setAttribute('aria-expanded', 'false');
    openKey = null;
    if (revealedKey) {
      const key = revealedKey;
      revealedKey = null;
      // Re-entry is already impossible by this line: `syncShown` calls back into `close()` only
      // while `openKey` names the tab being hidden, and `openKey` was cleared above. That — not the
      // order of these two statements — is what terminates it; clearing first only keeps the field
      // from being read half-restored.
      showSyncs.get(key)?.();
    }
  };

  const buildAnchor = (anchor: ShellInjectionRef): void => {
    const label = labelOf(anchor);
    // CAOS-734 — the panel is decided by the DECLARED `anchor.kind`, never by sniffing sourceKey/
    // label text: a future package literally named/labelled "Report Generator" must land on ITS OWN
    // panel, not the report intake form, just because its label contains "report". An injection
    // from BEFORE this field existed (`kind` absent) falls back to the pre-CAOS-734 regex heuristic
    // below — a COMPATIBILITY SHIM for anything not yet updated to declare `kind`, kept rather than
    // defaulting an undeclared tab straight to the assistant panel, which would have silently
    // re-routed any already-shipped report/guidance package that has not picked up the new field.
    const resolvedKind = anchorKind(anchor, label);
    const isReport = resolvedKind === 'report';
    // A `kind: 'help'` tab (e.g. Guidance Center's own anchor) gets the book-open mark — a generic
    // visual treatment for the "help" kind, not anything specific to whichever package declares it.
    const isHelp = resolvedKind === 'help';
    // The tab rides a wrapper so its pin control can sit against it without being part of the
    // button itself (a control inside a button is not reachable, and would toggle the panel).
    const wrap = document.createElement('span');
    wrap.className = 'caos-frame__edge-slot--rail';
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'caos-frame__edge-anchor caos-frame__edge-anchor--rail';
    tab.dataset.sourceKey = anchor.sourceKey;
    // CAOS-756 — the aria-label reads the package's own declared/resolved label, same as every other
    // kind. It used to hardcode "Open the Guidance Center" for every `kind: 'help'` tab regardless of
    // which package declared it — content-specific text baked into a generic seam.
    tab.setAttribute('aria-label', label);
    tab.setAttribute('aria-haspopup', 'dialog');
    tab.setAttribute('aria-expanded', 'false');

    // CAOS-375 — the reference tab is a MARK and a word: an open book beneath the word GUIDE, both
    // reading UP the edge. A bare word was the tab without its own identity, and read the wrong way
    // round besides.
    const word = document.createElement('span');
    word.className = 'caos-frame__edge-word';
    word.textContent = railLabel(anchor, label);
    // CAOS-752 — EVERY tab carries its mark now, not only the `help` one, because compact mode has
    // nothing else left to show once the words are gone: a tab with no glyph would go blank, and
    // "still reachable" would stop being true of it. Which glyph comes from the DECLARED `kind`,
    // which is where the help tab's book already came from — a picture per kind of thing, not per
    // package. Ordinary mode is unchanged: the stylesheet draws the mark only on a `help` tab until
    // the rail is compact, so a workspace that never turns the setting on sees exactly what it did.
    tab.dataset.anchorKind = resolvedKind;
    const mark = document.createElement('span');
    mark.className = 'caos-frame__edge-mark';
    mark.setAttribute('aria-hidden', 'true');
    mark.append(makeIcon(EDGE_KIND_GLYPH[resolvedKind]));
    tab.append(mark, word);
    const openFor = (focusRole?: string): void => {
      close();
      // CAOS-753 — asked for from the account menu while hidden: show the tab for this visit, so the
      // panel has its anchor and its close control back.
      if (wrap.hidden) {
        wrap.hidden = false;
        revealedKey = anchor.sourceKey;
        syncGutterReservation();
      }
      // CAOS-320 — when the report tab's source package contributes an engine-rendered INTAKE form,
      // the panel is the REAL working form (submit POSTs the write route), not the placeholder
      // composer. CAOS-148 — a `help`-kind tab whose source package roots guidance recipes opens
      // them, read from installed metadata. CAOS-756 — a help tab without them (and any other kind
      // with no bespoke renderer) opens the generic, content-blind placeholder: the source package's
      // own content ships as installed metadata, never as bespoke UI baked in here. The assistant
      // tab (and a report tab without an intake descriptor) keep their placeholder panels.
      const panel = isReport
        ? anchor.intake
          ? buildIntakePanel(label, anchor.intake, close)
          : buildReportPanel(label, close)
        : isHelp
          ? // CAOS-150 — a guide of ARTICLES ALONE is a guide. The test was `recipes.length > 0`,
            // which would have sent a package shipping topics and no walkthroughs to the generic
            // placeholder — the one outcome this whole change exists to prevent.
            anchor.guidance && (anchor.guidance.recipes.length > 0 || (anchor.guidance.topics?.length ?? 0) > 0)
            ? buildGuidancePanel(label, anchor.guidance, close, focusRole, anchor.sourceKey, guidanceContext?.())
            : buildGenericAnchorPanel(label, close)
          : buildAssistantPanel(label, close, anchor.assistant, nav);
      openPanel = panel;
      openKey = anchor.sourceKey;
      tab.setAttribute('aria-expanded', 'true');
      railSlot.parentElement?.insertBefore(panel, railSlot);
      panel.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    };
    tab.addEventListener('click', () => {
      if (openKey === anchor.sourceKey) {
        close();
        return;
      }
      openFor();
    });
    wrap.append(tab);
    // CAOS-327/450 — every tab can be UN-TACKED. Pinned (the default) is what ships today: the tab
    // sits proud of the right edge, always readable. Un-tacked slides it almost all the way off,
    // leaving a sliver that slides back out when you reach for it (hover or focus) — so it stays one
    // reach away without sitting on top of a wide table for people who are not currently reading it.
    //
    // The pin control only appears while the tab is hovered or focused: a permanently visible second
    // control on a tab this narrow competes with the tab itself.
    const pin = document.createElement('button');
    pin.type = 'button';
    pin.className = 'caos-frame__edge-pin';
    const syncPin = (): void => {
      const pinned = wrap.dataset.pinned !== 'false';
      pin.setAttribute('aria-pressed', String(pinned));
      // The label states which state you are IN, so hovering confirms it rather than only offering
      // the next action.
      pin.setAttribute('aria-label', pinned ? `${label} is pinned — unpin it` : `${label} is tucked away — pin it`);
      pin.title = pinned ? 'Pinned — click to tuck away' : 'Tucked away — click to pin';
      // CAOS-375 — a thumbtack, and a struck-through one when it is not pushed in. The two states
      // have to be distinguishable at a glance; a plain dot said nothing about which you were in.
      pin.replaceChildren(makeIcon(pinned ? 'pin' : 'pin-off'));
    };
    wrap.dataset.pinned = String(loadRailPinned(anchor.sourceKey));
    pin.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const next = wrap.dataset.pinned === 'false';
      wrap.dataset.pinned = String(next);
      saveRailPinned(anchor.sourceKey, next);
      syncPin();
      syncGutterReservation();
    });
    syncPin();
    // CAOS-470 — a pinning the person chose on another device arrives when their preferences load.
    pinSyncs.set(anchor.sourceKey, () => {
      wrap.dataset.pinned = String(loadRailPinned(anchor.sourceKey));
      syncPin();
      syncGutterReservation();
    });

    // CAOS-751 — whether this person wants the tab at all. `hidden` rather than removing the
    // wrapper, so turning it back on is the same one-line answer and needs no rebuild of a tab that
    // may have a panel's worth of state behind it.
    const syncShown = (): void => {
      const shown = edgeTabShown(anchor.sourceKey);
      wrap.hidden = !shown;
      // A tab cannot be hidden while its own panel stands open over the content: the panel would
      // outlive the only control that closes it.
      if (!shown && openKey === anchor.sourceKey) close();
      syncGutterReservation();
    };
    // Seed the flag only. The whole-rail reservation is computed once after every tab is built and
    // appended — calling it here would measure a rail this wrapper has not joined yet.
    wrap.hidden = !edgeTabShown(anchor.sourceKey);
    showSyncs.set(anchor.sourceKey, syncShown);
    wrap.append(pin);
    tabs.set(anchor.sourceKey, tab);
    openers.set(anchor.sourceKey, openFor);
    // Still `recipes` alone, and deliberately — unlike the panel gate above. These openers answer
    // "open the guide AT THIS ANCHOR ROLE" (CAOS-150 did not change that): a recipe's steps name
    // roles, an article names none, so a topic-only guide has nothing for a role request to find.
    // Widening this to topics would make a control's "show me" open a guide that cannot answer it.
    if (isHelp && anchor.guidance && anchor.guidance.recipes.length > 0) {
      guidanceOpeners.push({ sourceKey: anchor.sourceKey, guidance: anchor.guidance, open: openFor });
    }
    railSlot.append(wrap);
  };

  /**
   * CAOS-752 — marks-only mode, for the whole rail rather than per tab. Every tab shares one
   * vertical lane, so a rail with some tabs worded and some not reads as something half-broken
   * rather than as a choice; compactness is a property of the rail, and hiding one PARTICULAR tab
   * is a different question with its own ticket.
   *
   * A compact tab keeps its accessible name — that never came from the word on it — and gains a
   * title, so the pointer can still name a tab whose word is no longer drawn. The gutter WIDTH is
   * untouched on purpose: the words run up the edge, so taking them away gives back height, and the
   * lane is exactly as wide as it was.
   */
  const syncCompact = (): void => {
    const on = compactRailOn();
    railSlot.dataset.compact = String(on);
    for (const tab of tabs.values()) {
      if (on) tab.title = tab.getAttribute('aria-label') ?? '';
      else tab.removeAttribute('title');
    }
  };

  for (const anchor of ordered) buildAnchor(anchor);
  activeEdgeOpeners = openers;
  if (ordered.length > 0) {
    frag.append(railSlot);
    syncGutterReservation();
    syncCompact();
  }

  // Esc closes; an outside click (not on the panel or a tab) closes.
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape' || !openPanel) return;
    // CAOS-327 — a panel with an expanded read gives the page back first and closes second.
    if (panelAbsorbedEscape(openPanel)) return;
    close();
  });
  document.addEventListener('pointerdown', (ev) => {
    if (!openPanel) return;
    const t = ev.target as Node;
    if (openPanel.contains(t) || railSlot.contains(t)) return;
    close();
  });

  // CAOS-148 — a control that installed guidance points at can open that guidance in place: it
  // dispatches GUIDANCE_OPEN_EVENT naming its anchor ROLE, and the first help tab whose recipes name
  // that role opens at them. The request carries a role, never a package or a recipe key, so a
  // surface asking for help needs to know nothing about which package supplies it. A role no
  // installed recipe names opens nothing.
  const onGuidanceOpen = (ev: Event): void => {
    const role = (ev as CustomEvent<{ role?: unknown } | null>).detail?.role;
    if (typeof role !== 'string' || role.length === 0) return;
    // CAOS-751 — a tab this person hid is not a place help can be opened. Its panel opens AGAINST
    // the rail and the tab is the only control that closes it, so answering here would float a
    // panel with nothing to dismiss it, in a gutter whose reserved width is now zero. Filtered at
    // DISPATCH rather than at registration so toggling visibility takes effect without rebuilding
    // the rail — and so a different help tab that is still shown can answer the same role instead.
    //
    // `openAssistant` is deliberately NOT gated this way: it is a standalone panel reached from its
    // own explicit control elsewhere in the workspace, not from the rail, so hiding the tab must not
    // disable a separate entry point the person just used.
    const target = guidanceOpeners.find(
      (o) => edgeTabShown(o.sourceKey) && o.guidance.recipes.some((r) => recipeNames(r, role)),
    );
    target?.open(role);
  };
  // Only the newest frame listens, as with the open-record subscription: a re-render builds fresh
  // tabs, and a previous frame left subscribed would open its unmounted tab beside the live one.
  // CAOS-470 — ONE subscription for the whole rail, and only the newest frame keeps one.
  activeRailPreferenceListener = replaceDocumentListener(PREFERENCES_CHANGE_EVENT, activeRailPreferenceListener, (ev) => {
    for (const key of (ev as CustomEvent<{ keys?: string[] }>).detail?.keys ?? []) {
      if (key.startsWith(RAIL_PIN_PREFIX)) pinSyncs.get(key.slice(RAIL_PIN_PREFIX.length))?.();
      // CAOS-751 — the same one subscription carries visibility, so a person turning a tab off in
      // My Profile sees the gutter answer immediately, on this device and on any other.
      else if (key.startsWith(RAIL_SHOWN_PREFIX)) showSyncs.get(key.slice(RAIL_SHOWN_PREFIX.length))?.();
      // CAOS-752 — the control for this lives on another surface entirely (My Profile ›
      // Preferences), so the rail learns about the change the same way it learns about a pinning
      // made on another device: from the store, not from whatever pressed it.
      else if (key === COMPACT_RAIL_KEY) syncCompact();
    }
  });
  if (activeGuidanceOpenListener) document.removeEventListener(GUIDANCE_OPEN_EVENT, activeGuidanceOpenListener);
  activeGuidanceOpenListener = onGuidanceOpen;
  document.addEventListener(GUIDANCE_OPEN_EVENT, onGuidanceOpen);

  return frag;
}

/**
 * CAOS-450/AC5, migrated under CAOS-465 — the DECLARED, deterministic stacking order:
 * `anchor.order` (lower first) when a package declares one, tie-broken by `sourceKey` — never by
 * the position an anchor happens to occupy in the array handed in (that position can just as
 * easily be install order). This used to be its own inline sort; it now calls
 * `orderSeamContributions` (`../seam-order.js`) — the same function `setup-surface.ts`'s
 * `contributedSectionGroups` calls for Setup's contributed nav — so the edge gutter no longer
 * carries a bespoke comparator no other seam can reach for (CAOS-465 AC2). The kernel's
 * CapabilityRegistry already returns rows pre-sorted on exactly this key (it runs the mirrored
 * kernel-side copy of this same function); re-sorting here is a defensive second guarantee, not
 * the primary one, so a caller that assembles a plan directly (a test, a hand-built dev fixture, a
 * stub registry) cannot silently regress into install-order stacking.
 *
 * CAOS-732 — `pinnedSourceKeys`, when supplied, is handed straight through as the
 * `orderSeamContributions` `override` argument, so this defensive re-sort reproduces the org's
 * PINNED arrangement instead of discarding it back down to the unpinned default. Absent (no caller
 * supplies it, or the org has no pin for this seam) behaves exactly as before this ticket.
 */
function orderedAnchors(edge: readonly ShellInjectionRef[], pinnedSourceKeys?: readonly string[]): ShellInjectionRef[] {
  const override = pinnedSourceKeys && pinnedSourceKeys.length > 0 ? { sourceKeys: pinnedSourceKeys } : undefined;
  return orderSeamContributions(edge, (r) => ({ sourceKey: r.sourceKey, order: r.anchor?.order }), override);
}

/**
 * CAOS-735 — `header-actions`' own defensive second guarantee, the same one `orderedAnchors`
 * above already gives the edge gutter. The kernel's `CapabilityRegistry.injectionsFor` already
 * returns `header-actions` rows pre-sorted on this exact key (it is a kernel-mastered seam, so
 * `injectionsFor`'s per-bucket loop orders it exactly like every other bucket, `edge-anchored`
 * included — see `kernel/src/capabilities/registry.ts`), and that primary ordering is true in
 * production today. But before this ticket nothing on the host RE-checked it: a caller that
 * assembles a plan directly — a test, a hand-built dev fixture, a future non-DB registry reader —
 * could hand `header-actions` an unsorted array and it would render in whatever order the array
 * arrived in, silently regressing into install-order stacking exactly the way `orderedAnchors`'s
 * own doc comment warns edge-anchored would without this second guarantee. `pinnedSourceKeys`
 * carries `plan.seamOrderOverrides['header-actions']` through unchanged, so this re-sort
 * reproduces the org's pinned arrangement rather than discarding it back to the unpinned default
 * — the SAME threading `orderedAnchors` already does for `edge-anchored`.
 */
function orderedHeaderActions(
  actions: readonly ShellInjectionRef[],
  pinnedSourceKeys?: readonly string[],
): ShellInjectionRef[] {
  const override = pinnedSourceKeys && pinnedSourceKeys.length > 0 ? { sourceKeys: pinnedSourceKeys } : undefined;
  return orderSeamContributions(actions, (r) => ({ sourceKey: r.sourceKey, order: r.anchor?.order }), override);
}

/**
 * The word a right-edge rail tab shows, set along the edge. A surface that runs down the edge needs
 * a SHORT word, and only its author knows which one, so the anchor carries it; failing that the
 * surface's own first word stands in rather than the whole multi-word label running down the page.
 */
function railLabel(anchor: ShellInjectionRef, label: string): string {
  const word = anchor.anchor?.label ?? label.trim().split(/\s+/)[0] ?? label;
  return word.toUpperCase();
}

/** The shared panel shell: a header (kicker + close) over a body; the caller fills the body. */
function buildEdgePanelShell(kicker: string, close: () => void): { panel: HTMLElement; body: HTMLElement } {
  const panel = document.createElement('div');
  panel.className = 'caos-frame__edge-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', kicker);

  const head = document.createElement('div');
  head.className = 'caos-frame__edge-panel-head';
  const kick = document.createElement('span');
  kick.className = 'caos-frame__edge-panel-kicker';
  kick.textContent = kicker;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'caos-frame__edge-panel-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', close);
  head.append(kick, closeBtn);

  const body = document.createElement('div');
  body.className = 'caos-frame__edge-panel-body';
  panel.append(head, body);
  return { panel, body };
}

/**
 * CAOS-756 — the generic, content-blind panel for an edge-anchored tab whose declared `anchor.kind`
 * carries no bespoke renderer of its own (today: `'help'`). Guidance Center's hand-built help panel
 * — a hardcoded, browsable/searchable `platform.*` topic browser — retired under CAOS-756: it was
 * host-baked content, exactly the packaging defect the ticket closed. This is what such a tab gets
 * when its package ships no recipes (a help tab that roots some opens {@link buildGuidancePanel}
 * instead, CAOS-148): the SAME shared edge-panel chrome every other tab uses (so Esc / outside-click /
 * the close button all work identically), with an honest note that the source package supplies no
 * bespoke rendering yet — never any content specific to whichever package declared the tab. A
 * package that wants richer content ships it as installed metadata (see `caos-pkg-guidance`'s
 * `guidanceAnchor`, `guidanceRecipe`, `guidanceSection` and `guidanceTopic` members); this seam
 * stays content-blind either way.
 */
function buildGenericAnchorPanel(label: string, close: () => void): HTMLElement {
  const { panel, body } = buildEdgePanelShell(label, close);
  panel.classList.add('caos-frame__edge-panel--generic');
  const note = document.createElement('p');
  note.className = 'caos-frame__edge-panel-lead';
  note.setAttribute('data-autofocus', '');
  note.tabIndex = -1;
  note.textContent = `${label} has no dedicated panel yet — its content ships with the package that installs it.`;
  body.append(note);
  return panel;
}

/**
 * CAOS-148 — the event a control dispatches to open installed guidance at its anchor ROLE:
 * `new CustomEvent(GUIDANCE_OPEN_EVENT, { bubbles: true, composed: true, detail: { role } })`.
 */
export const GUIDANCE_OPEN_EVENT = 'caos-guidance-open';

/**
 * CAOS-327 — how a panel says it has a step of its own to give back before it closes.
 *
 * The reference guide stages Escape: from the expanded read it returns the page first and closes
 * second, so one key does not throw away two decisions at once. Only a panel that HAS an expanded
 * state registers here; Escape falls through to closing for every other panel exactly as before,
 * which is what keeps this from changing how the assistant or the report panel behave.
 */
const panelEscapeSteps = new WeakMap<HTMLElement, () => boolean>();

/** Give the panel a chance to absorb Escape. True when it did, so the caller must not also close. */
function panelAbsorbedEscape(panel: HTMLElement): boolean {
  return panelEscapeSteps.get(panel)?.() ?? false;
}

/** CAOS-148 — whether a recipe has a step pointing at `role`. */
function recipeNames(recipe: ShellGuidanceRecipe, role: string): boolean {
  return recipe.steps.some((s) => s.anchor === role);
}

/**
 * CAOS-148 — whether an anchor's live element is on the screen being viewed. A selector that does not
 * parse counts as not on screen: anchors are authored by other packages, and a bad selector there must
 * not take the help panel down with it.
 */
function anchorOnScreen(anchor: ShellGuidanceAnchor | undefined): boolean {
  if (!anchor?.selector) return false;
  try {
    return document.querySelector(anchor.selector) !== null;
  } catch {
    return false;
  }
}

/**
 * CAOS-149 — the attribute the walkthrough runner puts on the real element a step points at. It is
 * an attribute on the TARGET rather than a floating card positioned over it, because a card has to
 * track a box that moves whenever the page scrolls or reflows, and that tracking is the part that
 * goes wrong first. The element wears its own highlight, so it cannot drift away from it.
 */
export const GUIDANCE_TARGET_ATTR = 'data-caos-guidance-target';

/** CAOS-149 — take the highlight off whatever is wearing it. Safe to call when nothing is. */
function clearGuidanceHighlight(): void {
  for (const el of document.querySelectorAll(`[${GUIDANCE_TARGET_ATTR}]`)) el.removeAttribute(GUIDANCE_TARGET_ATTR);
}

/**
 * CAOS-149 — what a running step can show, and the rule that decides it.
 *
 *   'none'        the step names no anchor (an `explain`, or a `click` carrying only prose). It
 *                 never had a target, so nothing is highlighted and nothing is missing.
 *   'shown'       the step names an installed anchor whose live element is on this screen.
 *   'described'   the step names an installed anchor whose element is NOT on this screen — for any
 *                 reason: the anchor carries no selector, the selector does not parse, or the
 *                 element is simply not here.
 *   'unknown'     no installed anchor serves the role at all.
 *
 * ── The rule: the runner discloses nothing the list does not ────────────────────────────────────
 * dev-docs `pages-components.md` ("Instance addressing") requires that an address which does not
 * resolve and one that resolves but is not rendered be "reported to the user IDENTICALLY, with
 * nothing highlighted and no explanation of what is missing, because distinguishing the two on
 * screen would answer a question about a field the user was not shown".
 *
 * What carries that risk here is the SECOND hop. `projectGuidance` is a pure projection of installed
 * metadata with no permission input, so which anchors exist is an org-level fact, identical for
 * every reader in the org and already stated by the list one click away. Whether an anchor's element
 * is on a given screen is not: a control the reader may not use is a control that is not rendered
 * for them. So the three ways the second hop can fail are ONE state that says nothing about which —
 * a reader who could tell "this org installed no such control" from "it is installed and not here
 * for you" would be reading their own permissions off a help panel, one step at a time.
 *
 * The first hop is not collapsed into the second, and that is deliberate rather than an omission. A
 * runner that reported an uninstalled role the same way would still be joinable against the list,
 * which names the control's label and region for every role an anchor serves; collapsing it here
 * would hide the difference in one view while leaving it in the other, which is worse than not
 * hiding it — it looks like a guarantee and is not one. Instead `'described'` and `'unknown'` say
 * exactly what the list says about the same role and nothing further, so running a walkthrough adds
 * no fact to what a reader already had.
 */
type GuidanceTargetState = 'none' | 'shown' | 'described' | 'unknown';

interface GuidanceTarget {
  readonly state: GuidanceTargetState;
  /** The live element, only when `state` is `'shown'`. */
  readonly element?: HTMLElement;
  /** The installed anchor — present for `'shown'` and `'described'`, which read alike in the list. */
  readonly anchor?: ShellGuidanceAnchor;
}

/**
 * CAOS-149 — resolve one step to what the runner can show, in the two hops the shipped model uses:
 * the step names an anchor ROLE, the installed anchor carries the live `selector`. Never throws:
 * anchors are authored by other packages, and a selector that does not parse must cost this step
 * its highlight, not the run.
 */
function resolveGuidanceTarget(
  step: ShellGuidanceStep,
  anchors: ReadonlyMap<string, ShellGuidanceAnchor>,
): GuidanceTarget {
  if (step.anchor === undefined) return { state: 'none' };
  const anchor = anchors.get(step.anchor);
  if (!anchor) return { state: 'unknown' };
  // Every remaining branch returns 'described' with the same anchor, so no caller can tell a missing
  // selector from an unparseable one from an element that is not here.
  if (!anchor.selector) return { state: 'described', anchor };
  let element: Element | null = null;
  try {
    element = document.querySelector(anchor.selector);
  } catch {
    return { state: 'described', anchor };
  }
  if (!(element instanceof HTMLElement)) return { state: 'described', anchor };
  return { state: 'shown', element, anchor };
}

/**
 * CAOS-149 — the walkthrough runner: a recipe's ordered steps, one at a time, with the real element
 * on screen highlighted for each step that has one.
 *
 * The recipe IS the walkthrough. `guidance-center.md` ("Interactive walkthroughs") puts it plainly —
 * "because anchors can carry a live `selector`, guidance can move from describing to doing" — and the
 * `guidanceAnchor` metatype says the same of the field itself. So nothing new is addressed here: the
 * steps are the ones the panel already lists, resolved live instead of described.
 *
 * A step that cannot be shown says so and offers to skip; see {@link GuidanceTargetState} for why
 * the four ways that can happen are one presentation and not four.
 */
function buildWalkthroughRunner(
  recipe: ShellGuidanceRecipe,
  anchors: ReadonlyMap<string, ShellGuidanceAnchor>,
  exit: () => void,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'caos-frame__walkthrough';
  root.dataset.recipeKey = recipe.key;

  const progress = document.createElement('p');
  progress.className = 'caos-frame__walkthrough-progress';

  const title = document.createElement('h3');
  title.className = 'caos-frame__guidance-title';
  title.textContent = recipe.label ?? recipe.intent;

  const prose = document.createElement('p');
  prose.className = 'caos-frame__guidance-step-text';
  // Focus follows the step, so a keyboard or screen-reader user hears each one as it arrives rather
  // than having to go looking for what changed.
  prose.tabIndex = -1;
  prose.setAttribute('data-autofocus', '');

  const where = document.createElement('span');
  where.className = 'caos-frame__guidance-where';

  const controls = document.createElement('div');
  controls.className = 'caos-frame__walkthrough-controls';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'caos-frame__walkthrough-back';
  back.dataset.walkthrough = 'back';
  back.textContent = 'Back';
  const advance = document.createElement('button');
  advance.type = 'button';
  advance.className = 'caos-frame__walkthrough-advance';
  advance.dataset.walkthrough = 'advance';
  const leave = document.createElement('button');
  leave.type = 'button';
  leave.className = 'caos-frame__walkthrough-leave';
  leave.dataset.walkthrough = 'leave';
  leave.textContent = 'All guides';
  controls.append(leave, back, advance);

  root.append(progress, title, prose, where, controls);

  // The schema requires at least one step (`GuidanceRecipeSchema`, `steps: z.array(...).min(1)`),
  // so this is a guard against that changing or against a member reaching the host without having
  // been through it — not a case the shipped packages produce. An empty walk draws nothing rather
  // than reading past the end of the array.
  if (recipe.steps.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'caos-frame__guidance-step-text';
    empty.tabIndex = -1;
    empty.setAttribute('data-autofocus', '');
    empty.textContent = 'This guide has no steps to walk.';
    root.replaceChildren(empty, controls);
    back.disabled = true;
    advance.textContent = 'Done';
    advance.addEventListener('click', exit);
    leave.addEventListener('click', exit);
    return root;
  }

  let index = 0;

  const draw = (): void => {
    // The previous step's highlight goes before the next one is chosen, so a step that cannot be
    // shown leaves the page with nothing highlighted rather than with the last thing that could be.
    clearGuidanceHighlight();
    const step = recipe.steps[index]!;
    const target = resolveGuidanceTarget(step, anchors);

    root.dataset.stepIndex = String(index);
    root.dataset.stepCount = String(recipe.steps.length);
    root.dataset.target = target.state;
    progress.textContent = `Step ${index + 1} of ${recipe.steps.length}`;
    // A step may carry no prose (the schema allows a bare `wait`); its action is then all there is.
    // CAOS-1916 — a step being WALKED names its controls inline the same way the listed one does.
    if (step.text === undefined) prose.textContent = step.action;
    else renderGuidanceProse(prose, step.text, anchors);

    where.replaceChildren();
    delete where.dataset.role;
    delete where.dataset.missing;
    if (target.state === 'none') {
      where.hidden = true;
    } else {
      // 'shown' and 'described' render the SAME line from the SAME anchor — the only difference
      // between them is whether anything on the page is outlined, which the reader can see for
      // themselves and which needs no explaining. 'unknown' is the list's own sentence for a role
      // no installed anchor serves. Nothing here states more than the list already does.
      where.hidden = false;
      renderAnchorWhere(where, target.anchor);
    }
    if (target.state === 'shown') {
      target.element!.setAttribute(GUIDANCE_TARGET_ATTR, '');
      // Not every environment implements it, and a step must not fail to draw over a scroll.
      try {
        target.element!.scrollIntoView?.({ block: 'center', inline: 'nearest' });
      } catch {
        /* the highlight stands whether or not the view moved */
      }
    }

    back.disabled = index === 0;
    const last = index === recipe.steps.length - 1;
    // There is nothing after the last step to skip to, so a skip control there would be one that
    // does what Done does while claiming otherwise.
    advance.textContent = last ? 'Done' : target.state === 'shown' || target.state === 'none' ? 'Next' : 'Skip this step';
    prose.focus();
  };

  back.addEventListener('click', () => {
    if (index > 0) {
      index -= 1;
      draw();
    }
  });
  advance.addEventListener('click', () => {
    if (index < recipe.steps.length - 1) {
      index += 1;
      draw();
      return;
    }
    exit();
  });
  leave.addEventListener('click', exit);

  draw();
  return root;
}

/**
 * CAOS-148 — the help panel for a `help`-kind tab whose source package roots guidance recipes.
 *
 * It lists those recipes, each with its intent and its ordered steps. A step naming an anchor ROLE is
 * realized against the installed anchor when the panel opens: the label, icon and layout region the
 * org actually shows (docs-site guidance-center.md, "Separate the intent from its realization"), so a
 * recipe never has to change when the interface does. A step whose role no installed anchor serves
 * keeps its prose and says the control is not available here, instead of failing the panel.
 *
 * Most-local first: recipes with a step whose anchor is on the screen being viewed come before the
 * rest. Opened for a role ({@link GUIDANCE_OPEN_EVENT}), the recipes naming that role come before
 * both, and the first of them is marked as the one asked for. Within each group the package's rooted
 * order stands.
 *
 * This stays content-blind toward whichever package declared the tab (CAOS-756): every word it shows
 * comes from installed metadata, except the one line a missing anchor gets.
 */
function buildGuidancePanel(
  label: string,
  guidance: ShellGuidanceDescriptor,
  close: () => void,
  focusRole?: string,
  sourceKey?: string,
  /**
   * CAOS-150 — the screen this guide was opened beside and who opened it, for resolving ARTICLES.
   * Absent ⇒ no surface and no reader facts: every unscoped topic is read, and no topic whose
   * visibility names anything is. See `guidance-articles.ts` for why that is the safe default.
   */
  context?: { readonly surface: GuidanceSurface; readonly reader: GuidanceReader },
): HTMLElement {
  const { panel, body } = buildEdgePanelShell(label, close);
  panel.classList.add('caos-frame__edge-panel--guidance');

  /**
   * CAOS-327 — the EXPANDED read, and the page dimmed behind it.
   *
   * Expanded, the guide covers most of the window, and a page that carried on looking live behind
   * it would be saying two things are readable when one of them is not. The dim says which is
   * being read, and clicking it steps back out — so nobody has to hunt for the control that got
   * them there.
   *
   * The choice is remembered against the PERSON rather than the browser
   * (`person-preferences.ts`), which is the one place this deliberately goes further than the
   * reference: somebody who reads the guide expanded had to expand it again on every page, and
   * doing that per-browser would make them do it again on their second machine.
   */
  const expandKey = sourceKey === undefined ? undefined : `rail.expanded.${sourceKey}`;
  let expanded = expandKey !== undefined && preference(expandKey) === true;

  // A CHILD of the panel on purpose: it is removed when the panel is, and the frame's
  // outside-click handler already treats anything the panel contains as inside, so dimming the
  // page cannot be mistaken for a click that dismisses it.
  const scrim = document.createElement('div');
  scrim.className = 'caos-frame__edge-panel-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  scrim.addEventListener('pointerdown', (ev) => {
    ev.stopPropagation();
    setExpanded(false);
  });
  panel.append(scrim);

  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'caos-frame__edge-panel-expand';
  panel.querySelector('.caos-frame__edge-panel-head')?.prepend(expandBtn);

  function setExpanded(next: boolean): void {
    expanded = next;
    panel.dataset.expanded = String(expanded);
    scrim.hidden = !expanded;
    expandBtn.setAttribute('aria-pressed', String(expanded));
    expandBtn.setAttribute('aria-label', expanded ? 'Shrink the guide' : 'Expand the guide');
    expandBtn.textContent = expanded ? '\u2198' : '\u2197';
    if (expandKey !== undefined) void setPreference(expandKey, expanded);
  }
  expandBtn.addEventListener('click', () => setExpanded(!expanded));
  // Set before the preference write below would matter, so the panel is never drawn mid-state.
  panel.dataset.expanded = String(expanded);
  scrim.hidden = !expanded;
  expandBtn.setAttribute('aria-pressed', String(expanded));
  expandBtn.setAttribute('aria-label', expanded ? 'Shrink the guide' : 'Expand the guide');
  expandBtn.textContent = expanded ? '\u2198' : '\u2197';

  // Escape returns the page first and closes second. Registered only by this panel, so every other
  // edge panel keeps closing on the first press.
  panelEscapeSteps.set(panel, () => {
    if (!expanded) return false;
    setExpanded(false);
    return true;
  });
  const anchors = new Map(guidance.anchors.map((a) => [a.role, a] as const));

  // CAOS-149 — the body shows one of two things: the list of recipes, or one recipe being walked.
  // Leaving a run puts the SAME three nodes back rather than building new ones, so the search box
  // and whatever was typed in it survive the walk: coming back from a run to a guide that had
  // silently forgotten the query would read as the search having been cleared for you.
  const showList = (): void => {
    clearGuidanceHighlight();
    // CAOS-1917 — the standing line goes back with the rest of the list, and is simply absent from
    // this list when the package authored none. Spread rather than appended conditionally so there
    // is ONE statement of what the list view is made of: the bug waiting in the other shape is a
    // footer that is restored in a different order than it was first built in, which reads as the
    // panel rearranging itself on the way back from a walkthrough.
    body.replaceChildren(...[search, empty, list, ...(standing ? [standing] : [])]);
    draw();
  };
  const showRun = (recipe: ShellGuidanceRecipe): void => {
    body.replaceChildren(buildWalkthroughRunner(recipe, anchors, showList));
  };

  const onScreen = (r: ShellGuidanceRecipe): boolean =>
    r.steps.some((s) => s.anchor !== undefined && anchorOnScreen(anchors.get(s.anchor)));
  const ranked = guidance.recipes.map((recipe) => ({
    recipe,
    here: onScreen(recipe),
    asked: focusRole !== undefined && recipeNames(recipe, focusRole),
  }));
  // `sort` is stable, so recipes of equal rank keep the package's rooted order.
  const rankOf = (r: { here: boolean; asked: boolean }): number => (r.asked ? 0 : r.here ? 1 : 2);
  ranked.sort((a, b) => rankOf(a) - rankOf(b));

  /**
   * CAOS-327 — the guide is read by AREA, which is how the reference organises it and what
   * `guidanceSection` now lets a package say. Sections arrive already in the authoring package's
   * reading order; anything filed under no section is gathered at the end under no heading, because
   * a recipe nobody filed is still a recipe somebody wrote.
   *
   * Ranking still decides the order WITHIN a section, and a recipe asked for by role still pulls
   * its own section to the front {@link GUIDANCE_OPEN_EVENT} — opening the guide at a control and
   * finding its section collapsed three headings down would be a worse answer than no grouping.
   */
  /**
   * CAOS-150 — the ARTICLES, resolved twice because the two readings differ on purpose.
   *
   * `beside` is what is read next to this screen: in scope, permitted, the org's own first, and
   * with any platform article the org has stood in front of taken out. It is computed ONCE, here,
   * rather than inside `draw()` — the surface cannot change while a panel is open, and recomputing
   * it on every keystroke would be work that cannot produce a different answer.
   *
   * The SEARCH reading is computed per query inside `draw()`, because it has to ignore scope and
   * superseding, which is the whole of this ticket's second criterion. Both go through
   * `guidance-articles.ts` so the two rules stay in one place.
   */
  const allTopics = guidance.topics ?? [];
  const beside = topicsForSurface(allTopics, context?.surface ?? {}, context?.reader ?? {});

  const bySection = new Map<string, typeof ranked>();
  for (const entry of ranked) {
    const key = entry.recipe.section ?? '';
    const bucket = bySection.get(key);
    if (bucket) bucket.push(entry);
    else bySection.set(key, [entry]);
  }
  /**
   * CAOS-150 — a section holding only ARTICLES is a section.
   *
   * Grouping used to be derived from the recipes alone, which was complete while recipes were the
   * only thing a guide held. With topics it would have silently dropped every heading nothing was
   * filed under BY A RECIPE — so a guide of articles under three headings would have drawn as one
   * unheaded list, and the articles under the second and third headings would not have drawn at
   * all, because the loop below iterates groups.
   *
   * Built from EVERY topic rather than only the ones in scope: a section must survive into the
   * group list for a search to be able to put a hit in it, and a search reaches past scope.
   */
  const topicSectionKeys = new Set(allTopics.map((t) => t.section ?? ''));
  const groups = guidance.sections
    .filter((section) => bySection.has(section.key) || topicSectionKeys.has(section.key))
    .map((section) => ({ section, entries: bySection.get(section.key) ?? [] }));
  const unfiled = bySection.get('');
  if (unfiled || topicSectionKeys.has('')) {
    groups.push({ section: undefined as unknown as ShellGuidanceSection, entries: unfiled ?? [] });
  }
  const askedGroup = groups.findIndex((g) => g.entries.some((e) => e.asked));
  if (askedGroup > 0) groups.unshift(...groups.splice(askedGroup, 1));

  /**
   * Everything a search reads: the recipe's own words and its steps' prose.
   *
   * CAOS-1916 — a step's prose is read through `guidanceProseWords`, which takes any `{anchor:…}`
   * token out and closes the gap. Without that, annotating a step would make the phrase spanning the
   * glyph unfindable, so the better a step was written the less of it could be searched.
   */
  const haystack = (r: ShellGuidanceRecipe): string =>
    [r.label ?? '', r.intent, ...r.steps.map((st) => guidanceProseWords(st.text ?? ''))]
      .join(' ')
      .toLowerCase();


  // ── the search box ────────────────────────────────────────────────────────
  // CAOS-327 — searching the guide is a behaviour of this GENERIC renderer over metadata it
  // already holds, not content baked into the shell: every word it matches came from an installed
  // package.
  const search = document.createElement('div');
  search.className = 'caos-frame__guidance-search';
  const field = document.createElement('input');
  field.type = 'search';
  field.className = 'caos-frame__guidance-search-input';
  field.setAttribute('aria-label', `Search ${label}`);
  field.placeholder = 'Search the guide';
  const clear = document.createElement('button');
  clear.type = 'button';
  clear.className = 'caos-frame__guidance-search-clear';
  clear.textContent = '\u00d7';
  clear.setAttribute('aria-label', 'Clear the search');
  // Hidden until there is something to clear. The 2026-08-19 build shipped this control marked
  // hidden while still drawing, so an empty box offered to clear nothing — `hidden` is the
  // host's one hiding rule (`src/hidden.css`), which is why it is set here rather than a class.
  clear.hidden = true;
  search.append(field, clear);
  body.append(search);

  const empty = document.createElement('p');
  empty.className = 'caos-frame__guidance-empty';
  empty.hidden = true;
  body.append(empty);

  const list = document.createElement('div');
  list.className = 'caos-frame__guidance-list';
  body.append(list);

  /**
   * CAOS-1917 — the standing line: "this guide grows, come back to it", under the section list.
   *
   * BUILT ONLY IF THE PACKAGE AUTHORED ONE. `undefined` here is what makes "absent means absent"
   * structural rather than a styling choice — there is no node, so there is no empty strip to
   * accidentally give padding to, and no `hidden` state to get wrong. Most packages will never
   * declare it.
   *
   * It is a sibling of `list` rather than content inside it, and that placement is doing three jobs
   * at once, each an acceptance criterion:
   *
   *   • IT SURVIVES A SEARCH. `draw()` rebuilds `list` on every keystroke; the footer is outside
   *     `list`, so a query that filters every section away still leaves the line. Put inside, it
   *     would have blinked out exactly when the reader was most likely to want to know the guide is
   *     still growing — which is when they searched for something and found nothing.
   *
   *   • IT IS IN BOTH READS. `setExpanded` toggles state on the PANEL and never rebuilds the body,
   *     so one node serves the docked and the expanded guide. Nothing here is per-read.
   *
   *   • IT IS GONE WHILE A WALKTHROUGH IS WALKED. `showRun` replaces the body with the runner, so
   *     the footer leaves with the rest of the list and comes back when `showList` restores it. A
   *     living-document note under "step 2 of 3" is noise, and the ticket asked for that to be a
   *     decision rather than an accident: it is this one, and it falls out of the existing
   *     replace-the-body mechanism rather than needing a rule of its own.
   */
  const standing = ((): HTMLElement | undefined => {
    if (guidance.standingNote === undefined) return undefined;
    const el = document.createElement('p');
    el.className = 'caos-frame__guidance-standing';
    // The authored sentence, as text. Not `role="note"` or an `aria-live` region: it says the same
    // thing for the whole life of the panel, so announcing it as a change would be a lie about it,
    // and it is already read in document order after the list it belongs to.
    el.textContent = guidance.standingNote;
    return el;
  })();
  if (standing) body.append(standing);

  // Which sections the reader has open. The FIRST group starts open and the rest closed, matching
  // the reference: a guide that opens with every heading expanded is a wall, and one with all of
  // them shut says nothing about what is inside.
  const openSections = new Set<string>();
  const groupKey = (g: { section?: ShellGuidanceSection }): string => g.section?.key ?? '';
  if (groups.length > 0) openSections.add(groupKey(groups[0]!));

  let query = '';

  function draw(): void {
    list.replaceChildren();
    const q = query.trim().toLowerCase();
    const searching = q !== '';
    let shown = 0;
    let autofocused = false;

    // CAOS-150 — the articles for THIS pass. Searching swaps which set is being grouped, not how it
    // is grouped: `topicsMatchingSearch` reaches past scope and past superseding, so a query finds
    // an article about a screen the reader is not on, and finds a platform article the org has
    // replaced beside the screens they share.
    const articles = searching ? topicsMatchingSearch(allTopics, q, context?.reader ?? {}) : beside;

    for (const group of groups) {
      const matches = searching ? group.entries.filter((e) => haystack(e.recipe).includes(q)) : group.entries;
      const topicsHere = articles.filter((t) => (t.section ?? '') === groupKey(group));
      if (matches.length === 0 && topicsHere.length === 0) continue;
      shown += matches.length + topicsHere.length;

      const key = groupKey(group);
      const section = document.createElement('section');
      section.className = 'caos-frame__guidance-section';
      section.dataset.section = key === '' ? '(unfiled)' : key;

      // A search HOLDS its sections open. Making somebody click a heading back open to see what
      // they just searched for is the moment a search stops being a search.
      const open = searching || openSections.has(key);
      section.dataset.open = String(open);

      if (group.section) {
        const heading = document.createElement('button');
        heading.type = 'button';
        heading.className = 'caos-frame__guidance-section-heading';
        heading.setAttribute('aria-expanded', String(open));
        // While searching, the heading is a label rather than a control: there is nothing useful
        // about collapsing a section whose only reason for being on screen is the query.
        heading.disabled = searching;

        // The authored mark is drawn from the one shared glyph vocabulary, exactly as a step's
        // anchor draws its own below: a section naming a mark this library does not carry leads
        // with its title rather than a gap where an icon was promised.
        const icon = document.createElement('span');
        icon.className = 'caos-frame__guidance-section-icon';
        icon.setAttribute('aria-hidden', 'true');
        if (isIconName(group.section.icon)) icon.append(makeIcon(group.section.icon));
        else icon.hidden = true;

        const title = document.createElement('span');
        title.className = 'caos-frame__guidance-section-title';
        title.textContent = group.section.title;
        heading.append(icon, title);
        heading.addEventListener('click', () => {
          if (openSections.has(key)) openSections.delete(key);
          else openSections.add(key);
          draw();
        });
        section.append(heading);

        if (group.section.blurb) {
          const blurb = document.createElement('p');
          blurb.className = 'caos-frame__guidance-section-blurb';
          blurb.textContent = group.section.blurb;
          section.append(blurb);
        }
      }

      /**
       * CAOS-150 — the ARTICLES, before the walkthroughs in the same section.
       *
       * An article says what a screen is for; a walkthrough steps you through one task on it. The
       * reader who has just opened the guide beside a screen they do not recognise needs the first
       * before the second, and a guide that led with "Show me" for one task would be answering a
       * question they have not got to yet.
       *
       * A `ul`, not the recipes' `ol`: the recipes list is ordered because walking them in order is
       * meaningful, and articles have no such order — only the authored reading order within a
       * section, which the sort has already applied.
       */
      if (topicsHere.length > 0) {
        const articleList = document.createElement('ul');
        articleList.className = 'caos-frame__guidance-articles';
        articleList.hidden = !open;
        for (const topic of topicsHere) {
          const item = document.createElement('li');
          item.className = 'caos-frame__guidance-article';
          item.dataset.topicKey = topic.key;
          // Which article this is, readable from the outside: whose it is, and whether it is
          // standing in front of one that shipped with the platform. A reader comparing the two
          // after an org has replaced an article has no other way to tell them apart.
          item.dataset.platform = String(topic.platform);
          if (topic.supersedes !== undefined) item.dataset.supersedes = topic.supersedes;
          if (!autofocused) {
            item.tabIndex = -1;
            item.setAttribute('data-autofocus', '');
            autofocused = true;
          }

          const heading = document.createElement('h3');
          heading.className = 'caos-frame__guidance-title';
          heading.textContent = topic.title;
          const prose = document.createElement('p');
          prose.className = 'caos-frame__guidance-article-body';
          // An article is PROSE and not a way to put markup into the frame: a topic is installed
          // metadata and a package is not the host's author. CAOS-1916 keeps that — the one thing an
          // article may now carry is `{anchor:<role>}`, which resolves to an installed anchor's own
          // icon and can express nothing else. See `renderGuidanceProse`.
          renderGuidanceProse(prose, topic.body, anchors);
          item.append(heading, prose);
          articleList.append(item);
        }
        section.append(articleList);
      }

      const items = document.createElement('ol');
      items.className = 'caos-frame__guidance-recipes';
      items.hidden = !open;
      matches.forEach(({ recipe, here, asked }) => {
        const item = document.createElement('li');
        item.className = 'caos-frame__guidance-recipe';
        item.dataset.recipeKey = recipe.key;
        item.dataset.onScreen = String(here);
        if (!autofocused) {
          // The panel focuses the recipe it leads with, so a keyboard or screen-reader user starts
          // there. Under a search that is the first HIT, which is the thing they just asked for.
          item.tabIndex = -1;
          item.setAttribute('data-autofocus', '');
          if (asked) item.dataset.requested = 'true';
          autofocused = true;
        }

        const title = document.createElement('h3');
        title.className = 'caos-frame__guidance-title';
        title.textContent = recipe.label ?? recipe.intent;
        item.append(title);
        if (recipe.label) {
          const intent = document.createElement('p');
          intent.className = 'caos-frame__edge-panel-lead';
          intent.textContent = recipe.intent;
          item.append(intent);
        }

        const steps = document.createElement('ol');
        steps.className = 'caos-frame__guidance-steps';
        for (const step of recipe.steps) steps.append(buildGuidanceStep(step, anchors));
        item.append(steps);

        // CAOS-149 — every recipe is runnable, including one whose steps are all prose. The
        // alternative is a second class of recipe that looks the same and behaves differently, and
        // the reader would have to work out which kind they are looking at before knowing whether
        // to expect the control.
        const run = document.createElement('button');
        run.type = 'button';
        run.className = 'caos-frame__guidance-run';
        run.dataset.runRecipe = recipe.key;
        run.textContent = 'Show me';
        run.addEventListener('click', () => showRun(recipe));
        item.append(run);

        items.append(item);
      });
      // CAOS-150 — only when it holds something. A section of articles alone would otherwise append
      // an empty recipe list, which reads to a test and to a screen reader as a walkthrough list
      // that is there and empty rather than one that was never part of this section.
      if (matches.length > 0) section.append(items);
      list.append(section);
    }

    // A search that found nothing says so. Drawing an empty guide would read as a guide with
    // nothing in it, which is a different and untrue statement.
    empty.hidden = !(searching && shown === 0);
    if (!empty.hidden) empty.textContent = `Nothing in this guide matches “${query.trim()}”.`;
    clear.hidden = query === '';
  }

  field.addEventListener('input', () => {
    query = field.value;
    draw();
  });
  clear.addEventListener('click', () => {
    field.value = '';
    query = '';
    draw();
    field.focus();
  });

  draw();
  return panel;
}

/**
 * CAOS-148 / CAOS-149 — where a control lives in this org, drawn into `where`. ONE renderer, used by
 * the list and by the running walkthrough, so the two can never phrase the same fact two ways: a
 * runner that said more than the list would let a reader learn the difference by flipping between
 * them, which is the leak {@link GuidanceTargetState} exists to close.
 */
function renderAnchorWhere(where: HTMLElement, anchor: ShellGuidanceAnchor | undefined): void {
  if (anchor) {
    where.dataset.role = anchor.role;
    if (isIconName(anchor.icon)) where.append(makeIcon(anchor.icon));
    const name = document.createElement('span');
    name.textContent = `${anchor.label} · ${anchor.region.replace(/[-_]+/g, ' ')}`;
    where.append(name);
    return;
  }
  where.dataset.missing = 'true';
  where.textContent = 'This control is not available in this workspace.';
}

/**
 * CAOS-1916 — draw authored guidance prose, with any control it names drawn where it is named.
 *
 * Every authored string in this panel used to be set with `textContent`, and the reason is recorded
 * where the article body is built: an article is prose, not a way to put markup into the frame. That
 * still holds. What goes in here is not markup — `{anchor:<role>}` resolves to one icon belonging to
 * one installed {@link ShellGuidanceAnchor}, and nothing else can be drawn by writing it. The host
 * remains the only author of the frame; a package only gets to point at a control it registered.
 *
 * Three outcomes for a token, and each is a decision rather than a fallthrough:
 *
 *   • the role resolves and its icon is known  →  the anchor's own glyph, labelled for a screen
 *     reader with the anchor's label, so the org's word for the control is what is read out.
 *   • the role resolves and its icon is NOT a known glyph  →  the anchor's LABEL as text. The
 *     sentence still names the control in the org's own words, which is the part that cannot be
 *     allowed to go missing; only the picture is lost.
 *   • the role resolves to no installed anchor  →  the literal `{anchor:…}` the author typed.
 *
 * That last one is deliberately the reference's behaviour (an unknown key renders literal) and NOT
 * the sentence a whole step gets when its anchor is missing ("This control is not available in this
 * workspace."). A step is ABOUT its control, so when that control is absent the step needs telling.
 * A token is one word inside a paragraph that still reads correctly without it, and replacing it
 * mid-sentence with a clause would damage prose that is otherwise fine. Showing the raw token also
 * makes an author's typo visible to the author, which is the only way it gets fixed — silently
 * dropping it would leave a guide that looks right and says less than it was written to say.
 */
function renderGuidanceProse(
  target: HTMLElement,
  text: string,
  anchors: ReadonlyMap<string, ShellGuidanceAnchor>,
): void {
  const parts = splitGuidanceProse(text);
  // Prose carrying no token takes the path it always took: one text node, set the way every other
  // authored string in this panel is set. Stated precisely because review caught the loose version
  // of this claim — it is "no token", not "nothing authored before this existed". Prose that happens
  // to contain a `{anchor:…}`-shaped substring nobody meant as a reference takes the branch below
  // instead, and arrives as several text nodes rather than one. The WORDS are identical either way
  // (an unresolved token is re-emitted verbatim); only the node count differs, and nothing reads it.
  if (parts.length <= 1 && (parts[0] === undefined || parts[0].kind === 'text')) {
    target.textContent = text;
    return;
  }
  target.replaceChildren();
  for (const part of parts) {
    if (part.kind === 'text') {
      target.append(document.createTextNode(part.text));
      continue;
    }
    const anchor = anchors.get(part.role);
    if (anchor === undefined) {
      target.append(document.createTextNode(part.token));
      continue;
    }
    // One wrapper either way, so the drawn shape of a named control is the same thing to a
    // stylesheet and to a test whether or not its glyph could be found.
    const control = document.createElement('span');
    control.className = 'caos-frame__guidance-control';
    control.dataset.role = anchor.role;
    if (isIconName(anchor.icon)) {
      // Labelled rather than hidden. The glyph often duplicates a word the author already wrote, so
      // reading both is a little redundant; but the token is sometimes the only thing naming the
      // control, and a redundant reading loses nothing where a hidden one can lose the subject of
      // the sentence.
      control.setAttribute('role', 'img');
      control.setAttribute('aria-label', anchor.label);
      control.append(makeIcon(anchor.icon));
    } else {
      // No `role="img"` and no label attribute here: the words ARE the label, and announcing it
      // twice is worse than announcing it once.
      control.dataset.glyph = 'missing';
      control.textContent = anchor.label;
    }
    target.append(control);
  }
}

/** CAOS-148 — one step: its prose, and where the control it points at lives in this org. */
function buildGuidanceStep(step: ShellGuidanceStep, anchors: ReadonlyMap<string, ShellGuidanceAnchor>): HTMLElement {
  const li = document.createElement('li');
  li.className = 'caos-frame__guidance-step';
  li.dataset.action = step.action;

  const text = document.createElement('span');
  text.className = 'caos-frame__guidance-step-text';
  // A step may carry no prose (the schema allows a bare `wait`); its action is then all there is to say.
  // CAOS-1916 — authored prose may name a control inline; a bare action never does, so it is set flat.
  if (step.text === undefined) text.textContent = step.action;
  else renderGuidanceProse(text, step.text, anchors);
  li.append(text);

  if (step.anchor) {
    const where = document.createElement('span');
    where.className = 'caos-frame__guidance-where';
    renderAnchorWhere(where, anchors.get(step.anchor));
    li.append(where);
  }
  return li;
}

/**
 * CAOS-320 — the report orb's panel when its source package contributes an engine-rendered intake
 * form. Reuses the shared edge-panel chrome (header + close, so Esc / outside-click / the close
 * button all work through {@link buildEdgeAnchors}) and renders the REAL working form inside the
 * body: submit POSTs the descriptor's write route. Replaces the placeholder {@link buildReportPanel}.
 */
function buildIntakePanel(label: string, intake: ShellIntakeDescriptor, close: () => void): HTMLElement {
  const { panel, body } = buildEdgePanelShell(label, close);
  const { root } = renderIntakeContent(
    { kind: 'intake', ...intake },
    {
      // The panel head already says "Report a Bug"; the form must not say it a second time.
      titled: false,
      // Going to look at your own submissions closes the panel first — otherwise it stays floating
      // over the very page it just sent you to.
      navigate: (href) => {
        close();
        navigateFromFrame(href);
      },
    },
  );
  body.append(root);
  return panel;
}

/** The Atlantis AI assistant panel: suggested prompts + a composer that echoes into an honest thread. */
/**
 * CAOS-688 — what the assistant panel falls back to when its package declares nothing.
 *
 * These two are the platform's, not any package's: the gateway route the shell has always posted to,
 * and the three openers that were written beside the composer before a package could name its own.
 * They are kept, rather than removed, because an installed package that predates the declaration
 * must keep exactly the panel it had — but they apply ONLY in that case. A package that declares an
 * assistant panel and names no openers gets none, because it said so.
 */
const ASSISTANT_FALLBACK_CHAT_ROUTE = '/api/ai/chat';
const ASSISTANT_FALLBACK_PROMPTS: readonly string[] = [
  'Summarize my open work',
  'What changed recently?',
  'Draft a status update',
];

/** CAOS-688 — where the way-through opens, by the same rule every other contributed entry follows. */
function assistantJumpHref(jump: { readonly app: string; readonly tab?: string }): string {
  return jump.tab ? `/${jump.app}/${jump.tab}` : `/${jump.app}`;
}

/**
 * CAOS-688 — what a WORKSPACE PATH is, for a navigation target that arrived as live data.
 *
 * MIRRORED, not invented: this is the rule `kernel/src/meta/page.ts` already applies to an intake
 * action's `to` (a single leading slash, never protocol-relative, no scheme), and
 * `host/test/caos-688-assistant-panel.test.ts` reads that file to hold the two in agreement — the
 * same structural pairing `caos-768-home-tabs.test.ts` keeps for the slot count. `@caos/host`
 * deploys as its own dependency-free Worker and never imports kernel code, so agreement is the only
 * mechanism available; two places deriving this rule for themselves would drift the moment either
 * changed. Exported for exactly that check and read by nothing else.
 *
 * WHY IT IS CHECKED HERE and not only in the schema. The two routes a package DECLARES are patterned
 * at deploy. The rows that come back from the recent read are not: they are whatever that package's
 * backend returns at runtime, and a row's `href` became a navigation target. `clientNavigate` hands
 * anything whose origin differs from the workspace's — including a `javascript:` URI, which parses
 * with an opaque origin — to `window.location.assign`. So an open redirect out of the authenticated
 * shell, and script execution in it, were both one malformed row away, from a backend the platform
 * does not control. A row's own href is now used only when it is a workspace path; anything else is
 * treated as though the row carried none, which falls back to the declared jump — a host-built
 * address — or leaves the row inert.
 *
 * WHAT THIS DOES NOT CHECK. Dot segments: `/../../x` passes. A path-absolute reference never gets
 * its authority reparsed, so no accepted string can change the origin, which is the whole of what
 * this guard is for. But it does not normalise the path either, so read it as "cannot leave the
 * workspace origin" rather than "is a path this workspace has" — the client router still decides
 * what an unexpected path draws.
 */
export const WORKSPACE_PATH = /^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?#]*$/;

/**
 * CAOS-688 — fill the peek from the package's own read route.
 *
 * Three outcomes, all of them said in words, because a panel that goes quiet is the one thing this
 * region must never do: the reader cannot tell an empty list from a broken read, and the difference
 * is whether they have work waiting. A row opens its thread in the full surface, which is the same
 * hand-over the way-through makes — so a row uses the declared jump destination when there is one
 * and its own href when the read supplies one.
 */
async function fillAssistantRecent(
  into: HTMLElement,
  route: string,
  nav: (href: string) => void,
  jump?: { readonly app: string; readonly tab?: string },
): Promise<void> {
  const say = (text: string, state: 'empty' | 'unreadable'): void => {
    const el = document.createElement('p');
    el.className = 'caos-frame__edge-recent-note';
    el.dataset.state = state;
    el.textContent = text;
    into.replaceChildren(el);
  };
  let rows: { label: string; sub?: string; href?: string }[] = [];
  try {
    const res = await controlPlaneFetch(route);
    const payload = (await res.json()) as { ok?: boolean; data?: unknown };
    if (payload?.ok !== true) {
      say('Recent threads could not be read just now.', 'unreadable');
      return;
    }
    const list = Array.isArray(payload.data)
      ? payload.data
      : Array.isArray((payload.data as { rows?: unknown })?.rows)
        ? ((payload.data as { rows: unknown[] }).rows)
        : [];
    rows = list
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r) => ({
        label: typeof r['label'] === 'string' ? r['label'] : typeof r['name'] === 'string' ? r['name'] : '',
        ...(typeof r['sub'] === 'string' ? { sub: r['sub'] } : {}),
        // A row's address is live data from the package's own backend, so it is admitted only when it
        // is a workspace path. Anything else is dropped HERE rather than filtered at the click, so
        // there is one place to read and no second path that could forget.
        ...(typeof r['href'] === 'string' && WORKSPACE_PATH.test(r['href']) ? { href: r['href'] } : {}),
      }))
      .filter((r) => r.label !== '');
  } catch {
    say('Recent threads could not be read just now.', 'unreadable');
    return;
  }
  if (rows.length === 0) {
    say('Nothing recent yet.', 'empty');
    return;
  }
  const frag = document.createDocumentFragment();
  for (const row of rows) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'caos-frame__edge-recent-row';
    const name = document.createElement('span');
    name.className = 'caos-frame__edge-recent-label';
    name.textContent = row.label;
    item.append(name);
    if (row.sub) {
      const sub = document.createElement('span');
      sub.className = 'caos-frame__edge-recent-sub';
      sub.textContent = row.sub;
      item.append(sub);
    }
    const href = row.href ?? (jump ? assistantJumpHref(jump) : undefined);
    if (href) item.addEventListener('click', () => nav(href));
    else item.disabled = true;
    frag.append(item);
  }
  into.replaceChildren(frag);
}

function buildAssistantPanel(
  label: string,
  close: () => void,
  descriptor?: ShellAssistantDescriptor,
  nav?: (href: string) => void,
): HTMLElement {
  const { panel, body } = buildEdgePanelShell(
    `${label} · ${descriptor?.subtitle ?? 'Always on'}`,
    close,
  );
  // CAOS-688 — a panel built from its package's own declaration says so, which is what the tests
  // distinguish and what tells a reader whether the peek's absence is a choice or a failure.
  if (descriptor) panel.dataset.assistantSource = 'package';

  const sub = document.createElement('div');
  sub.className = 'caos-frame__edge-panel-sublabel';
  sub.textContent = 'Suggested';
  const suggestions = document.createElement('div');
  suggestions.className = 'caos-frame__edge-suggestions';

  const thread = document.createElement('div');
  thread.className = 'caos-frame__edge-thread';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'caos-frame__edge-input';
  input.placeholder = 'Ask anything about your workspace…';
  input.setAttribute('aria-label', 'Message the assistant');
  input.setAttribute('data-autofocus', '');

  // CAOS-688 — the openers are the PACKAGE's words. Three of them used to be written in here, which
  // is content that cannot ship, version or be turned off with the package that owns it — the same
  // defect CAOS-756 closed for the guidance panel. A package that names none offers none, and the
  // "Suggested" heading goes with them rather than standing over an empty row.
  const prompts = descriptor ? descriptor.prompts ?? [] : ASSISTANT_FALLBACK_PROMPTS;
  for (const prompt of prompts) {
    const s = document.createElement('button');
    s.type = 'button';
    s.className = 'caos-frame__edge-suggestion';
    s.textContent = prompt;
    s.addEventListener('click', () => {
      input.value = prompt;
      input.focus();
    });
    suggestions.append(s);
  }
  // The word "Suggested" over an empty row is the shell claiming the package offered something it
  // did not, so the heading is only APPENDED when there is something under it. Removing it after
  // appending is what the first attempt did, and appending it again below undid that silently.
  if (prompts.length > 0) body.append(sub);
  body.append(suggestions, thread);

  // CAOS-688 — THE PEEK. The most recently active threads, a handful of them, each one opening in
  // the full surface. This is the half that makes the panel worth opening instead of going to the
  // app: the reader sees where they were without leaving where they are.
  if (descriptor?.recent && nav) {
    const heading = document.createElement('div');
    // Two classes: the shared one so it is styled like every other sublabel, and its own so a reader
    // of this panel (and a test) can name THIS heading rather than whichever comes first.
    heading.className = 'caos-frame__edge-panel-sublabel caos-frame__edge-recent-heading';
    heading.textContent = descriptor.recent.label;
    const recent = document.createElement('div');
    recent.className = 'caos-frame__edge-recent';
    // The peek LEADS. Where the reader was is what they came for; the openers are what to do if it
    // turns out they were nowhere. Empty until the read answers, and an empty region and a failed
    // read are then said in different words rather than both left blank.
    body.insertBefore(heading, body.firstChild);
    heading.after(recent);
    void fillAssistantRecent(recent, descriptor.recent.route, nav, descriptor.jump);
  }

  const composer = document.createElement('form');
  composer.className = 'caos-frame__edge-composer';
  const send = document.createElement('button');
  send.type = 'submit';
  send.className = 'caos-frame__edge-send';
  send.textContent = 'Send';
  composer.append(input, send);
  panel.append(composer);

  // CAOS-688 — THE WAY THROUGH. A peek surface with no way out of it is a dead end, so a package
  // that declares one gets a control that closes the panel and opens the full surface. The panel
  // closes first: leaving it pinned over the surface it just sent the reader to is the duplication
  // this whole shape exists to avoid.
  if (descriptor?.jump && nav) {
    const jump = document.createElement('button');
    jump.type = 'button';
    jump.className = 'caos-frame__edge-jump';
    jump.dataset.jumpApp = descriptor.jump.app;
    jump.textContent = descriptor.jump.label;
    const href = assistantJumpHref(descriptor.jump);
    jump.addEventListener('click', () => {
      close();
      nav(href);
    });
    panel.append(jump);
  }

  const msgEl = (from: 'user' | 'system', text: string): HTMLElement => {
    const el = document.createElement('div');
    el.className = 'caos-frame__edge-msg';
    el.dataset.from = from;
    el.textContent = text;
    return el;
  };

  composer.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    suggestions.replaceChildren();
    sub.remove();
    const mine = msgEl('user', text);
    // A pending assistant bubble while the kernel makes the real gateway call (CAOS-299 A orb).
    const reply = msgEl('system', 'Thinking…');
    reply.dataset.pending = '';
    thread.append(mine, reply);
    input.value = '';
    send.disabled = true;
    thread.scrollIntoView({ block: 'end' });
    void (async () => {
      let answer = 'The assistant is unavailable right now. Please try again in a moment.';
      try {
        const res = await controlPlaneFetch(descriptor?.chat.route ?? ASSISTANT_FALLBACK_CHAT_ROUTE, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ message: text }),
        });
        const payload = (await res.json()) as {
          ok?: boolean;
          data?: { reply?: unknown };
          error?: { message?: unknown };
        };
        if (payload?.ok === true && typeof payload.data?.reply === 'string' && payload.data.reply.trim()) {
          answer = payload.data.reply;
        } else if (
          // CAOS-299 — when the AI gateway isn't configured yet, say so honestly rather than faking a
          // reply or pretending a transient outage. The kernel surfaces this as a "not configured" error.
          typeof payload?.error?.message === 'string' &&
          /configur|gateway/i.test(payload.error.message)
        ) {
          answer =
            'The assistant isn’t connected yet — an administrator needs to configure the AI service before it can answer.';
        }
      } catch {
        // keep the graceful fallback answer
      }
      reply.textContent = answer;
      reply.removeAttribute('data-pending');
      send.disabled = false;
      thread.scrollIntoView({ block: 'end' });
    })();
  });

  return panel;
}

/**
 * CAOS-325 — open the assistant from somewhere OTHER than a corner orb.
 *
 * The corner belongs to the bug reporter now, so the AI is reached from its own app: the app
 * overview's "Ask" button calls this. It is the same panel the orb used to open — the one that talks
 * to the real gateway and says so plainly when the gateway is not configured — mounted on its own
 * rather than stacked above a column of orbs.
 *
 * This is the interim home for the conversation. CAOS-355 replaces it with the full surface
 * (channels, threads, a message stream), at which point Ask becomes a navigation instead.
 */
let standaloneAssistant: HTMLElement | null = null;

let assistantDismissers: (() => void) | null = null;

/** Close the standalone assistant, if one is open. */
export function closeAssistant(): void {
  standaloneAssistant?.remove();
  standaloneAssistant = null;
  assistantDismissers?.();
  assistantDismissers = null;
}

/** Open the assistant panel over the workspace. Opening it twice re-focuses the one already open. */
export function openAssistant(label = 'Atlantis AI'): void {
  if (standaloneAssistant) {
    standaloneAssistant.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return;
  }
  const panel = buildAssistantPanel(label, closeAssistant);
  panel.classList.add('caos-frame__edge-panel--standalone');
  document.body.append(panel);
  standaloneAssistant = panel;
  panel.querySelector<HTMLElement>('[data-autofocus]')?.focus();

  // Esc closes it, and so does a click anywhere outside it — the same two ways every other floating
  // panel in the workspace closes, so it does not need to be learned separately. The outside-click
  // listener is armed one turn LATER on purpose: the very click that opened the panel is still
  // bubbling, and arming it now would close the panel in the same gesture that asked for it.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') closeAssistant();
  };
  const onClick = (ev: MouseEvent): void => {
    const target = ev.target as Node | null;
    if (target && !panel.contains(target)) closeAssistant();
  };
  document.addEventListener('keydown', onKey);
  const arm = setTimeout(() => document.addEventListener('click', onClick), 0);
  assistantDismissers = () => {
    clearTimeout(arm);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onClick);
  };
}

/**
 * What one of the host-baked edge composers asks for, and what it files it as.
 *
 * CAOS-50 — there are two of these now. They were one until a person who needed HELP turned out to
 * have nowhere to go: every route into this intake was for telling us something is wrong, the kinds
 * are bug / confusing / idea, and the panel asks "What went wrong?". A question is the same
 * submission with a different word on it, so it is the same composer with different copy rather
 * than a second pipeline that would have to answer "where did my question go" on its own.
 */
interface SubmissionPanelCopy {
  /** The value stored in `caos.feedback.kind`. */
  readonly kind: 'bug' | 'question';
  readonly lead: string;
  readonly fieldLabel: string;
  readonly placeholder: string;
  readonly ariaLabel: string;
  /** What the confirmation calls the thing they sent, in their words. */
  readonly noun: string;
}

const REPORT_COPY: SubmissionPanelCopy = {
  kind: 'bug',
  lead: 'Spotted something off? Tell us what happened and it goes to the team.',
  fieldLabel: 'What went wrong?',
  placeholder: 'Describe what you were doing and what happened…',
  ariaLabel: 'Describe the problem',
  noun: 'report',
};

const HELP_COPY: SubmissionPanelCopy = {
  kind: 'question',
  lead: 'Tell us what you are trying to do and we will come back to you. The screen you are on is included automatically.',
  fieldLabel: 'What do you need help with?',
  placeholder: 'Describe what you are trying to do and where you are stuck…',
  ariaLabel: 'Describe what you need help with',
  noun: 'question',
};

/** A short lead + a description composer that files one submission against the given copy. */
function buildSubmissionPanel(label: string, copy: SubmissionPanelCopy, close: () => void): HTMLElement {
  const { panel, body } = buildEdgePanelShell(label, close);

  const lead = document.createElement('p');
  lead.className = 'caos-frame__edge-panel-lead';
  lead.textContent = copy.lead;

  const field = document.createElement('label');
  field.className = 'caos-frame__edge-panel-sublabel';
  field.textContent = copy.fieldLabel;
  const textarea = document.createElement('textarea');
  textarea.className = 'caos-frame__edge-textarea';
  textarea.placeholder = copy.placeholder;
  textarea.setAttribute('aria-label', copy.ariaLabel);
  textarea.setAttribute('data-autofocus', '');
  body.append(lead, field, textarea);

  // The composer lives INSIDE the scrolling body so submitting can swap the whole body
  // (lead + field + textarea + composer) for the confirmation in one safe operation.
  const composer = document.createElement('form');
  composer.className = 'caos-frame__edge-composer';
  const send = document.createElement('button');
  send.type = 'submit';
  send.className = 'caos-frame__edge-send';
  send.textContent = 'Submit';
  send.disabled = true;
  composer.append(send);
  body.append(composer);

  textarea.addEventListener('input', () => {
    send.disabled = textarea.value.trim().length === 0;
  });

  composer.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const text = textarea.value.trim();
    if (!text) return;
    send.disabled = true;
    send.textContent = 'Submitting…';
    // Capture the "where were they" context at submit time (auto-captured, so even a terse note is
    // triageable): the current route + the active surface's eyebrow label. The org and the person
    // are NOT sent — the route takes both from the authenticated session, so they cannot be wrong
    // and cannot be spoofed by whatever is on this page.
    const pageUrl = typeof location !== 'undefined' ? location.pathname + location.search : undefined;
    const appContext = currentContextLabel();
    void (async () => {
      let submitted = false;
      let reference = '';
      try {
        const res = await controlPlaneFetch('/api/feedback/submit', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // `reporterName` is the denormalised display name the triage queue shows, the same one
          // the metadata intake form sends. Identity itself is still the server's: the row's
          // reporter is the authenticated principal, so this name cannot change WHOSE submission
          // it is — it only saves a triager resolving an id to a person.
          //
          // `generationField()` spreads LAST and contributes at most `metadataGeneration`, so it
          // cannot overwrite any field above it. It contributes nothing at all when no plan
          // carrying a generation has been assembled, which is the honest answer rather than a
          // number nobody observed.
          body: JSON.stringify({
            description: text,
            kind: copy.kind,
            pageUrl,
            appContext,
            reporterName: signedInName(),
            ...generationField(),
            // CAOS-1688 — and WHICH BROWSER, so a triager does not have to ask. Spread last for the
            // same reason `generationField()` is: it contributes at most `userAgent` and so cannot
            // overwrite anything above it, and it contributes nothing at all rather than an empty
            // string when there is no navigator to read.
            ...userAgentField(),
          }),
        });
        const payload = (await res.json()) as { ok?: boolean; data?: { feedback?: { id?: unknown } } };
        submitted = payload?.ok === true;
        if (submitted) reference = submissionRef(payload?.data?.feedback?.id);
      } catch {
        submitted = false;
      }

      body.replaceChildren();

      const done = document.createElement('p');
      done.className = 'caos-frame__edge-panel-lead';
      if (!submitted) {
        done.textContent = `We couldn’t send your ${copy.noun} just now. Please try again in a moment.`;
        body.append(done);
        return;
      }

      // CAOS-50 — confirm receipt to the person who sent it, which means two corrections.
      //
      // This used to point at Setup → Submitted Feedback. That is the administrators' triage queue,
      // and a reporter who is not an administrator cannot open it: the confirmation sent most people
      // to a door they have no key to. My Feedback is the reporter's own page and exists for exactly
      // this (CAOS-326).
      //
      // And it threw away the id the submit route returns, so there was nothing to refer to when
      // asking about it later — while `feedbackRef` sat in the list surface formatting the very
      // reference the form was supposed to have handed over.
      done.textContent = reference
        ? `Thanks — your ${copy.noun} was received. Its reference is ${reference} if you need to follow it up.`
        : `Thanks — your ${copy.noun} was received.`;
      body.append(done);

      const mine = document.createElement('button');
      mine.type = 'button';
      // Wears the send button's styling (it is the one action left in the panel) but carries its own
      // marker, so a selector meant for the composer's Submit cannot pick this up after the swap.
      mine.className = 'caos-frame__edge-send';
      mine.setAttribute('data-followup', 'my-feedback');
      mine.textContent = 'See my submissions';
      mine.addEventListener('click', () => {
        close();
        navigateFromFrame(MY_FEEDBACK_PATH);
      });
      body.append(mine);
    })();
  });

  return panel;
}

/**
 * CAOS-50 — the standalone Get-help panel, opened from the account menu.
 *
 * It lives in the shell rather than on an edge tab because every edge tab belongs to an optional
 * installed package (`pkg_report_a_bug`, `pkg_guidance`, `pkg_atlantis_ai`), and the story asks for
 * a way to reach support "from inside ANY org" — which an org that has installed nothing still is.
 * The account menu is the one always-present surface that already carries a person-shaped entry
 * (My Feedback, CAOS-326), and help belongs to the person for the same reason feedback does.
 */
let standaloneHelp: HTMLElement | null = null;
let helpDismissers: (() => void) | null = null;

/** Close the Get-help panel, if one is open. */
export function closeHelp(): void {
  standaloneHelp?.remove();
  standaloneHelp = null;
  helpDismissers?.();
  helpDismissers = null;
}

/** Open the Get-help panel over the workspace. Opening it twice re-focuses the one already open. */
export function openHelp(label = 'Get help'): void {
  if (standaloneHelp) {
    standaloneHelp.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return;
  }
  const panel = buildHelpPanel(label, closeHelp);
  panel.classList.add('caos-frame__edge-panel--standalone');
  document.body.append(panel);
  standaloneHelp = panel;
  panel.querySelector<HTMLElement>('[data-autofocus]')?.focus();

  // Esc and an outside click close it, the same two ways every other floating panel closes. The
  // outside-click listener is armed one turn LATER for the reason the assistant's is: the click that
  // opened it is still bubbling, and arming now would close it in the gesture that asked for it.
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') closeHelp();
  };
  const onClick = (ev: MouseEvent): void => {
    const target = ev.target as Node | null;
    if (target && !panel.contains(target)) closeHelp();
  };
  document.addEventListener('keydown', onKey);
  const arm = setTimeout(() => document.addEventListener('click', onClick), 0);
  helpDismissers = () => {
    clearTimeout(arm);
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onClick);
  };
}

/** The report-a-bug panel: tell us something is wrong. */
function buildReportPanel(label: string, close: () => void): HTMLElement {
  return buildSubmissionPanel(label, REPORT_COPY, close);
}

/** The get-help panel: ask us something, from wherever you are. */
function buildHelpPanel(label: string, close: () => void): HTMLElement {
  return buildSubmissionPanel(label, HELP_COPY, close);
}

/**
 * CAOS-302 — the content region for the active app when the content resolver has NOT answered: a tab
 * with no object, page or url behind it, an app with no tabs, or a host running without a resolver
 * (tests and fixtures). Never a generated menu of tab-name cards — that fabricated "APP / <name> +
 * cards" page was removed, and an app with no tabs renders a legible empty state instead.
 *
 * CAOS-1649 — WHAT THIS IS NOT, corrected because the comment here used to claim it and the claim
 * was the defect. This does not draw "the landing tab's list view". The object branch below asks
 * {@link loadListView}, which takes an OBJECT KEY and builds its columns from describe — the name
 * field, then every non-system field, capped. It cannot see the tab's `defaultListView` and so
 * honours none of the authored columns, labels, presentation, search, sort or filters.
 *
 * That was invisible for as long as it was reachable by the ordinary way in. A bare app address
 * (`/feedback` — where the Home screen's app card goes) resolved to NO tab, `resolveShellContent`
 * returned undefined, and this filled the silence while the nav strip highlighted the landing tab
 * above it. `parseContentRoute` now resolves the landing tab, so a tab with real content is drawn by
 * the one renderer that reads list-view metadata, and this is the fallback it was always written to
 * be. The comment is kept rather than deleted because "lands on its REAL content" is exactly what a
 * reader would assume again.
 */
function renderAppSurface(app: ShellInjectionRef, activeTabKey: string | undefined): HTMLElement {
  const appLabel = labelOf(app);
  const tabs = app.tabs ?? [];
  const activeTab = tabs.find((t) => t.key === activeTabKey);

  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__page';

  const header = document.createElement('header');
  header.className = 'caos-frame__page-header';
  const eyebrow = document.createElement('div');
  eyebrow.className = 'caos-frame__page-eyebrow';
  eyebrow.textContent = appLabel;
  const title = document.createElement('h1');
  title.className = 'caos-frame__page-title';
  title.textContent = activeTab ? activeTab.label : appLabel;
  header.append(eyebrow, title);
  wrap.append(header);

  if (activeTab?.controlPlane) {
    // A host-contributed control-plane surface: a live roster read of
    // `/api/<noun>/<verb>`, rendered by the shared control-plane renderer (its own header + table).
    const cp = activeTab.controlPlane;
    return renderControlPlaneContent({
      kind: 'controlPlane',
      app: app.sourceKey,
      tab: activeTab.key,
      title: cp.title,
      read: { noun: cp.noun, verb: cp.verb },
      ...(cp.rowsAt ? { rowsAt: cp.rowsAt } : {}),
      ...(cp.columns ? { columns: cp.columns } : {}),
    }).root;
  }

  if (activeTab?.object) {
    // A bare roster of the tab's OBJECT: its fields become columns and the org's records become rows
    // (CAOS-306). NOT the tab's authored list view — see the note on this function. The roster paints
    // a loading line immediately, then swaps in the real table (or an honest error) once the
    // same-origin data read resolves, so the frame stays responsive and never blocks on the fetch.
    const roster = document.createElement('div');
    roster.className = 'caos-frame__list';
    const loading = document.createElement('p');
    loading.className = 'caos-frame__ready-body';
    loading.textContent = `Loading ${activeTab.label}…`;
    roster.append(loading);
    wrap.append(roster);
    void loadListView(activeTab.object)
      .then((view) => paintListView(roster, view))
      .catch(() => {
        const err = document.createElement('p');
        err.className = 'caos-frame__ready-body';
        err.textContent = `Could not load ${activeTab.label}.`;
        roster.replaceChildren(err);
      });
    return wrap;
  }

  const note = document.createElement('p');
  note.className = 'caos-frame__ready-body';
  if (activeTab) {
    // A tab with no target object — a legible identity line, never a placeholder record count.
    note.textContent = `${activeTab.label}.`;
  } else {
    // A thin app with no object tabs — a legible empty state, never a menu of cards.
    note.textContent = `The ${appLabel} app is installed. It has no object tabs to show yet.`;
  }
  wrap.append(note);
  return wrap;
}

/**
 * Paint a resolved {@link ListView} into the roster host: a `<caos-data-table>` fed the object's real
 * columns + rows AS DATA (the component does no fetch of its own). An empty object still renders the
 * COLUMN HEADERS with the table's own columns-present empty state (`empty-label`); a read failure
 * renders an honest line instead of a broken table.
 */
function paintListView(host: HTMLElement, view: ListView): void {
  host.replaceChildren();
  if (view.error) {
    const err = document.createElement('p');
    err.className = 'caos-frame__ready-body';
    err.textContent = view.error;
    host.append(err);
    return;
  }
  const table = document.createElement('caos-data-table');
  table.setAttribute('empty-label', `No ${view.pluralLabel.toLowerCase()} yet`);
  (table as unknown as { columns: unknown }).columns = view.columns.map((c) => ({
    key: c.key,
    label: c.label,
    ...(c.align ? { align: c.align } : {}),
  }));
  (table as unknown as { rows: unknown }).rows = view.rows.map((r) => ({ id: r.id, cells: r.cells }));
  host.append(table);
}

/** The time-of-day greeting ("Good morning" / "afternoon" / "evening"), from the local clock. */
function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** The first name from a display name / email, for a warm greeting ("Riley Chen" → "Riley"). */
function firstName(name?: string): string | undefined {
  if (!name) return undefined;
  const base = name.includes('@') ? name.split('@')[0]! : name;
  const first = base.replace(/[._-]+/g, ' ').trim().split(/\s+/)[0];
  if (!first) return undefined;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/**
 * CAOS-136 — the real Home landing, the shell's default surface on login. It is HONEST — it renders
 * only real org data, never a fabricated dashboard: a personal greeting, then a grid of the org's
 * ACTUALLY installed apps (each card opens its app), and — when one is actually anchored — a
 * pointer to the assistant, named by its own declared label (CAOS-769). A brand-new org with no
 * apps shows a legible getting-started state rather than a blank invite, so a fresh org is legible.
 */
function renderHomeSurface(
  plan: ShellFramePlan,
  navEntries: readonly ShellInjectionRef[],
  runtime: ShellRuntime,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__page caos-home';

  // Hero — a personal greeting (real user name when present) over a short lead.
  const header = document.createElement('header');
  header.className = 'caos-frame__page-header caos-home__hero';
  const eyebrow = document.createElement('div');
  eyebrow.className = 'caos-frame__page-eyebrow';
  eyebrow.textContent = 'Home';
  const title = document.createElement('h1');
  title.className = 'caos-frame__page-title caos-home__title';
  const who = firstName(runtime.user?.name);
  title.textContent = who ? `${greeting()}, ${who}` : greeting();
  const lead = document.createElement('p');
  lead.className = 'caos-frame__ready-body caos-home__lead';
  // CAOS-769 — this line used to promise "ask Atlantis AI from the spark in the corner" on every
  // org, including one running Core alone with no assistant installed and no spark to ask. The
  // pointer is earned: it appears only when an assistant is actually anchored, and it calls that
  // assistant by ITS OWN declared label rather than by a name compiled in here.
  // Resolved through `anchorKind`, NOT a bare `kind ?? 'assistant'`: none of the real catalog
  // entries declares `kind`, so treating an undeclared anchor as the assistant would point at
  // Report a Bug or Guidance Center — the same false claim, aimed at a different package.
  //
  // CAOS-1519 — and it names it ONLY when the anchor declares a label of its own. `labelOf` falls
  // back to `humanize(sourceKey)`, which is right for a nav entry (a machine key rendered as a
  // readable string beats a blank) and wrong inside a sentence: it put a component's registration
  // key into English, and an org saw "ask Cr Proving Ground Edge Anchor from the spark in the
  // corner". That reads as broken copy to anyone who does not know the metadata.
  //
  // The reason an unlabelled anchor reaches this line at all is `anchorKind`'s compatibility shim
  // (CAOS-734), which calls an anchor declaring no `kind` an assistant. That shim is deliberate and
  // load-bearing for panel ROUTING, so it stays; what changes is that prose no longer states an
  // identity the platform only guessed at. Without a declared label there is nothing to name, so
  // the sentence points at the affordance instead — which is what the reader needs anyway.
  const assistant = (plan.injections['edge-anchored'] ?? []).find((a) => anchorKind(a) === 'assistant');
  // Deliberately NOT `labelOf` — see its own note on why the same `typeof` guard lives there too.
  // `labelOf` answers "what should this anchor be CALLED", and falls back to `humanize(sourceKey)`;
  // this line asks the narrower question "did the package NAME itself", where the humanized key is
  // exactly the wrong answer and absence must read as absence. The guard is repeated rather than
  // shared because two call sites do not justify a third concept; a third would.
  const label: unknown = assistant?.label;
  const assistantName = typeof label === 'string' ? label.trim() : '';
  // CAOS-1823 — and it points at WHERE THE AFFORDANCE IS. The sentence said "the spark in the
  // corner" from a time when an anchored surface rendered as a floating corner orb. CAOS-450 ended
  // that: every `edge-anchored` surface now paints as a vertical tab stacked in the right-hand
  // gutter. So a reader who followed this sentence checked four corners and found nothing in any of
  // them — the same class of false claim CAOS-769 and CAOS-1519 removed from this very line, this
  // time about the place rather than the name.
  //
  // Deliberately NOT branched on the anchor's declared `anchor.edge`. That field still exists and
  // still accepts 'bottom-right', but the metatype says in terms that it "is retained for a future
  // distinct placement but does not currently select between two renderings" — so reading it here
  // would put a declared value into English that the renderer ignores, which is how this sentence
  // went wrong the last two times. The copy states what is actually drawn.
  lead.textContent = !assistant
    ? 'Jump into an app to pick up your work.'
    : assistantName
      ? `Jump into an app to pick up your work, or ask ${assistantName} from its tab on the right-hand edge.`
      : 'Jump into an app to pick up your work, or use the tabs on the right-hand edge.';
  header.append(eyebrow, title, lead);
  wrap.append(header);

  // Apps — the org's REAL installed apps. The shell's own workspace app is not a destination, so it
  // is omitted; each remaining app is a card that opens it.
  const openable = navEntries.filter((a) => a.sourceKey !== plan.master && offeredInSwitcher(a));
  const section = document.createElement('section');
  section.className = 'caos-home__section';
  const secTitle = document.createElement('h2');
  secTitle.className = 'caos-home__section-title';
  secTitle.textContent = 'Your apps';
  section.append(secTitle);

  if (openable.length > 0) {
    const grid = document.createElement('div');
    grid.className = 'caos-home__apps';
    for (const app of openable) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'caos-home__app';
      card.dataset.appKey = app.sourceKey;
      const mark = document.createElement('span');
      mark.className = 'caos-home__app-mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = (app.icon ?? labelOf(app).charAt(0)).slice(0, 2);
      const name = document.createElement('span');
      name.className = 'caos-home__app-name';
      name.textContent = labelOf(app);
      const meta = document.createElement('span');
      meta.className = 'caos-home__app-meta';
      const tabCount = app.tabs?.length ?? 0;
      meta.textContent = tabCount > 0 ? `${tabCount} ${tabCount === 1 ? 'tab' : 'tabs'}` : 'Open';
      card.append(mark, name, meta);
      card.addEventListener('click', () => runtime.navigate?.(`/${app.sourceKey}`));
      grid.append(card);
    }
    section.append(grid);
  } else {
    const empty = document.createElement('p');
    empty.className = 'caos-frame__ready-body';
    empty.textContent =
      'No apps are installed yet. An administrator can add apps from Setup to get your workspace started.';
    section.append(empty);
  }
  wrap.append(section);
  return wrap;
}

/** The default content-region "ready" state when a route has not yet resolved a page. */
function readyContent(plan: ShellFramePlan): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__ready';
  const h = document.createElement('h1');
  h.className = 'caos-frame__ready-title';
  h.textContent = 'Workspace ready';
  const p = document.createElement('p');
  p.className = 'caos-frame__ready-body';
  p.textContent = `The ${humanize(plan.master)} workspace is assembled. Choose a destination to open it.`;
  wrap.append(h, p);
  return wrap;
}

/**
 * Paint a resolved content descriptor into the content region — a shallow, honest projection of the
 * kernel content plan (record / list / fallback). Full field-level fidelity is CAOS-127; this
 * renders the plan's identity so the frame is demonstrable.
 */
export function renderContentPlan(content: ShellContent): HTMLElement {
  if (content.kind === 'fallback') {
    const wrap = document.createElement('div');
    wrap.className = 'caos-frame__fallback';
    wrap.dataset.fallbackKind = content.fallbackKind;
    const h = document.createElement('h1');
    h.className = 'caos-frame__ready-title';
    h.textContent = { 'unknown-route': 'Page not found', 'missing-record': 'Record not found', unauthorized: 'Access denied' }[
      content.fallbackKind
    ];
    const p = document.createElement('p');
    p.className = 'caos-frame__ready-body';
    p.textContent = content.message;
    wrap.append(h, p);
    return wrap;
  }

  // CAOS-247 — a control-plane surface fetches its payload live and renders tiles + roster + actions.
  if (content.kind === 'controlPlane') return renderControlPlaneContent(content).root;

  // CAOS-320 — an intake surface renders an engine-driven working form; submit POSTs the write route.
  if (content.kind === 'intake') return renderIntakeContent(content).root;

  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__page';

  const header = document.createElement('header');
  header.className = 'caos-frame__page-header';
  const eyebrow = document.createElement('div');
  eyebrow.className = 'caos-frame__page-eyebrow';
  eyebrow.textContent = `${humanize(content.app)} · ${humanize(content.tab)}`;
  const title = document.createElement('h1');
  title.className = 'caos-frame__page-title';
  title.textContent = content.title;
  header.append(eyebrow, title);
  wrap.append(header);

  if (content.kind === 'record') {
    for (const section of content.sections ?? []) {
      const sec = document.createElement('section');
      sec.className = 'caos-frame__section';
      const st = document.createElement('h2');
      st.className = 'caos-frame__section-title';
      st.textContent = section.label;
      sec.append(st);
      const grid = document.createElement('div');
      grid.className = 'caos-frame__fields';
      for (const field of section.fields) {
        const row = document.createElement('div');
        row.className = 'caos-frame__field';
        const label = document.createElement('span');
        label.className = 'caos-frame__field-label';
        label.textContent = field;
        const value = document.createElement('span');
        value.className = 'caos-frame__field-value';
        value.textContent = '—';
        row.append(label, value);
        grid.append(row);
      }
      sec.append(grid);
      wrap.append(sec);
    }
  } else {
    const table = document.createElement('table');
    table.className = 'caos-frame__table';
    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    for (const col of content.columns ?? []) {
      const th = document.createElement('th');
      th.textContent = col;
      htr.append(th);
    }
    thead.append(htr);
    const tbody = document.createElement('tbody');
    for (const rowData of content.rows ?? []) {
      const tr = document.createElement('tr');
      for (const col of content.columns ?? []) {
        const td = document.createElement('td');
        const cell = rowData[col];
        td.textContent = cell === undefined || cell === null ? '—' : String(cell);
        tr.append(td);
      }
      tbody.append(tr);
    }
    table.append(thead, tbody);
    wrap.append(table);
  }

  return wrap;
}

/**
 * CAOS-247 — the control-plane fetch seam. The renderer reads the READ route and POSTs WRITE routes
 * through this; production leaves it as the host worker's `/api/*` proxy (the same origin the account
 * routes live behind), and the deterministic test overrides it with a stub returning a fixture
 * payload — exactly the seam pattern the empty-workspace logout uses (`setSignOutHandler`).
 */
export interface ControlPlaneResponse {
  json(): Promise<unknown>;
  /** HTTP ok flag — the intake submit reads it to confirm the write landed (CAOS-320). */
  readonly ok?: boolean;
}
/**
 * CAOS-247 read/write seam, extended for CAOS-320 with a JSON `body` + `headers` so an intake form can
 * POST `/api/data/create` with `{ object, fields }`. A control-plane row action still calls it with
 * only `{ method }` (the row id rides the path), so the extra fields are optional and back-compatible.
 */
export type ControlPlaneFetch = (
  path: string,
  init?: { method?: string; body?: string; headers?: Record<string, string> },
) => Promise<ControlPlaneResponse>;

export const defaultControlPlaneFetch: ControlPlaneFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...(init ?? {}) }) as unknown as Promise<ControlPlaneResponse>;

let controlPlaneFetch: ControlPlaneFetch = defaultControlPlaneFetch;

/** CAOS-607 — stop. The host clears that cookie whatever this answers. */
const IMPERSONATION_END_ENDPOINT = '/api/impersonation/end';
/**
 * CAOS-607/CAOS-1904 — the overlay, the required reason, the POST and the reload used to live here
 * as a module-private function, reachable from the account menu and nowhere else.
 *
 * It is now `./impersonation-door.ts`, unchanged in behaviour, because CAOS-1904 gives the same
 * capability a second door on a person's own page in Setup and the ticket is explicit that this must
 * be a second DOOR rather than a second implementation. The reason it MOVED rather than being
 * exported and called across is a cycle: a roster surface importing this file would close
 * `shell-frame` → `content-source` → the roster surfaces → `shell-frame`, and would pull four
 * thousand lines of shell into every test that renders a detail page.
 *
 * The two seams below forward into it, so there is still one injection point.
 */


/** Full-document navigation seam — real navigation in the browser, observable in tests. */
export type LeaveTo = (path: string) => void;
const defaultLeaveTo: LeaveTo = (path) => window.location.assign(path);
let leaveTo: LeaveTo = defaultLeaveTo;

/**
 * CAOS-292 — is this app offered in the app switcher (and on the home app cards)? An app that says
 * nothing is offered, so the switch is purely opt-OUT. Hiding an app is a NAVIGATION choice, never
 * an access one: the app's routes still resolve and a direct link still opens it.
 */
function offeredInSwitcher(app: ShellInjectionRef): boolean {
  return app.showInAppSwitcher !== false;
}

/**
 * Override the full-navigation seam (tests).
 *
 * CAOS-1904 — forwarded into `impersonation-door.ts` as well, so a test that overrides this still
 * controls where a started impersonation leaves to. The door owns its own copy of the seam because
 * importing this file from a roster surface would close a cycle; forwarding is what keeps the
 * number of injection points at one rather than letting it become two.
 */
export function setLeaveTo(fn: LeaveTo): void {
  leaveTo = fn;
  setImpersonationLeaveTo(fn);
}

/**
 * CAOS-340 — the "where were they" label carried with every feedback report.
 *
 * The shell knows its own active app + tab, so record it as the frame paints rather than scraping a
 * CSS class at submit time: only the shell's OWN page renderers emit `.caos-frame__page-eyebrow`, so
 * on a list or record surface (rendered by a component, inside its shadow root) the scrape found
 * nothing and the review queue's Where column came back empty. The DOM read stays as a fallback for
 * surfaces the shell paints itself.
 */
let activeContextLabel: string | undefined;

/** Record the active app/tab context label (called by the frame on every repaint; tests may set it). */
export function setActiveContextLabel(label: string | undefined): void {
  activeContextLabel = label;
}

/**
 * CAOS-327 — whether a rail tab sits proud of the edge (default) or tucks away to a sliver. A
 * preference about the workspace's furniture, so it is remembered rather than reset every visit.
 * CAOS-470 — remembered against the person (`rail.pinned.<source>`), so it follows them to any browser.
 */
const RAIL_PIN_PREFIX = 'rail.pinned.';
function railPinKey(sourceKey: string): string {
  return `${RAIL_PIN_PREFIX}${sourceKey}`;
}


/**
 * CAOS-470 — the document subscriptions that follow the theme and the person’s preferences. A frame is
 * rebuilt on every shell refresh, so each is held here and replaced rather than added to: only the
 * newest frame follows a change, and an unmounted one is not kept alive by its listeners.
 */
let activeThemeListener: ((ev: Event) => void) | undefined;
let activeNavPreferenceListener: ((ev: Event) => void) | undefined;
let activeRailPreferenceListener: ((ev: Event) => void) | undefined;
function replaceDocumentListener(
  type: string,
  previous: ((ev: Event) => void) | undefined,
  next: (ev: Event) => void,
): (ev: Event) => void {
  if (previous) document.removeEventListener(type, previous);
  document.addEventListener(type, next);
  return next;
}
function loadRailPinned(sourceKey: string): boolean {
  return preference(railPinKey(sourceKey)) !== false; // nothing chosen ⇒ the tab stays where everyone can see it
}
function saveRailPinned(sourceKey: string, pinned: boolean): void {
  void setPreference(railPinKey(sourceKey), pinned);
}

/** The context label to file a report against: the shell's own state first, the page eyebrow second. */
function currentContextLabel(): string | undefined {
  if (activeContextLabel) return activeContextLabel;
  if (typeof document === 'undefined') return undefined;
  return document.querySelector('.caos-frame__page-eyebrow')?.textContent?.trim() || undefined;
}

/**
 * CAOS-326 — the reserved route for a reporter's own submissions. It is a shell route rather than a
 * tab in some app because feedback belongs to the person, not to an app: you reach it from the report
 * form and from the account menu, wherever in the workspace you happen to be.
 */
export const MY_FEEDBACK_PATH = '/my-feedback';

/**
 * The frame's navigate seam moved to `../navigate-seam.js` in CAOS-497, when a page-placed
 * `card_grid` became its second caller. It is imported at the top of this file; the behaviour,
 * including the real-document-load fallback, is unchanged.
 */

/** Who is signed in, as a readable name (the frame records it on every repaint). */
let activeUserName: string | undefined;

/** Record the signed-in person's display name (called by the frame; tests may set it). */
export function setActiveUserName(name: string | undefined): void {
  activeUserName = name;
}

/** The reporter's readable name for a submission, or undefined when the shell does not know it. */
function signedInName(): string | undefined {
  return activeUserName?.trim() || undefined;
}

/**
 * Override the control-plane fetch seam (production wiring / tests).
 *
 * CAOS-1904 — forwarded into `impersonation-door.ts` for the reason {@link setLeaveTo} gives, and
 * for the one `getControlPlaneFetch` below already states: sharing this seam rather than letting a
 * caller reach for the bare global `fetch` is what keeps same-origin credential handling and the
 * test override working for every reader of a control-plane route.
 */
export function setControlPlaneFetch(fn: ControlPlaneFetch): void {
  controlPlaneFetch = fn;
  setImpersonationFetch(fn as unknown as DoorFetch);
}

/**
 * CAOS-755 — read the ACTIVE control-plane fetch seam, for a caller that reads a control-plane
 * route without going through {@link renderControlPlaneContent} (e.g. `my-feedback-surface.ts`,
 * which draws the real List View surface via `renderCollection` instead of the generic host-baked
 * table). Sharing this seam — rather than each caller reaching for the bare global `fetch` — keeps
 * the same-origin credentials handling and the test override (`setControlPlaneFetch`) working for
 * every reader of a control-plane route, not only the ones that render through this file.
 */
export function getControlPlaneFetch(): ControlPlaneFetch {
  return controlPlaneFetch;
}

/**
 * Extract the roster row array from the payload at `rowsAt` (or the payload itself), object rows only.
 *
 * CAOS-362 — the predicate this file used to carry now lives in {@link ../predicate.js}, shared with
 * the record-header actions. Both ask the same question of a row, so both ask it in the same words.
 */
function extractRows(payload: unknown, rowsAt?: string): Record<string, unknown>[] {
  const raw = rowsAt ? readJsonPath(payload, rowsAt) : payload;
  return Array.isArray(raw) ? raw.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object') : [];
}

/** Build the per-row action buttons cell — native `<button>`s that POST the write route, then re-read. */
function actionCell(
  row: Record<string, unknown>,
  actions: readonly ShellRowAction[],
  reload: () => Promise<void>,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'caos-frame__cp-actions';
  for (const action of actions) {
    if (action.appliesWhen && !rowMatches(action.appliesWhen, row)) continue;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'caos-frame__cp-action';
    btn.dataset.actionId = action.id;
    if (action.intent) btn.dataset.intent = action.intent;
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      btn.setAttribute('aria-disabled', 'true');
      const rowId = String(row[action.paramFrom] ?? '');
      void (async () => {
        // POST the control-plane WRITE route, then RE-READ the read route + re-render (empty-workspace
        // reboot precedent) so the roster reflects the new state without a full-page reload.
        await controlPlaneFetch(`/api/${action.verb.noun}/${action.verb.verb}/${encodeURIComponent(rowId)}`, {
          method: 'POST',
        });
        await reload();
      })();
    });
    box.append(btn);
  }
  return box;
}

/**
 * CAOS-247 — render a control-plane surface. On mount it fetches the READ route, renders the stat
 * tiles (`<caos-stat-tile>`) + the roster (`<caos-data-table>` fed columns + rows AS DATA — the
 * component does NO live fetch of its own) + the per-row action buttons, and re-reads after any
 * action. Returns the root element (paints immediately) plus the in-flight `done` promise so a test
 * can await the initial fetch before asserting.
 */
export function renderControlPlaneContent(content: ShellControlPlaneContent): {
  root: HTMLElement;
  done: Promise<void>;
} {
  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__page caos-frame__cp';

  const header = document.createElement('header');
  header.className = 'caos-frame__page-header';
  const eyebrow = document.createElement('div');
  eyebrow.className = 'caos-frame__page-eyebrow';
  eyebrow.textContent = `${humanize(content.app)} · ${humanize(content.tab)}`;
  const title = document.createElement('h1');
  title.className = 'caos-frame__page-title';
  title.textContent = content.title;
  header.append(eyebrow, title);

  const statsRow = document.createElement('div');
  statsRow.className = 'caos-frame__cp-stats';
  const rosterHost = document.createElement('div');
  rosterHost.className = 'caos-frame__cp-roster';
  wrap.append(header, statsRow, rosterHost);

  const readRoute = `/api/${content.read.noun}/${content.read.verb}`;
  const actions = content.rowActions ?? [];

  // CAOS-331 — an honest status line for the transient loading / error states (never a blank roster).
  const statusLine = (text: string, error = false): HTMLElement => {
    const p = document.createElement('p');
    p.className = 'caos-frame__ready-body';
    p.setAttribute('role', error ? 'alert' : 'status');
    if (!error) p.setAttribute('aria-live', 'polite');
    p.textContent = text;
    return p;
  };

  // Paint a loading line the instant the surface mounts — before the live roster read settles.
  rosterHost.append(statusLine(`Loading ${content.title.toLowerCase()}…`));

  const populate = (payload: unknown): void => {
    // Stat tiles — each reads a JSON path from the live payload.
    statsRow.replaceChildren();
    for (const stat of content.stats ?? []) {
      const tile = document.createElement('caos-stat-tile');
      tile.setAttribute('label', stat.label);
      const v = readJsonPath(payload, stat.valueAt);
      tile.setAttribute('value', v === undefined || v === null ? '—' : String(v));
      statsRow.append(tile);
    }

    // Roster — a caos-data-table fed columns + rows as DATA; actions ride an in-cell node column. An
    // empty roster keeps the column headers with the table's columns-present empty state.
    rosterHost.replaceChildren();
    const rows = extractRows(payload, content.rowsAt);
    const table = document.createElement('caos-data-table');
    table.setAttribute('empty-label', `No ${content.title.toLowerCase()} yet`);
    const dataColumns = (content.columns ?? []).map((c) => ({ key: c.key, label: c.label }));
    const hasActions = actions.length > 0;
    (table as unknown as { columns: unknown }).columns = hasActions
      ? [...dataColumns, { key: '__actions', label: 'Actions', align: 'end' }]
      : dataColumns;
    (table as unknown as { rows: unknown }).rows = rows.map((row, i) => {
      const cells: Record<string, unknown> = {};
      for (const c of content.columns ?? []) cells[c.key] = c.derive ? (c.derive(row) ?? null) : (row[c.key] ?? null);
      if (hasActions) cells['__actions'] = { node: actionCell(row, actions, reload) };
      return { id: String(row['id'] ?? row['orgId'] ?? i), cells };
    });
    rosterHost.append(table);
  };

  const reload = async (): Promise<void> => {
    try {
      const res = await controlPlaneFetch(readRoute);
      populate(await res.json());
    } catch {
      // A distinct, honest error state — NOT a silent empty roster.
      statsRow.replaceChildren();
      rosterHost.replaceChildren(
        statusLine(`Couldn't load ${content.title.toLowerCase()}. Check your connection and try again.`, true),
      );
    }
  };

  return { root: wrap, done: reload() };
}

/**
 * CAOS-300 — the persistent-shell client-router seam. The most-recently assembled frame registers a
 * repaint closure here; the host's navigate + popstate wiring calls {@link applyShellFrameRoute} to
 * repaint ONLY the app-scoped regions in place, so the top chrome never remounts on an in-app move.
 */
let activeFrameNavigator: ((route: string) => void) | null = null;

/** The newest frame's open-record subscription, so a re-render can unsubscribe the previous one. */
let activeRecordOpenListener: ((ev: Event) => void) | null = null;

/** The newest frame's guidance-open subscription (CAOS-148), replaced the same way on a re-render. */
let activeGuidanceOpenListener: ((ev: Event) => void) | null = null;

/** Register the active frame's in-place route repaint (called by `assembleShellFrame`). */
function setActiveFrameNavigator(fn: (route: string) => void): void {
  activeFrameNavigator = fn;
}

/**
 * Repaint the live shell frame for a new path WITHOUT a full document load or a host re-render.
 * Returns true when a frame handled it (so the caller knows client routing was in effect). The host
 * calls this after a `history.pushState` and on `popstate`; there is no-op when no frame is mounted.
 */
export function applyShellFrameRoute(route: string): boolean {
  if (!activeFrameNavigator) return false;
  activeFrameNavigator(route);
  return true;
}

/**
 * CAOS-326 — a half-written report must survive walking away from it.
 *
 * The panel is only mounted while it is open, and it remounts on navigation. Anything kept purely in
 * the form's own state is gone the moment somebody switches app to go and look at the thing they are
 * reporting — which is exactly when they navigate. The typed fields are mirrored to session storage,
 * so they also survive a reload, and they are scoped to this browser tab rather than kept forever.
 *
 * Screenshots are deliberately NOT persisted: a pasted image cannot be serialised into storage, and
 * writing a base64 copy of every screenshot into session storage to pretend otherwise would blow the
 * quota on the second one. They survive navigation (they live in memory alongside the open panel)
 * and are lost on a reload, which is the honest limit.
 */
function draftKey(object: string): string {
  return `caos.intakeDraft.v1.${object}`;
}
function loadIntakeDraft(object: string): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(draftKey(object));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {}; // corrupt or unavailable storage ⇒ start blank, never throw at the reporter
  }
}
function saveIntakeDraft(object: string, values: Map<string, string>): void {
  try {
    const typed = Object.fromEntries([...values].filter(([, v]) => v.trim().length > 0));
    if (Object.keys(typed).length === 0) sessionStorage.removeItem(draftKey(object));
    else sessionStorage.setItem(draftKey(object), JSON.stringify(typed));
  } catch {
    /* storage full or blocked — the draft simply does not survive, which is the pre-existing behaviour */
  }
}

/**
 * A short, human-citable reference code for a submitted intake row.
 *
 * The code itself is {@link submissionRef}, shared with the Get-help composer and the My Feedback
 * list so all three render one id the same way. The fallback is this form's own and is unchanged:
 * when the write route returns no usable id it mints a LOCAL placeholder from the clock, so the
 * footer has something to show. That placeholder matches no stored row — it is a receipt for the
 * person's own eyes, not a reference anyone can look up, which is why the Get-help confirmation
 * deliberately shows nothing in the same situation rather than reusing this.
 */
function intakeRef(id: unknown): string {
  return submissionRef(id) || `FB-${Date.now().toString(36).toUpperCase()}`;
}

/**
 * The lead a form shows when its package authored none (CAOS-1492 K1).
 *
 * Kept as the FALLBACK rather than deleted along with the hardcoding it used to be: every intake page
 * authored before `lead` existed relies on it, and a form that suddenly had no lead at all would be a
 * worse outcome than one whose lead the package cannot yet change.
 */
const DEFAULT_INTAKE_LEAD =
  'Tell us what happened. Add screenshots if you can — attach, paste (Ctrl/Cmd-V), or drag them in.';

/**
 * CAOS-1492 K2 — the reference shown with a confirmation, on the shape the page DECLARED.
 *
 * Three cases, in the order the descriptor decides them:
 *  - `reference.field` — a field of the record that was just created. Read off the created row, not
 *    off what was typed: the two differ exactly when the write normalised, defaulted or generated the
 *    value, which is when a reference is worth showing at all.
 *  - `reference.idPrefix` / `idLength` — the id projected through the shared {@link projectedRef}, so
 *    an authored code goes through the same formatter as the platform's own.
 *  - neither — the host's own `FB-` code, which is what every form showed before this existed.
 *
 * Returns the empty string when an authored reference cannot be produced. The caller shows the
 * message alone in that case, which is deliberate: a confirmation with no code is honest, and a
 * fabricated code is the one failure this module's header calls out by name.
 */
function intakeConfirmationRef(
  spec: NonNullable<ShellIntakeContent['confirmation']>['reference'] | undefined,
  createdFields: Record<string, unknown> | undefined,
  id: unknown,
  bareKey: (full: string) => string,
): string {
  if (spec === undefined) return intakeRef(id);
  if (spec.field !== undefined) {
    const value = createdFields?.[bareKey(spec.field)] ?? createdFields?.[spec.field];
    return value == null ? '' : String(value).trim();
  }
  if (spec.idPrefix !== undefined || spec.idLength !== undefined) {
    return projectedRef(id, spec.idPrefix ?? '', spec.idLength ?? 6);
  }
  return intakeRef(id);
}

/** The one place in an authored confirmation message where the reference is substituted. */
const REFERENCE_PLACEHOLDER = '{reference}';

/**
 * The placeholder as a pattern, DERIVED from the string above rather than written twice, so the two
 * cannot drift into disagreeing about what the placeholder is.
 *
 * Two forms, because removing a placeholder that resolved to nothing is not the same edit as
 * removing one mid-sentence: the first takes the space BEFORE the placeholder when punctuation
 * follows it, turning `your reference is {reference}.` into `your reference is.` rather than leaving
 * the orphaned ` .` that a plain removal does.
 */
const ESCAPED_PLACEHOLDER = REFERENCE_PLACEHOLDER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const PLACEHOLDER_BEFORE_PUNCTUATION = new RegExp(` *${ESCAPED_PLACEHOLDER} *(?=[.,;:!?])`, 'g');
const PLACEHOLDER_ANYWHERE = new RegExp(` *${ESCAPED_PLACEHOLDER} *`, 'g');

/**
 * CAOS-1492 K2 — the sentence a reporter is left looking at.
 *
 * An authored `message` may place the reference with `{reference}`; without the placeholder the code
 * is APPENDED after a space, and a message authored with no reference resolvable shows alone.
 *
 * The placeholder is not decoration, and the first version of this had only the append. It was the
 * Report a Bug package that showed why append is not enough: the reference build's own confirmation
 * reads `Thanks — logged. Your reference is FB-A1B2C3.` — the code sits MID-SENTENCE with a full
 * stop after it, and append can only ever put it last. So a package authoring its own copy could not
 * reproduce the sentence the platform was already showing, which would have made authoring this
 * field a downgrade and left the only faithful version of that sentence in host code. That is the
 * defect CAOS-326 exists to correct, reintroduced one layer up.
 *
 * Append is kept as the no-placeholder behaviour rather than replaced: it is what a short
 * confirmation wants, it needs nothing learned, and a message that never mentions the reference
 * still gets one.
 */
function confirmationText(message: string, ref: string): string {
  if (!message.includes(REFERENCE_PLACEHOLDER)) {
    return ref === '' ? message : `${message} ${ref}`;
  }
  // A resolvable reference is substituted and NOTHING ELSE about the message is touched.
  //
  // Raised in review, and it was the right catch: a first version ran `.replace(/ {2,}/g, ' ')` over
  // the whole string on every substitution, to tidy the gap an empty reference leaves. That also
  // silently reformatted authored copy in the ordinary case — two spaces after a full stop is a
  // style somebody chooses, and rewriting it is not this function's business. Whoever authored the
  // sentence gets the sentence they authored.
  if (ref !== '') return message.split(REFERENCE_PLACEHOLDER).join(ref);

  // Nothing to put there. Remove the placeholder and only the seam it leaves: the space before it
  // when punctuation follows (so `your reference is {reference}.` reads `your reference is.` rather
  // than keeping an orphaned ` .`), otherwise collapse to one space so words do not run together.
  // `trim` is end-only and therefore safe — leading or trailing space in a status line carries
  // nothing, while an interior double space may well be deliberate.
  return message
    .replace(PLACEHOLDER_BEFORE_PUNCTUATION, '')
    .replace(PLACEHOLDER_ANYWHERE, ' ')
    .trim();
}

/** Pull the created record id out of whatever envelope the write route returned (raw or `{data}`-wrapped). */
function createdRecordId(payload: unknown): unknown {
  if (payload == null || typeof payload !== 'object') return undefined;
  const p = payload as Record<string, unknown>;
  const data = p['data'] as Record<string, unknown> | undefined;
  // CAOS-326 — a write verb names its result after what it wrote: the generic record create returns
  // `record`, feedback returns `feedback`. Reading only `record` meant the id was never found for a
  // report, and the reference code fell back to a timestamp — a code that identified NOTHING, handed
  // to the reporter as though they could quote it back. Take the first object carrying an id. (vocabulary-ok: quote as in repeat it back)
  for (const candidate of [p['record'], data?.['record'], p['feedback'], data?.['feedback']]) {
    const id = (candidate as Record<string, unknown> | undefined)?.['id'];
    if (id != null) return id;
  }
  return p['id'];
}

/**
 * CAOS-1492 K2 — the created ROW itself, for a confirmation whose reference cites one of its fields.
 *
 * Same envelope walk as {@link createdRecordId} and deliberately the same candidate order, so the two
 * never disagree about WHICH object in the response is the record: reading the id from one candidate
 * and the fields from another would produce a reference belonging to neither.
 */
function createdRecordFields(payload: unknown): Record<string, unknown> | undefined {
  if (payload == null || typeof payload !== 'object') return undefined;
  const p = payload as Record<string, unknown>;
  const data = p['data'] as Record<string, unknown> | undefined;
  for (const candidate of [p['record'], data?.['record'], p['feedback'], data?.['feedback']]) {
    const row = candidate as Record<string, unknown> | undefined;
    if (row != null && typeof row === 'object' && row['id'] != null) return row;
  }
  return typeof p['id'] === 'string' || typeof p['id'] === 'number' ? p : undefined;
}

/**
 * CAOS-320 — render an engine-driven INTAKE form (the primitive that makes the edge-anchored orb a
 * REAL working surface, not a static button). It paints the form from the descriptor's `fields`
 * (textarea / text / picklist / attachment controls), enforces `required`, and on submit POSTs the
 * WRITE route (`/api/<noun>/<verb>`) with `{ object, fields }` through the shared control-plane fetch
 * seam — then confirms on-screen with a reference code and clears the form. A faithful port of the
 * Dragon `BugReportPanel` behaviour (required description, Bug/Confusing/Idea picker, screenshot
 * attach + paste + drag), recolored to CAOS tokens. Returns the root element plus the `submit`
 * function so a test can await one submit deterministically.
 */
export function renderIntakeContent(
  content: ShellIntakeContent,
  opts: {
    onSubmitted?: (ref: string) => void;
    /**
     * CAOS-326 — the form is inside a panel that already names it. Without this the panel showed its
     * title twice, one immediately under the other.
     */
    titled?: boolean;
    /** When present, the form offers a way to go and see what you have already reported. */
    navigate?: (href: string) => void;
  } = {},
): { root: HTMLElement; submit: () => Promise<void> } {
  const wrap = document.createElement('div');
  wrap.className = 'caos-frame__intake';

  // Header — the form title (suppressed when the surface around it already carries one).
  const header = document.createElement('header');
  header.className = 'caos-frame__intake-header';
  if (opts.titled !== false) {
    const title = document.createElement('h2');
    title.className = 'caos-frame__intake-title';
    title.textContent = content.title;
    header.append(title);
  }
  const blurb = document.createElement('p');
  blurb.className = 'caos-frame__intake-blurb';
  // CAOS-1492 K1 — the authoring package's own words when it supplied any. This sentence used to be
  // the only possibility, which meant a package could neither translate it nor say something truer
  // about its own form.
  blurb.textContent = content.lead ?? DEFAULT_INTAKE_LEAD;
  header.append(blurb);
  wrap.append(header);

  const body = document.createElement('div');
  body.className = 'caos-frame__intake-body';
  wrap.append(body);

  // Per-field state. Scalar controls store a string; the attachment control stores data URLs.
  const values = new Map<string, string>();
  const attachments = new Map<string, { name: string; dataUrl: string }[]>();
  const requiredScalar: string[] = [];
  // CAOS-1625 — the drawn checkboxes, kept by field so the post-submit reset can put each one back
  // to its declared default. Held as ELEMENT REFERENCES rather than looked up by selector: a field
  // key contains a dot (`bug_report.notify`), so `#intake-bug_report.notify` parses as an id plus a
  // class and silently matches nothing — a reset that appears to work and does not.
  const booleanBoxes = new Map<string, HTMLInputElement>();

  // CAOS-1492 K4 — whether this form is expected to hold an unsent draft at all. Absent means
  // RETAINED: that is what every intake page did before the flag existed, so reading an absent value
  // as "off" would quietly take the behaviour away from the surface it was built for.
  const retainDraft = content.retainDraft !== false;

  // CAOS-326 — whatever was typed and left behind last time, so nobody loses a report by navigating.
  const draft = retainDraft ? loadIntakeDraft(content.object) : {};
  // A package that has just turned retention OFF may have left one behind. Clear it on mount rather
  // than leaving a draft in storage that nothing will ever read again or clear.
  if (!retainDraft) saveIntakeDraft(content.object, new Map());

  const refreshSubmitEnabled = (): void => {
    const ok = requiredScalar.every((f) => (values.get(f) ?? '').trim().length > 0);
    submitBtn.disabled = !ok || submitting;
    submitBtn.style.opacity = submitBtn.disabled ? '0.5' : '1';
    submitBtn.style.cursor = submitBtn.disabled ? 'not-allowed' : 'pointer';
  };

  /** Record a typed value and keep the draft in step with it. */
  const setValue = (field: string, value: string): void => {
    values.set(field, value);
    if (retainDraft) saveIntakeDraft(content.object, values);
    refreshSubmitEnabled();
  };

  for (const field of content.fields) {
    const group = document.createElement('div');
    group.className = 'caos-frame__intake-field';

    // CAOS-1697 — a field may now ask for its visible caption not to be drawn.
    //
    // The append below was UNCONDITIONAL and happened before the per-control branch opened, so a
    // control could not decline a label that had already been added to its group. Making it
    // conditional is the whole of the host-side change, and the interesting half is what happens to
    // the NAME that caption was carrying: `nameWithoutCaption` puts it on the control itself. The
    // call sits beside each element's creation rather than in a sweep at the end, so a control type
    // added later either names itself or is visibly missing the line.
    const hideLabel = field.hideLabel === true;

    const label = document.createElement('label');
    label.className = 'caos-frame__intake-label';
    label.textContent = field.required ? `${field.label} *` : field.label;
    label.setAttribute('for', `intake-${field.field}`);
    if (!hideLabel) group.append(label);

    /**
     * Give a control the name its suppressed caption would have given it, and nothing else.
     *
     * `aria-label` carries the name. `aria-required` carries what the visible ` *` carried, and is
     * set ONLY in the suppressed case: in the ordinary one the asterisk is still on screen, and
     * announcing it twice would be a change to pages that authored nothing. Putting the asterisk
     * inside the accessible name instead was the other option and is worse — it becomes part of
     * what the field is called ("Kind star"), where `aria-required` is announced as the state it
     * actually is.
     *
     * A no-op when the caption is drawn, because then the `<label for>` is the name and a second
     * one would override it.
     */
    const nameWithoutCaption = (el: HTMLElement): void => {
      if (!hideLabel) return;
      el.setAttribute('aria-label', field.label);
      if (field.required === true) el.setAttribute('aria-required', 'true');
    };

    if (field.control === 'picklist') {
      const options = field.options ?? [];
      // Default to the first option so a picklist always carries a value — unless a draft already
      // says which one was chosen (CAOS-326).
      const drafted = draft[field.field];
      const chosen = options.find((o) => o.value === drafted)?.value ?? options[0]?.value;
      if (chosen) values.set(field.field, chosen);
      const picker = document.createElement('div');
      picker.className = 'caos-frame__intake-picker';
      picker.setAttribute('role', 'radiogroup');
      picker.setAttribute('aria-label', field.label);
      // Already named on the line above, which is CAOS-1697's evidence that suppressing the caption
      // costs nothing here. The call is still made, so the radiogroup picks up `aria-required` on
      // the same rule as every other control and the pattern reads the same in all five branches.
      nameWithoutCaption(picker);
      const buttons: HTMLButtonElement[] = [];
      for (const opt of options) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'caos-frame__intake-choice';
        b.dataset.value = opt.value;
        b.textContent = opt.label;
        b.setAttribute('role', 'radio');
        const sync = (): void => {
          for (const other of buttons) {
            const on = other === b;
            other.dataset.active = on ? 'true' : 'false';
            other.setAttribute('aria-checked', on ? 'true' : 'false');
          }
        };
        b.addEventListener('click', () => {
          setValue(field.field, opt.value);
          sync();
        });
        buttons.push(b);
        picker.append(b);
      }
      const activeIndex = Math.max(0, options.findIndex((o) => o.value === chosen));
      const activeButton = buttons[activeIndex];
      if (activeButton) {
        activeButton.dataset.active = 'true';
        activeButton.setAttribute('aria-checked', 'true');
      }
      group.append(picker);
    } else if (field.control === 'attachment') {
      attachments.set(field.field, []);
      // CAOS-596 — the real control, replacing ~90 lines of hand-rolled picker, drop, paste and
      // thumbnail code that lived here. Two behaviours change because they were defects: a file
      // this refuses now says so (the old `addFiles` filtered to images and returned silently, so
      // dropping a PDF did nothing and explained nothing), and a file in flight shows a busy row
      // rather than nothing at all.
      //
      // The label is left to the component rather than appended above it, so the drop target
      // carries a real accessible name. A `<label for>` cannot name a control inside a shadow
      // root, so keeping the outer one here would have left a label pointing at nothing and a
      // button announced as "Choose files".
      //
      // CAOS-1697 — `remove()` on a node that was never appended is a no-op, so this still does the
      // right thing when the caption was suppressed and the append above did not happen.
      label.remove();
      const fileField = document.createElement('caos-file-field') as HTMLElement & {
        value: readonly { name: string; url: string }[];
        clear(): void;
      };
      fileField.id = `intake-${field.field}`;
      fileField.className = 'caos-frame__intake-file-field';
      // CAOS-1697 — the asterisk moves OUT of the label string and onto `required`, which is the
      // attribute the component already had for it.
      //
      // This line used to be `field.required ? \`${field.label} *\` : field.label` with no
      // `required` set at all, so for a required attachment the asterisk was part of the label
      // TEXT — and `label` is also where the drop target's accessible name comes from, which made
      // the name "Add screenshots * — choose files". Review found it: that is the "Kind star"
      // outcome `nameWithoutCaption` below refuses for every other control, and it meant the
      // required STATE reached assistive technology nowhere at all, because the component sets
      // `aria-required` from `required` and nothing was setting `required`.
      //
      // It only became load-bearing with this ticket. Suppressing the caption hides the component's
      // whole `<label>`, asterisk included, so a required-and-suppressed attachment had no visible
      // signal AND no `aria-required` — a state no page could reach before `hideLabel` existed.
      //
      // Drawn, the asterisk is still there but NOT identically: it comes from the component's own
      // `.req` span now, so it is `--caos-color-danger` with a 2px margin rather than the label
      // text's muted colour with a literal space. ("Looks the same" was in an earlier draft of this
      // comment and review was right to call it an overclaim.) Same meaning, and the new rendering
      // is how this component draws every other required field. No deployed page sees either, since
      // the one attachment field in any first-party package is not required.
      fileField.setAttribute('label', field.label);
      if (field.required) fileField.setAttribute('required', '');
      // `label` stays set when the caption is suppressed, because the component reads it twice and
      // independently — once for the visible span and once for the drop target's `aria-label`. Only
      // the visible one goes. Setting `aria-label` out here instead would name a custom element
      // with no role and leave the button inside it still announced as "Choose files".
      if (hideLabel) fileField.setAttribute('label-hidden', '');
      fileField.setAttribute('multiple', '');
      // CAOS-1663 — the page may now say what it takes. The fallback is the literal that was
      // hardcoded here, so a page that authors neither gets byte-for-byte what it got before.
      //
      // `max-bytes` is set ONLY when authored. There is no platform default on purpose: an
      // unauthored page accepts any size today, and inventing a cap here would start refusing
      // files that deploy fine right now. That absence is worth knowing about rather than
      // papering over — attachments travel as data URLs inline in the intake payload (see the
      // transport note below), so an uncapped control is the page's decision to make explicitly.
      fileField.setAttribute('accept', field.accept ?? 'image/png,image/jpeg,image/webp,image/gif');
      if (field.maxBytes !== undefined) fileField.setAttribute('max-bytes', String(field.maxBytes));
      // The transport stays what it was: data URLs carried inline in the intake payload, so the
      // row genuinely holds the screenshots and no blob store is needed. When a durable store and
      // an upload route exist, this is the one line that changes.
      fileField.addEventListener('caos-file-change', () => {
        attachments.set(
          field.field,
          fileField.value.map((v) => ({ name: v.name, dataUrl: v.url })),
        );
      });
      group.append(fileField);
    } else if (field.control === 'textarea') {
      const ta = document.createElement('textarea');
      ta.id = `intake-${field.field}`;
      ta.className = 'caos-frame__intake-textarea';
      ta.rows = 4;
      nameWithoutCaption(ta);
      if (field.placeholder) ta.placeholder = field.placeholder;
      if (draft[field.field]) {
        ta.value = draft[field.field]!;
        values.set(field.field, ta.value);
      }
      ta.addEventListener('input', () => setValue(field.field, ta.value));
      group.append(ta);
      if (field.required) requiredScalar.push(field.field);
    } else if (field.control === 'boolean') {
      // CAOS-1625 — a checkbox for a yes/no preference. Unlike every control above it, this one has
      // a state before anybody touches it, and WHICH state is the author's call: the case it exists
      // for is an opt-OUT ("Email me a confirmation", default on), where leaving it alone must mean
      // the thing still happens.
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.id = `intake-${field.field}`;
      box.className = 'caos-frame__intake-checkbox';
      nameWithoutCaption(box);
      // A saved draft outranks the declared default, for the same reason it does for a textarea:
      // the draft is what this reporter last said, and the default is only what to assume when they
      // have said nothing. `undefined` is the test, not falsiness — a drafted 'false' is a decision
      // to opt out and must not be overwritten by a default of true.
      const drafted = draft[field.field];
      const initial = drafted === undefined ? field.default === true : drafted === 'true';
      box.checked = initial;
      values.set(field.field, initial ? 'true' : 'false');
      booleanBoxes.set(field.field, box);
      box.addEventListener('change', () => setValue(field.field, box.checked ? 'true' : 'false'));
      group.classList.add('caos-frame__intake-field--boolean');
      // Box first, then the label that already points at it — a checkbox reads left to right, and
      // `label` was appended above on the assumption that the control sits under it. With the
      // caption suppressed (CAOS-1697) there is nothing above it, and `prepend` into an empty group
      // is the same as `append`, so the box lands in the one place it can either way.
      group.prepend(box);
      // Not added to `requiredScalar`: `required` is refused on a boolean by the kernel grammar
      // (kernel/src/meta/page.ts), because a checkbox has no empty state for that rule to test.
    } else {
      const input = document.createElement('input');
      input.type = 'text';
      input.id = `intake-${field.field}`;
      input.className = 'caos-frame__intake-input';
      nameWithoutCaption(input);
      if (field.placeholder) input.placeholder = field.placeholder;
      if (draft[field.field]) {
        input.value = draft[field.field]!;
        values.set(field.field, input.value);
      }
      input.addEventListener('input', () => setValue(field.field, input.value));
      group.append(input);
      if (field.required) requiredScalar.push(field.field);
    }

    body.append(group);
  }

  // Footer — status line + submit.
  const footer = document.createElement('div');
  footer.className = 'caos-frame__intake-footer';
  const status = document.createElement('span');
  status.className = 'caos-frame__intake-status';
  status.setAttribute('role', 'status');
  const submitBtn = document.createElement('button');
  submitBtn.type = 'button';
  submitBtn.className = 'caos-frame__intake-submit';
  submitBtn.textContent = content.submitLabel ?? 'Submit';
  // CAOS-1492 K3 — the actions the FORM offers, beside submit.
  //
  // An authored set REPLACES the host's own "My feedback" button rather than sitting beside it. That
  // button is this ticket's own evidence that the slot was missing — a package could not express it,
  // so the shell injected it — and drawing both would put two controls for the same destination in
  // the same footer the moment a package authored the one it had been denied.
  //
  // Both paths still need `opts.navigate`: the surface embedding the form owns navigation, and a
  // button that cannot go anywhere is worse than no button.
  const authoredActions = content.actions ?? [];
  if (opts.navigate) {
    if (authoredActions.length > 0) {
      for (const action of authoredActions) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'caos-frame__intake-secondary';
        b.dataset.actionId = action.id;
        if (action.intent) b.dataset.intent = action.intent;
        b.textContent = action.label;
        // CAOS-1626 — the hover help, when the package authored it. Set only when present: an empty
        // `title` is not the same as no title, and a blank tooltip on hover reads as a broken one.
        // This is the whole of what made authoring the slot a regression before the word existed —
        // the fallback below has always had help text and an authored action could not say any.
        if (action.helpText) b.title = action.helpText;
        b.addEventListener('click', () => opts.navigate!(action.to));
        footer.append(b);
      }
    } else {
      // CAOS-326 — a way to go and see what you already reported. Reporting into a void is the thing
      // that stops people reporting a second time; the reference puts this control right here.
      const mine = document.createElement('button');
      mine.type = 'button';
      mine.className = 'caos-frame__intake-secondary';
      mine.textContent = 'My feedback';
      // CAOS-1626 — "you've", matching the reference build verbatim. The platform said "you have"
      // and nothing recorded which was intended; parity is the criterion this surface is measured by,
      // and a one-word delta is still a delta. Decided here rather than left for the package, because
      // this string is the one anyone actually reads until a published kernel carries `helpText` and
      // caos-pkg-report-a-bug can author its own.
      mine.title = 'See the status of feedback you’ve submitted';
      mine.addEventListener('click', () => opts.navigate!(MY_FEEDBACK_PATH));
      footer.append(mine);
    }
  }
  footer.append(status, submitBtn);
  wrap.append(footer);

  let submitting = false;

  const submit = async (): Promise<void> => {
    if (submitting) return;
    // Guard: required scalars must be filled.
    if (!requiredScalar.every((f) => (values.get(f) ?? '').trim().length > 0)) {
      status.textContent = 'Please fill the required fields.';
      status.dataset.tone = 'error';
      return;
    }
    // CAOS-596 — a screenshot still uploading is not in `attachments` yet, because the field only
    // reports a file once it has settled. Sending now would post the report WITHOUT it and say
    // "Thanks — logged", so the reporter believes they attached something they did not. Refusing
    // for the moment it takes is the only version of this that does not quietly lose evidence.
    const stillAttaching = [...wrap.querySelectorAll('caos-file-field')].some(
      (el) => (el as HTMLElement & { busy?: boolean }).busy === true,
    );
    if (stillAttaching) {
      status.textContent = 'Still attaching — one moment.';
      status.dataset.tone = 'error';
      return;
    }

    submitting = true;
    refreshSubmitEnabled();
    status.dataset.tone = '';
    status.textContent = 'Sending…';

    // Assemble the write payload: each field's BARE key → its value. The `data/create` verb keys
    // fields by bare name (`description`), so strip the object prefix (`bug_report.description`).
    // Attachment controls serialize their data URLs as a JSON array so the row genuinely carries the
    // screenshots (no blob store needed).
    const bareKey = (full: string): string =>
      full.startsWith(`${content.object}.`) ? full.slice(content.object.length + 1) : full;
    const fields: Record<string, unknown> = {};
    for (const field of content.fields) {
      if (field.control === 'attachment') {
        const shots = attachments.get(field.field) ?? [];
        if (shots.length > 0) fields[bareKey(field.field)] = JSON.stringify(shots.map((s) => s.dataUrl));
      } else if (field.control === 'boolean') {
        // ALWAYS SENT, AND AS A REAL BOOLEAN, and the scalar branch below is wrong for it twice.
        //
        // It sends a STRING. Measured by reverting this branch: an unticked box arrives as `"false"`,
        // which is truthy nearly everywhere it might be read — so the opted-out reporter is mailed
        // anyway, by a reader doing nothing wrong.
        //
        // And it DROPS an empty value (`if (v.length > 0)`), which is why `values` holds the word
        // `'false'` here rather than the empty string the obvious implementation would store. With
        // `''` the unticked case would not arrive at all, and "they opted out" would be
        // indistinguishable from "this form has no such field" — a reader that treats an absent flag
        // as the default would then mail someone who explicitly asked not to be. CAOS-326 records
        // that same mistake read the other way round, where an absent `retainDraft` taken as "off"
        // silently removed draft retention and every test of the authored `true` case still passed.
        //
        // Both states are written, and written as booleans, so neither hazard is reachable.
        fields[bareKey(field.field)] = values.get(field.field) === 'true';
      } else {
        const v = (values.get(field.field) ?? '').trim();
        if (v.length > 0) fields[bareKey(field.field)] = v;
      }
    }

    try {
      // CAOS-340 — carry the "where were they" context with every intake submit, the way the original
      // report panel did, so a terse report is still triageable and the review queue's Where column
      // is populated. These ride alongside the envelope; a write verb that does not want them (the
      // generic record create) simply ignores unknown top-level keys.
      const pageUrl = typeof location !== 'undefined' ? location.pathname + location.search : undefined;
      const appContext = currentContextLabel();
      // CAOS-326 — and WHO reported it, in a form a triager can read. The identity itself is recorded
      // server-side from the session; this is the readable name beside it, so the queue does not show
      // a column of blanks.
      const reporterName = signedInName();
      const res = await controlPlaneFetch(`/api/${content.write.noun}/${content.write.verb}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          object: content.object,
          fields,
          pageUrl,
          appContext,
          ...generationField(),
          ...(reporterName ? { reporterName } : {}),
          // CAOS-1688 — the SECOND submit path, and the one that would have been missed. The metadata
          // intake form posts the generic envelope, so without this a report filed through the
          // installed form would arrive without a browser while the panel's reports carried one, and
          // the difference would look like a browser that does not report itself.
          ...userAgentField(),
        }),
      });
      const payload = await res.json().catch(() => undefined);
      const failed = res.ok === false;
      if (failed) {
        status.textContent = "Couldn't submit — please try again.";
        status.dataset.tone = 'error';
        submitting = false;
        refreshSubmitEnabled();
        return;
      }
      // CAOS-1492 K2 — the package's own confirmation copy and reference shape when it authored them,
      // and otherwise the sentence every intake form showed before this existed.
      const ref = intakeConfirmationRef(
        content.confirmation?.reference,
        createdRecordFields(payload),
        createdRecordId(payload),
        bareKey,
      );
      status.textContent =
        content.confirmation === undefined
          ? `Thanks — logged. Your reference is ${ref}.`
          : confirmationText(content.confirmation.message, ref);
      status.dataset.tone = 'ok';
      // Clear the form for the next report — and the saved draft with it, so a submitted report does
      // not come back the next time the panel opens (CAOS-326).
      values.clear();
      if (retainDraft) saveIntakeDraft(content.object, values);
      for (const key of attachments.keys()) attachments.set(key, []);
      // CAOS-596 — and tell the control itself, or the next report opens carrying this one's
      // screenshots. Clearing only the map above would leave them on screen and re-attach them
      // the moment anything else changed.
      wrap.querySelectorAll('caos-file-field').forEach((el) => {
        (el as HTMLElement & { clear(): void }).clear();
      });
      wrap.querySelectorAll('textarea, input[type="text"]').forEach((el) => {
        (el as HTMLTextAreaElement | HTMLInputElement).value = '';
      });
      // Re-seed picklist defaults.
      for (const field of content.fields) {
        if (field.control === 'picklist' && field.options?.[0]) values.set(field.field, field.options[0].value);
        // CAOS-1625 — and put every checkbox back to its DECLARED default, in the DOM as well as in
        // `values`. This is the trap in this block: the clear above matches
        // `textarea, input[type="text"]`, which does not match a checkbox, and the re-seed beside it
        // knew only about picklists. Left out of both, a submitted form would clear `values` while
        // the box on screen stayed as the reporter left it — so the SECOND report would show a
        // ticked box and send `false`. The screen and the payload would disagree, which is worse
        // than either being wrong on its own, and only a test of the second submit sees it.
        if (field.control === 'boolean') {
          const on = field.default === true;
          values.set(field.field, on ? 'true' : 'false');
          const box = booleanBoxes.get(field.field);
          if (box) box.checked = on;
        }
      }
      submitting = false;
      refreshSubmitEnabled();
      opts.onSubmitted?.(ref);
    } catch {
      status.textContent = "Couldn't submit — please try again.";
      status.dataset.tone = 'error';
      submitting = false;
      refreshSubmitEnabled();
    }
  };

  submitBtn.addEventListener('click', () => {
    void submit();
  });
  refreshSubmitEnabled();

  return { root: wrap, submit };
}

/** The runtime context the registered renderer paints with (session/route bits not on the plan). */
let runtimeContext: ShellRuntime = {};

/** Supply the runtime context (production wiring / dev fixture / tests). */
export function configureShellFrame(runtime: ShellRuntime): void {
  runtimeContext = runtime;
}

/**
 * The `hand-to-shell` surface renderer (CAOS-243). The host stamps the plan's theme, then this
 * assembles the chrome from the plan + the configured runtime context. Registered on the host like
 * any other surface; the host dispatches here only when the decision carries a {@link ShellFramePlan}.
 */
export const shellFrameSurface: SurfaceRenderer<'hand-to-shell'> = {
  surface: 'hand-to-shell',
  render(decision, ctx) {
    // The host already stamped the theme before dispatch, but re-stamp defensively so a direct
    // call (tests / a driver) also brands correctly. `shell` is guaranteed present at dispatch.
    if (decision.shell) applyDesignSystem(decision.shell.design, ctx.root);
    const plan = decision.shell;
    if (!plan) return; // a bare hand-to-shell never reaches here via the host, but stay defensive.
    ctx.mount.append(assembleShellFrame(plan, runtimeContext));
  },
};
